import { CONNECT_EVENT_ERROR_CODES, SEND_TRANSACTION_ERROR_CODES, SIGN_DATA_ERROR_CODES, SIGN_MESSAGE_ERROR_CODES, Base64, SessionCrypto, hexToByteArray, DISCONNECT_ERROR_CODES, CONNECT_ITEM_ERROR_CODES } from '@tonconnect/protocol';
export * from '@tonconnect/protocol';
import '@tonconnect/isomorphic-eventsource';
import '@tonconnect/isomorphic-fetch';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Base class for TonConnect errors. You can check if the error was triggered by the @tonconnect/sdk using `err instanceof TonConnectError`.
 */
class TonConnectError extends Error {
    get info() {
        return '';
    }
    constructor(message, options) {
        super(message, options);
        this.message = `${TonConnectError.prefix} ${this.constructor.name}${this.info ? ': ' + this.info : ''}${message ? '\n' + message : ''}`;
        Object.setPrototypeOf(this, TonConnectError.prototype);
    }
}
TonConnectError.prefix = '[TON_CONNECT_SDK_ERROR]';

/**
 * Thrown when passed DappMetadata is in incorrect format.
 */
class DappMetadataError extends TonConnectError {
    get info() {
        return 'Passed DappMetadata is in incorrect format.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, DappMetadataError.prototype);
    }
}

/**
 * Thrown when passed manifest contains errors.
 */
class ManifestContentErrorError extends TonConnectError {
    get info() {
        return 'Passed `tonconnect-manifest.json` contains errors. Check format of your manifest. See more https://github.com/ton-connect/docs/blob/main/requests-responses.md#app-manifest';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, ManifestContentErrorError.prototype);
    }
}

/**
 * Thrown when wallet can't get manifest by passed manifestUrl.
 */
class ManifestNotFoundError extends TonConnectError {
    get info() {
        return 'Manifest not found. Make sure you added `tonconnect-manifest.json` to the root of your app or passed correct manifestUrl. See more https://github.com/ton-connect/docs/blob/main/requests-responses.md#app-manifest';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, ManifestNotFoundError.prototype);
    }
}

/**
 * Thrown when wallet connection called but wallet already connected. To avoid the error, disconnect the wallet before doing a new connection.
 */
class WalletAlreadyConnectedError extends TonConnectError {
    get info() {
        return 'Wallet connection called but wallet already connected. To avoid the error, disconnect the wallet before doing a new connection.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, WalletAlreadyConnectedError.prototype);
    }
}

/**
 * Thrown when send transaction or other protocol methods called while wallet is not connected.
 */
class WalletNotConnectedError extends TonConnectError {
    get info() {
        return 'Send transaction or other protocol methods called while wallet is not connected.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, WalletNotConnectedError.prototype);
    }
}

/**
 * Thrown when there is an attempt to connect to the injected wallet while it is not exists in the webpage.
 */
class WalletNotInjectedError extends TonConnectError {
    get info() {
        return 'There is an attempt to connect to the injected wallet while it is not exists in the webpage.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, WalletNotInjectedError.prototype);
    }
}

/**
 * Thrown when wallet doesn't support requested feature method.
 */
class WalletNotSupportFeatureError extends TonConnectError {
    get info() {
        return "Wallet doesn't support requested feature method.";
    }
    constructor(message, options) {
        super(message, options);
        Object.setPrototypeOf(this, WalletNotSupportFeatureError.prototype);
    }
}

/**
 * Thrown when wallet can't get manifest by passed manifestUrl.
 */
class WalletMissingRequiredFeaturesError extends TonConnectError {
    get info() {
        return 'Missing required features. You need to update your wallet.';
    }
    constructor(message, options) {
        super(message, options);
        Object.setPrototypeOf(this, WalletMissingRequiredFeaturesError.prototype);
    }
}

class WalletWrongNetworkError extends TonConnectError {
    constructor(message, options) {
        super(message, options);
        this.name = 'WalletWrongNetworkError';
        Object.setPrototypeOf(this, WalletWrongNetworkError.prototype);
    }
}

function hasItems(req) {
    return 'items' in req && Array.isArray(req.items);
}
function hasMessages(req) {
    return 'messages' in req && Array.isArray(req.messages);
}

function isWalletConnectionSourceJS(value) {
    return 'jsBridgeKey' in value;
}
function isWalletConnectionSourceWalletConnect(value) {
    return 'type' in value && value.type === 'wallet-connect';
}

/**
 * Thrown when user rejects the action in the wallet.
 */
class UserRejectsError extends TonConnectError {
    get info() {
        return 'User rejects the action in the wallet.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, UserRejectsError.prototype);
    }
}

/**
 * Thrown when request to the wallet contains errors.
 */
class BadRequestError extends TonConnectError {
    get info() {
        return 'Request to the wallet contains errors.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, BadRequestError.prototype);
    }
}

/**
 * Thrown when app tries to send rpc request to the injected wallet while not connected.
 */
class UnknownAppError extends TonConnectError {
    get info() {
        return 'App tries to send rpc request to the injected wallet while not connected.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, UnknownAppError.prototype);
    }
}

/**
 * Thrown when `Storage` was not specified in the `DappMetadata` and default `localStorage` was not detected in the Node.js environment.
 */
class LocalstorageNotFoundError extends TonConnectError {
    get info() {
        return 'Storage was not specified in the `DappMetadata` and default `localStorage` was not detected in the environment.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, LocalstorageNotFoundError.prototype);
    }
}

/**
 * Thrown when an error occurred while fetching the wallets list.
 */
class FetchWalletsError extends TonConnectError {
    get info() {
        return 'An error occurred while fetching the wallets list.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, FetchWalletsError.prototype);
    }
}

/**
 * Thrown when passed address is in incorrect format.
 */
class WrongAddressError extends TonConnectError {
    get info() {
        return 'Passed address is in incorrect format.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, WrongAddressError.prototype);
    }
}

/**
 * Thrown when passed hex is in incorrect format.
 */
class ParseHexError extends TonConnectError {
    get info() {
        return 'Passed hex is in incorrect format.';
    }
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, ParseHexError.prototype);
    }
}

/**
 * Unhanded unknown error.
 */
class UnknownError extends TonConnectError {
    constructor(...args) {
        super(...args);
        Object.setPrototypeOf(this, UnknownError.prototype);
    }
}

const connectEventErrorsCodes = {
    [CONNECT_EVENT_ERROR_CODES.UNKNOWN_ERROR]: UnknownError,
    [CONNECT_EVENT_ERROR_CODES.USER_REJECTS_ERROR]: UserRejectsError,
    [CONNECT_EVENT_ERROR_CODES.BAD_REQUEST_ERROR]: BadRequestError,
    [CONNECT_EVENT_ERROR_CODES.UNKNOWN_APP_ERROR]: UnknownAppError,
    [CONNECT_EVENT_ERROR_CODES.MANIFEST_NOT_FOUND_ERROR]: ManifestNotFoundError,
    [CONNECT_EVENT_ERROR_CODES.MANIFEST_CONTENT_ERROR]: ManifestContentErrorError
};
class ConnectErrorsParser {
    parseError(error) {
        let ErrorConstructor = UnknownError;
        if (error.code in connectEventErrorsCodes) {
            ErrorConstructor = connectEventErrorsCodes[error.code] || UnknownError;
        }
        return new ErrorConstructor(error.message);
    }
}
const connectErrorsParser = new ConnectErrorsParser();

class RpcParser {
    isError(response) {
        return 'error' in response;
    }
}

const sendTransactionErrors = {
    [SEND_TRANSACTION_ERROR_CODES.UNKNOWN_ERROR]: UnknownError,
    [SEND_TRANSACTION_ERROR_CODES.USER_REJECTS_ERROR]: UserRejectsError,
    [SEND_TRANSACTION_ERROR_CODES.BAD_REQUEST_ERROR]: BadRequestError,
    [SEND_TRANSACTION_ERROR_CODES.UNKNOWN_APP_ERROR]: UnknownAppError
};
class SendTransactionParser extends RpcParser {
    convertToRpcRequest(request) {
        return {
            method: 'sendTransaction',
            params: [JSON.stringify(request)]
        };
    }
    parseAndThrowError(response) {
        let ErrorConstructor = UnknownError;
        if (response.error.code in sendTransactionErrors) {
            ErrorConstructor = sendTransactionErrors[response.error.code] || UnknownError;
        }
        throw new ErrorConstructor(response.error.message);
    }
    convertFromRpcResponse(rpcResponse) {
        return {
            boc: rpcResponse.result
        };
    }
}
const sendTransactionParser = new SendTransactionParser();

const signDataErrors = {
    [SIGN_DATA_ERROR_CODES.UNKNOWN_ERROR]: UnknownError,
    [SIGN_DATA_ERROR_CODES.USER_REJECTS_ERROR]: UserRejectsError,
    [SIGN_DATA_ERROR_CODES.BAD_REQUEST_ERROR]: BadRequestError,
    [SIGN_DATA_ERROR_CODES.UNKNOWN_APP_ERROR]: UnknownAppError
};
class SignDataParser extends RpcParser {
    convertToRpcRequest(payload) {
        return {
            method: 'signData',
            params: [JSON.stringify(payload)]
        };
    }
    parseAndThrowError(response) {
        let ErrorConstructor = UnknownError;
        if (response.error.code in signDataErrors) {
            ErrorConstructor = signDataErrors[response.error.code] || UnknownError;
        }
        throw new ErrorConstructor(response.error.message);
    }
    convertFromRpcResponse(rpcResponse) {
        return rpcResponse.result;
    }
}
const signDataParser = new SignDataParser();

const signMessageErrors = {
    [SIGN_MESSAGE_ERROR_CODES.UNKNOWN_ERROR]: UnknownError,
    [SIGN_MESSAGE_ERROR_CODES.USER_REJECTS_ERROR]: UserRejectsError,
    [SIGN_MESSAGE_ERROR_CODES.BAD_REQUEST_ERROR]: BadRequestError,
    [SIGN_MESSAGE_ERROR_CODES.UNKNOWN_APP_ERROR]: UnknownAppError
};
class SignMessageParser extends RpcParser {
    convertToRpcRequest(request) {
        return {
            method: 'signMessage',
            params: [JSON.stringify(request)]
        };
    }
    parseAndThrowError(response) {
        let ErrorConstructor = UnknownError;
        if (response.error.code in signMessageErrors) {
            ErrorConstructor = signMessageErrors[response.error.code] || UnknownError;
        }
        throw new ErrorConstructor(response.error.message);
    }
    convertFromRpcResponse(rpcResponse) {
        return {
            internalBoc: rpcResponse.result.internalBoc
        };
    }
}
const signMessageParser = new SignMessageParser();

class HttpBridgeGatewayStorage {
    constructor(storage, bridgeUrl) {
        this.storage = storage;
        this.storeKey = 'ton-connect-storage_http-bridge-gateway::' + bridgeUrl;
    }
    storeLastEventId(lastEventId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.storage.setItem(this.storeKey, lastEventId);
        });
    }
    removeLastEventId() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.storage.removeItem(this.storeKey);
        });
    }
    getLastEventId() {
        return __awaiter(this, void 0, void 0, function* () {
            const stored = yield this.storage.getItem(this.storeKey);
            if (!stored) {
                return null;
            }
            return stored;
        });
    }
}

function removeUrlLastSlash(url) {
    if (url.slice(-1) === '/') {
        return url.slice(0, -1);
    }
    return url;
}
function addPathToUrl(url, path) {
    return removeUrlLastSlash(url) + '/' + path;
}
function isTelegramUrl(link) {
    if (!link) {
        return false;
    }
    const url = new URL(link);
    return url.protocol === 'tg:' || url.hostname === 't.me';
}
function isConnectUrl(link) {
    if (!link) {
        return false;
    }
    return link.includes('ton_addr') || link.includes('ton--5Faddr');
}
function encodeTelegramUrlParameters(parameters) {
    return parameters
        .replaceAll('.', '%2E')
        .replaceAll('-', '%2D')
        .replaceAll('_', '%5F')
        .replaceAll('&', '-')
        .replaceAll('=', '__')
        .replaceAll('%', '--');
}
function decodeTelegramUrlParameters(parameters) {
    return parameters
        .replaceAll('--', '%')
        .replaceAll('__', '=')
        .replaceAll('-', '&')
        .replaceAll('%5F', '_')
        .replaceAll('%2D', '-')
        .replaceAll('%2E', '.');
}

/**
 * Delays the execution of code for a specified number of milliseconds.
 * @param {number} timeout - The number of milliseconds to delay the execution.
 * @param {DelayOptions} [options] - Optional configuration options for the delay.
 * @return {Promise<void>} - A promise that resolves after the specified delay, or rejects if the delay is aborted.
 */
function delay(timeout, options) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            var _a, _b;
            if ((_a = void 0 ) === null || _a === void 0 ? void 0 : _a.aborted) {
                reject(new TonConnectError('Delay aborted'));
                return;
            }
            const timeoutId = setTimeout(() => resolve(), timeout);
            (_b = void 0 ) === null || _b === void 0 ? void 0 : _b.addEventListener('abort', () => {
                clearTimeout(timeoutId);
                reject(new TonConnectError('Delay aborted'));
            });
        });
    });
}

/**
 * Creates an AbortController instance with an optional AbortSignal.
 *
 * @param {AbortSignal} [signal] - An optional AbortSignal to use for aborting the controller.
 * @returns {AbortController} - An instance of AbortController.
 */
function createAbortController(signal) {
    const abortController = new AbortController();
    if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
        abortController.abort();
    }
    else {
        signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', () => abortController.abort(), { once: true });
    }
    return abortController;
}

/**
 * Function to call ton api until we get response.
 * Because ton network is pretty unstable we need to make sure response is final.
 * @param {T} fn - function to call
 * @param {CallForSuccessOptions} [options] - optional configuration options
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function callForSuccess(fn, options) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const attempts = (_a = options === null || options === void 0 ? void 0 : options.attempts) !== null && _a !== void 0 ? _a : 10;
        const delayMs = (_b = options === null || options === void 0 ? void 0 : options.delayMs) !== null && _b !== void 0 ? _b : 200;
        const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
        if (typeof fn !== 'function') {
            throw new TonConnectError(`Expected a function, got ${typeof fn}`);
        }
        let i = 0;
        let lastError;
        while (i < attempts) {
            if (abortController.signal.aborted) {
                throw new TonConnectError(`Aborted after attempts ${i}`);
            }
            try {
                return yield fn({ signal: abortController.signal });
            }
            catch (err) {
                lastError = err;
                i++;
                if (i < attempts) {
                    yield delay(delayMs);
                }
            }
        }
        throw lastError;
    });
}

function logDebug(...args) {
    {
        try {
            console.debug('[TON_CONNECT_SDK]', ...args);
        }
        catch (_a) { }
    }
}
function logError(...args) {
    {
        try {
            console.error('[TON_CONNECT_SDK]', ...args);
        }
        catch (_a) { }
    }
}
function logWarning(...args) {
    {
        try {
            console.warn('[TON_CONNECT_SDK]', ...args);
        }
        catch (_a) { }
    }
}

/**
 * Create a resource.
 *
 * @template T - The type of the resource.
 * @template Args - The type of the arguments for creating the resource.
 *
 * @param {(...args: Args) => Promise<T>} createFn - A function that creates the resource.
 * @param {(resource: T) => Promise<void>} [disposeFn] - An optional function that disposes the resource.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createResource(createFn, disposeFn) {
    let currentResource = null;
    let currentArgs = null;
    let currentPromise = null;
    let currentSignal = null;
    let abortController = null;
    // create a new resource
    const create = (signal, ...args) => __awaiter(this, void 0, void 0, function* () {
        currentSignal = signal !== null && signal !== void 0 ? signal : null;
        abortController === null || abortController === void 0 ? void 0 : abortController.abort();
        abortController = createAbortController(signal);
        if (abortController.signal.aborted) {
            throw new TonConnectError('Resource creation was aborted');
        }
        currentArgs = args !== null && args !== void 0 ? args : null;
        const promise = createFn(abortController.signal, ...args);
        currentPromise = promise;
        const resource = yield promise;
        if (currentPromise !== promise && resource !== currentResource) {
            yield disposeFn(resource);
            throw new TonConnectError('Resource creation was aborted by a new resource creation');
        }
        currentResource = resource;
        return currentResource;
    });
    // get the current resource
    const current = () => {
        return currentResource !== null && currentResource !== void 0 ? currentResource : null;
    };
    // dispose the current resource
    const dispose = () => __awaiter(this, void 0, void 0, function* () {
        try {
            const resource = currentResource;
            currentResource = null;
            const promise = currentPromise;
            currentPromise = null;
            try {
                abortController === null || abortController === void 0 ? void 0 : abortController.abort();
            }
            catch (e) { }
            yield Promise.allSettled([
                resource ? disposeFn(resource) : Promise.resolve(),
                promise ? disposeFn(yield promise) : Promise.resolve()
            ]);
        }
        catch (e) { }
    });
    // recreate the current resource
    const recreate = (delayMs) => __awaiter(this, void 0, void 0, function* () {
        const resource = currentResource;
        const promise = currentPromise;
        const args = currentArgs;
        const signal = currentSignal;
        yield delay(delayMs);
        if (resource === currentResource &&
            promise === currentPromise &&
            args === currentArgs &&
            signal === currentSignal) {
            return yield create(currentSignal, ...(args !== null && args !== void 0 ? args : []));
        }
        throw new TonConnectError('Resource recreation was aborted by a new resource creation');
    });
    return {
        create,
        current,
        dispose,
        recreate
    };
}

/**
 * Executes a function and provides deferred behavior, allowing for a timeout and abort functionality.
 *
 * @param {Deferrable<T>} fn - The function to execute. It should return a promise that resolves with the desired result.
 * @param {DeferOptions} options - Optional configuration options for the defer behavior.
 * @returns {Promise<T>} - A promise that resolves with the result of the executed function, or rejects with an error if it times out or is aborted.
 */
function timeout(fn, options) {
    const timeout = options === null || options === void 0 ? void 0 : options.timeout;
    const signal = options === null || options === void 0 ? void 0 : options.signal;
    const abortController = createAbortController(signal);
    return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
        if (abortController.signal.aborted) {
            reject(new TonConnectError('Operation aborted'));
            return;
        }
        let timeoutId;
        if (typeof timeout !== 'undefined') {
            timeoutId = setTimeout(() => {
                abortController.abort();
                reject(new TonConnectError(`Timeout after ${timeout}ms`));
            }, timeout);
        }
        abortController.signal.addEventListener('abort', () => {
            clearTimeout(timeoutId);
            reject(new TonConnectError('Operation aborted'));
        }, { once: true });
        const deferOptions = { timeout, abort: abortController.signal };
        yield fn((...args) => {
            clearTimeout(timeoutId);
            resolve(...args);
        }, () => {
            clearTimeout(timeoutId);
            reject();
        }, deferOptions);
    }));
}

class BridgeGateway {
    get isReady() {
        const eventSource = this.eventSource.current();
        return (eventSource === null || eventSource === void 0 ? void 0 : eventSource.readyState) === EventSource.OPEN;
    }
    get isClosed() {
        const eventSource = this.eventSource.current();
        return (eventSource === null || eventSource === void 0 ? void 0 : eventSource.readyState) !== EventSource.OPEN;
    }
    get isConnecting() {
        const eventSource = this.eventSource.current();
        return (eventSource === null || eventSource === void 0 ? void 0 : eventSource.readyState) === EventSource.CONNECTING;
    }
    constructor(storage, bridgeUrl, sessionId, listener, errorsListener, analyticsManager) {
        this.bridgeUrl = bridgeUrl;
        this.sessionId = sessionId;
        this.listener = listener;
        this.errorsListener = errorsListener;
        this.ssePath = 'events';
        this.postPath = 'message';
        this.heartbeatMessage = 'heartbeat';
        this.defaultTtl = 300;
        this.defaultReconnectDelay = 2000;
        this.defaultResendDelay = 5000;
        this.eventSource = createResource((signal, openingDeadlineMS, traceId) => __awaiter(this, void 0, void 0, function* () {
            const eventSourceConfig = {
                bridgeUrl: this.bridgeUrl,
                ssePath: this.ssePath,
                sessionId: this.sessionId,
                bridgeGatewayStorage: this.bridgeGatewayStorage,
                errorHandler: this.errorsHandler.bind(this),
                messageHandler: this.messagesHandler.bind(this),
                signal: signal,
                openingDeadlineMS: openingDeadlineMS,
                traceId
            };
            return yield createEventSource(eventSourceConfig);
        }), (resource) => __awaiter(this, void 0, void 0, function* () {
            resource.close();
        }));
        this.bridgeGatewayStorage = new HttpBridgeGatewayStorage(storage, bridgeUrl);
        this.analytics = analyticsManager === null || analyticsManager === void 0 ? void 0 : analyticsManager.scoped({
            bridge_url: bridgeUrl,
            client_id: sessionId
        });
    }
    registerSession(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.emitBridgeClientConnectStarted({
                    trace_id: options === null || options === void 0 ? void 0 : options.traceId
                });
                const connectionStarted = Date.now();
                yield this.eventSource.create(options === null || options === void 0 ? void 0 : options.signal, options === null || options === void 0 ? void 0 : options.openingDeadlineMS, options === null || options === void 0 ? void 0 : options.traceId);
                const bridgeConnectDuration = Date.now() - connectionStarted;
                (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.emitBridgeClientConnectEstablished({
                    bridge_connect_duration: bridgeConnectDuration,
                    trace_id: options === null || options === void 0 ? void 0 : options.traceId
                });
            }
            catch (error) {
                (_c = this.analytics) === null || _c === void 0 ? void 0 : _c.emitBridgeClientConnectError({
                    trace_id: options === null || options === void 0 ? void 0 : options.traceId,
                    error_message: String(error)
                });
                throw error;
            }
        });
    }
    send(message, receiver, topic, ttlOrOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // TODO: remove deprecated method
            const options = {};
            if (typeof ttlOrOptions === 'number') {
                options.ttl = ttlOrOptions;
            }
            else {
                options.ttl = ttlOrOptions === null || ttlOrOptions === void 0 ? void 0 : ttlOrOptions.ttl;
                options.signal = ttlOrOptions === null || ttlOrOptions === void 0 ? void 0 : ttlOrOptions.signal;
                options.attempts = ttlOrOptions === null || ttlOrOptions === void 0 ? void 0 : ttlOrOptions.attempts;
                options.traceId = ttlOrOptions === null || ttlOrOptions === void 0 ? void 0 : ttlOrOptions.traceId;
            }
            const url = new URL(addPathToUrl(this.bridgeUrl, this.postPath));
            url.searchParams.append('client_id', this.sessionId);
            url.searchParams.append('to', receiver);
            url.searchParams.append('ttl', ((options === null || options === void 0 ? void 0 : options.ttl) || this.defaultTtl).toString());
            url.searchParams.append('topic', topic);
            if (options === null || options === void 0 ? void 0 : options.traceId) {
                url.searchParams.append('trace_id', options.traceId);
            }
            const body = Base64.encode(message);
            yield callForSuccess((options) => __awaiter(this, void 0, void 0, function* () {
                const response = yield this.post(url, body, options.signal);
                if (!response.ok) {
                    throw new TonConnectError(`Bridge send failed, status ${response.status}`);
                }
            }), {
                attempts: (_a = options === null || options === void 0 ? void 0 : options.attempts) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER,
                delayMs: this.defaultResendDelay,
                signal: options === null || options === void 0 ? void 0 : options.signal
            });
        });
    }
    pause() {
        this.eventSource.dispose().catch(e => logError(`Bridge pause failed, ${e}`));
    }
    unPause() {
        return __awaiter(this, void 0, void 0, function* () {
            const RECREATE_WITHOUT_DELAY = 0;
            yield this.eventSource.recreate(RECREATE_WITHOUT_DELAY);
        });
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.eventSource.dispose().catch(e => logError(`Bridge close failed, ${e}`));
        });
    }
    setListener(listener) {
        this.listener = listener;
    }
    setErrorsListener(errorsListener) {
        this.errorsListener = errorsListener;
    }
    post(url, body, signal) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(url, {
                method: 'post',
                body: body,
                signal: signal
            });
            if (!response.ok) {
                throw new TonConnectError(`Bridge send failed, status ${response.status}`);
            }
            return response;
        });
    }
    errorsHandler(eventSource, e) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isConnecting) {
                eventSource.close();
                throw new TonConnectError('Bridge error, failed to connect');
            }
            if (this.isReady) {
                try {
                    this.errorsListener(e);
                }
                catch (e) { }
                return;
            }
            if (this.isClosed) {
                eventSource.close();
                logDebug(`Bridge reconnecting, ${this.defaultReconnectDelay}ms delay`);
                return yield this.eventSource.recreate(this.defaultReconnectDelay);
            }
            throw new TonConnectError('Bridge error, unknown state');
        });
    }
    messagesHandler(e) {
        return __awaiter(this, void 0, void 0, function* () {
            if (e.data === this.heartbeatMessage) {
                return;
            }
            yield this.bridgeGatewayStorage.storeLastEventId(e.lastEventId);
            if (this.isClosed) {
                return;
            }
            let bridgeIncomingMessage;
            try {
                const bridgeIncomingMessageRaw = JSON.parse(e.data);
                bridgeIncomingMessage = {
                    message: bridgeIncomingMessageRaw.message,
                    from: bridgeIncomingMessageRaw.from,
                    traceId: bridgeIncomingMessageRaw.trace_id
                };
            }
            catch (_) {
                throw new TonConnectError(`Bridge message parse failed, message ${e.data}`);
            }
            this.listener(bridgeIncomingMessage);
        });
    }
}
/**
 * Creates an event source.
 * @param {CreateEventSourceConfig} config - Configuration for creating an event source.
 */
function createEventSource(config) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield timeout((resolve, reject, deferOptions) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const abortController = createAbortController(deferOptions.signal);
            const signal = abortController.signal;
            if (signal.aborted) {
                reject(new TonConnectError('Bridge connection aborted'));
                return;
            }
            const url = new URL(addPathToUrl(config.bridgeUrl, config.ssePath));
            url.searchParams.append('client_id', config.sessionId);
            const lastEventId = yield config.bridgeGatewayStorage.getLastEventId();
            if (lastEventId) {
                url.searchParams.append('last_event_id', lastEventId);
            }
            if (config.traceId) {
                url.searchParams.append('trace_id', config.traceId);
            }
            if (signal.aborted) {
                reject(new TonConnectError('Bridge connection aborted'));
                return;
            }
            const eventSource = new EventSource(url.toString());
            eventSource.onerror = (reason) => __awaiter(this, void 0, void 0, function* () {
                if (signal.aborted) {
                    eventSource.close();
                    reject(new TonConnectError('Bridge connection aborted'));
                    return;
                }
                try {
                    const newInstance = yield config.errorHandler(eventSource, reason);
                    if (newInstance !== eventSource) {
                        eventSource.close();
                    }
                    if (newInstance && newInstance !== eventSource) {
                        resolve(newInstance);
                    }
                }
                catch (e) {
                    eventSource.close();
                    reject(e);
                }
            });
            eventSource.onopen = () => {
                if (signal.aborted) {
                    eventSource.close();
                    reject(new TonConnectError('Bridge connection aborted'));
                    return;
                }
                resolve(eventSource);
            };
            eventSource.onmessage = (event) => {
                if (signal.aborted) {
                    eventSource.close();
                    reject(new TonConnectError('Bridge connection aborted'));
                    return;
                }
                config.messageHandler(event);
            };
            (_a = config.signal) === null || _a === void 0 ? void 0 : _a.addEventListener('abort', () => {
                eventSource.close();
                reject(new TonConnectError('Bridge connection aborted'));
            });
        }), { timeout: config.openingDeadlineMS, signal: config.signal });
    });
}

const CONNECTION_HTTP_EXPIRATION_TIME = 5 * 60 * 1000;
function isPendingConnectionHttp(connection) {
    return !('connectEvent' in connection);
}
function isPendingConnectionHttpRaw(connection) {
    return !('connectEvent' in connection);
}
function isExpiredPendingConnectionHttpRaw(connection) {
    var _a;
    return Date.now() - ((_a = connection.createdAt) !== null && _a !== void 0 ? _a : 0) > CONNECTION_HTTP_EXPIRATION_TIME;
}

/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2010-2020 Robert Kieffer and other contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
    // lazy load so that environments that need to polyfill have a chance to do so
    if (!getRandomValues) {
        if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
            throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
        }
        getRandomValues = crypto.getRandomValues.bind(crypto);
    }
    return getRandomValues(rnds8);
}

/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2010-2020 Robert Kieffer and other contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */
const byteToHex = [];
for (let i = 0; i < 256; ++i) {
    byteToHex.push((i + 0x100).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
    // Note: Be careful editing this code!  It's been tuned for performance
    // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
    //
    // Note to future-self: No, you can't remove the `toLowerCase()` call.
    // REF: https://github.com/uuidjs/uuid/pull/677#issuecomment-1757351351
    return (byteToHex[arr[offset + 0]] +
        byteToHex[arr[offset + 1]] +
        byteToHex[arr[offset + 2]] +
        byteToHex[arr[offset + 3]] +
        '-' +
        byteToHex[arr[offset + 4]] +
        byteToHex[arr[offset + 5]] +
        '-' +
        byteToHex[arr[offset + 6]] +
        byteToHex[arr[offset + 7]] +
        '-' +
        byteToHex[arr[offset + 8]] +
        byteToHex[arr[offset + 9]] +
        '-' +
        byteToHex[arr[offset + 10]] +
        byteToHex[arr[offset + 11]] +
        byteToHex[arr[offset + 12]] +
        byteToHex[arr[offset + 13]] +
        byteToHex[arr[offset + 14]] +
        byteToHex[arr[offset + 15]]).toLowerCase();
}

/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2010-2020 Robert Kieffer and other contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const _state = {};
function UUIDv7(options, buf, offset) {
    var _a, _b, _c;
    let bytes;
    if (options) {
        // With options: Make UUID independent of internal state
        bytes = v7Bytes((_c = (_a = options.random) !== null && _a !== void 0 ? _a : (_b = options.rng) === null || _b === void 0 ? void 0 : _b.call(options)) !== null && _c !== void 0 ? _c : rng(), options.msecs, options.seq, buf, offset);
    }
    else {
        // No options: Use internal state
        const now = Date.now();
        const rnds = rng();
        updateV7State(_state, now, rnds);
        bytes = v7Bytes(rnds, _state.msecs, _state.seq, buf, offset);
    }
    return buf !== null && buf !== void 0 ? buf : unsafeStringify(bytes);
}
// (Private!)  Do not use.  This method is only exported for testing purposes
// and may change without notice.
function updateV7State(state, now, rnds) {
    var _a, _b;
    (_a = state.msecs) !== null && _a !== void 0 ? _a : (state.msecs = -Infinity);
    (_b = state.seq) !== null && _b !== void 0 ? _b : (state.seq = 0);
    if (now > state.msecs) {
        // Time has moved on! Pick a new random sequence number
        state.seq = (rnds[6] << 23) | (rnds[7] << 16) | (rnds[8] << 8) | rnds[9];
        state.msecs = now;
    }
    else {
        // Bump sequence counter w/ 32-bit rollover
        state.seq = (state.seq + 1) | 0;
        // In case of rollover, bump timestamp to preserve monotonicity. This is
        // allowed by the RFC and should self-correct as the system clock catches
        // up. See https://www.rfc-editor.org/rfc/rfc9562.html#section-6.2-9.4
        if (state.seq === 0) {
            state.msecs++;
        }
    }
    return state;
}
function v7Bytes(rnds, msecs, seq, buf, offset = 0) {
    if (rnds.length < 16) {
        throw new Error('Random bytes length must be >= 16');
    }
    if (!buf) {
        buf = new Uint8Array(16);
        offset = 0;
    }
    else {
        if (offset < 0 || offset + 16 > buf.length) {
            throw new RangeError(`UUID byte range ${offset}:${offset + 15} is out of buffer bounds`);
        }
    }
    // Defaults
    msecs !== null && msecs !== void 0 ? msecs : (msecs = Date.now());
    seq !== null && seq !== void 0 ? seq : (seq = ((rnds[6] * 0x7f) << 24) | (rnds[7] << 16) | (rnds[8] << 8) | rnds[9]);
    // byte 0-5: timestamp (48 bits)
    buf[offset++] = (msecs / 0x10000000000) & 0xff;
    buf[offset++] = (msecs / 0x100000000) & 0xff;
    buf[offset++] = (msecs / 0x1000000) & 0xff;
    buf[offset++] = (msecs / 0x10000) & 0xff;
    buf[offset++] = (msecs / 0x100) & 0xff;
    buf[offset++] = msecs & 0xff;
    // byte 6: `version` (4 bits) | sequence bits 28-31 (4 bits)
    buf[offset++] = 0x70 | ((seq >>> 28) & 0x0f);
    // byte 7: sequence bits 20-27 (8 bits)
    buf[offset++] = (seq >>> 20) & 0xff;
    // byte 8: `variant` (2 bits) | sequence bits 14-19 (6 bits)
    buf[offset++] = 0x80 | ((seq >>> 14) & 0x3f);
    // byte 9: sequence bits 6-13 (8 bits)
    buf[offset++] = (seq >>> 6) & 0xff;
    // byte 10: sequence bits 0-5 (6 bits) | random (2 bits)
    buf[offset++] = ((seq << 2) & 0xff) | (rnds[10] & 0x03);
    // bytes 11-15: random (40 bits)
    buf[offset++] = rnds[11];
    buf[offset++] = rnds[12];
    buf[offset++] = rnds[13];
    buf[offset++] = rnds[14];
    buf[offset++] = rnds[15];
    return buf;
}

function waitForSome(promises, count) {
    return __awaiter(this, void 0, void 0, function* () {
        if (count <= 0)
            return [];
        if (count > promises.length) {
            throw new RangeError('count cannot be greater than the number of promises');
        }
        const results = new Array(promises.length);
        let settledCount = 0;
        return new Promise(resolve => {
            promises.forEach((p, index) => {
                Promise.resolve(p)
                    .then(value => ({ status: 'fulfilled', value }))
                    .catch(reason => ({ status: 'rejected', reason }))
                    .then(result => {
                    results[index] = result;
                    settledCount++;
                    if (settledCount === count) {
                        resolve(results);
                    }
                });
            });
        });
    });
}

const PROTOCOL_VERSION = 2;

function normalizeBase64(data) {
    if (typeof data !== 'string')
        return undefined;
    const paddedLength = data.length + ((4 - (data.length % 4)) % 4);
    return data.replace(/-/g, '+').replace(/_/g, '/').padEnd(paddedLength, '=');
}
function toBase64Url(base64) {
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateUniversalLink(universalLink, message, options) {
    if (isTelegramUrl(universalLink)) {
        return generateTGUniversalLink(universalLink, message, options);
    }
    return generateRegularUniversalLink(universalLink, message, options);
}
function generateRegularUniversalLink(universalLink, message, options) {
    const url = new URL(universalLink);
    url.searchParams.append('v', PROTOCOL_VERSION.toString());
    url.searchParams.append('id', options.sessionId);
    url.searchParams.append('trace_id', options.traceId);
    url.searchParams.append('r', JSON.stringify(message));
    if (options.embeddedRequest) {
        url.searchParams.append('e', toBase64Url(Base64.encode(JSON.stringify(options.embeddedRequest), false)));
    }
    return url.toString();
}
function generateTGUniversalLink(universalLink, message, options) {
    const urlToWrap = generateRegularUniversalLink('about:blank', message, options);
    const linkParams = urlToWrap.split('?')[1];
    const startapp = 'tonconnect-' + encodeTelegramUrlParameters(linkParams);
    // TODO: Remove this line after all dApps and the wallets-list.json have been updated
    const updatedUniversalLink = convertToDirectLink(universalLink);
    const url = new URL(updatedUniversalLink);
    url.searchParams.append('startapp', startapp);
    return url.toString();
}
// TODO: Remove this method after all dApps and the wallets-list.json have been updated
function convertToDirectLink(universalLink) {
    const url = new URL(universalLink);
    if (url.searchParams.has('attach')) {
        url.searchParams.delete('attach');
        url.pathname += '/start';
    }
    return url.toString();
}

class BridgeProvider {
    static fromStorage(storage, analyticsManager) {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield storage.getHttpConnection();
            if (isPendingConnectionHttp(connection)) {
                return new BridgeProvider(storage, connection.connectionSource, analyticsManager);
            }
            return new BridgeProvider(storage, { bridgeUrl: connection.session.bridgeUrl }, analyticsManager);
        });
    }
    constructor(connectionStorage, walletConnectionSource, analyticsManager) {
        var _a;
        this.connectionStorage = connectionStorage;
        this.walletConnectionSource = walletConnectionSource;
        this.analyticsManager = analyticsManager;
        this.type = 'http';
        this.standardUniversalLink = 'tc://';
        this.pendingRequests = new Map();
        this.session = null;
        this.gateway = null;
        this.pendingGateways = [];
        this.listeners = [];
        this.defaultOpeningDeadlineMS = 12000;
        this.defaultRetryTimeoutMS = 2000;
        // NOTE: this is duplicated as `MAX_LINK_LENGTH` in
        // packages/ui/src/app/utils/web-api.ts. The duplication is a consequence
        // of an architectural shortcoming — universal-link generation lives here
        // in the SDK (which enforces this cap), while the UI layer independently
        // re-checks the produced link before opening it. Until the length policy
        // is centralised, both constants must be kept in sync.
        this.maxUrlLength = 1024;
        this.optionalOpenGateways = 3;
        this.analytics = (_a = this.analyticsManager) === null || _a === void 0 ? void 0 : _a.scoped();
    }
    connect(message, options) {
        var _a, _b, _c, _d;
        const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
        const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
        (_b = this.abortController) === null || _b === void 0 ? void 0 : _b.abort();
        this.abortController = abortController;
        this.closeGateways();
        const sessionCrypto = new SessionCrypto();
        this.session = {
            sessionCrypto,
            bridgeUrl: 'bridgeUrl' in this.walletConnectionSource
                ? this.walletConnectionSource.bridgeUrl
                : ''
        };
        this.connectionStorage
            .storeConnection({
            type: 'http',
            connectionSource: this.walletConnectionSource,
            sessionCrypto
        })
            .then(() => __awaiter(this, void 0, void 0, function* () {
            if (abortController.signal.aborted) {
                return;
            }
            yield callForSuccess(_options => {
                var _a;
                return this.openGateways(sessionCrypto, {
                    openingDeadlineMS: (_a = options === null || options === void 0 ? void 0 : options.openingDeadlineMS) !== null && _a !== void 0 ? _a : this.defaultOpeningDeadlineMS,
                    signal: _options === null || _options === void 0 ? void 0 : _options.signal,
                    traceId
                });
            }, {
                attempts: Number.MAX_SAFE_INTEGER,
                delayMs: this.defaultRetryTimeoutMS,
                signal: abortController.signal
            });
        }));
        const universalLink = 'universalLink' in this.walletConnectionSource &&
            this.walletConnectionSource.universalLink
            ? this.walletConnectionSource.universalLink
            : this.standardUniversalLink;
        const embeddedRequest = (_c = options === null || options === void 0 ? void 0 : options.embeddedRequest) === null || _c === void 0 ? void 0 : _c.peek();
        const link = generateUniversalLink(universalLink, message, {
            traceId,
            sessionId: this.session.sessionCrypto.sessionId,
            embeddedRequest
        });
        if (link.length <= this.maxUrlLength) {
            (_d = options === null || options === void 0 ? void 0 : options.embeddedRequest) === null || _d === void 0 ? void 0 : _d.consume();
            return link;
        }
        return generateUniversalLink(universalLink, message, {
            traceId,
            sessionId: this.session.sessionCrypto.sessionId
        });
    }
    restoreConnection(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            (_b = this.abortController) === null || _b === void 0 ? void 0 : _b.abort();
            this.abortController = abortController;
            if (abortController.signal.aborted) {
                return;
            }
            this.closeGateways();
            const storedConnection = yield this.connectionStorage.getHttpConnection();
            if (!storedConnection) {
                return;
            }
            if (abortController.signal.aborted) {
                return;
            }
            const openingDeadlineMS = (_c = options === null || options === void 0 ? void 0 : options.openingDeadlineMS) !== null && _c !== void 0 ? _c : this.defaultOpeningDeadlineMS;
            if (isPendingConnectionHttp(storedConnection)) {
                this.session = {
                    sessionCrypto: storedConnection.sessionCrypto,
                    bridgeUrl: 'bridgeUrl' in this.walletConnectionSource
                        ? this.walletConnectionSource.bridgeUrl
                        : ''
                };
                return yield this.openGateways(storedConnection.sessionCrypto, {
                    openingDeadlineMS: openingDeadlineMS,
                    signal: abortController === null || abortController === void 0 ? void 0 : abortController.signal,
                    traceId: options === null || options === void 0 ? void 0 : options.traceId
                });
            }
            if (Array.isArray(this.walletConnectionSource)) {
                throw new TonConnectError('Internal error. Connection source is array while WalletConnectionSourceHTTP was expected.');
            }
            this.session = storedConnection.session;
            if (this.gateway) {
                logDebug('Gateway is already opened, closing previous gateway');
                yield this.gateway.close();
            }
            this.gateway = new BridgeGateway(this.connectionStorage.storage, this.walletConnectionSource.bridgeUrl, storedConnection.session.sessionCrypto.sessionId, this.gatewayListener.bind(this), this.gatewayErrorsListener.bind(this), this.analyticsManager);
            if (abortController.signal.aborted) {
                return;
            }
            // notify listeners about stored connection
            this.listeners.forEach(listener => listener(Object.assign(Object.assign({}, storedConnection.connectEvent), { traceId })));
            // wait for the connection to be opened
            try {
                yield callForSuccess(options => this.gateway.registerSession({
                    openingDeadlineMS: openingDeadlineMS,
                    signal: options.signal,
                    traceId
                }), {
                    attempts: Number.MAX_SAFE_INTEGER,
                    delayMs: this.defaultRetryTimeoutMS,
                    signal: abortController.signal
                });
            }
            catch (e) {
                yield this.disconnect({ signal: abortController.signal, traceId });
                return;
            }
        });
    }
    sendRequest(request, optionsOrOnRequestSent) {
        var _a;
        // TODO: remove deprecated method
        const options = {};
        if (typeof optionsOrOnRequestSent === 'function') {
            options.onRequestSent = optionsOrOnRequestSent;
        }
        else {
            options.onRequestSent = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.onRequestSent;
            options.signal = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.signal;
            options.attempts = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.attempts;
            options.traceId = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.traceId;
        }
        (_a = options.traceId) !== null && _a !== void 0 ? _a : (options.traceId = UUIDv7());
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.gateway || !this.session || !('walletPublicKey' in this.session)) {
                throw new TonConnectError('Trying to send bridge request without session');
            }
            const id = (yield this.connectionStorage.getNextRpcRequestId()).toString();
            yield this.connectionStorage.increaseNextRpcRequestId();
            logDebug('Send http-bridge request:', Object.assign(Object.assign({}, request), { id }));
            const encodedRequest = this.session.sessionCrypto.encrypt(JSON.stringify(Object.assign(Object.assign({}, request), { id })), hexToByteArray(this.session.walletPublicKey));
            try {
                (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.emitBridgeClientMessageSent({
                    bridge_url: this.gateway.bridgeUrl,
                    client_id: this.session.sessionCrypto.sessionId,
                    wallet_id: this.session.walletPublicKey,
                    message_id: id,
                    request_type: request.method,
                    trace_id: options.traceId
                });
                yield this.gateway.send(encodedRequest, this.session.walletPublicKey, request.method, {
                    attempts: options === null || options === void 0 ? void 0 : options.attempts,
                    signal: options === null || options === void 0 ? void 0 : options.signal,
                    traceId: options.traceId
                });
                (_b = options === null || options === void 0 ? void 0 : options.onRequestSent) === null || _b === void 0 ? void 0 : _b.call(options);
                this.pendingRequests.set(id.toString(), resolve);
            }
            catch (e) {
                reject(e);
            }
        }));
    }
    closeConnection() {
        this.closeGateways();
        this.listeners = [];
        this.session = null;
        this.gateway = null;
    }
    disconnect(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                let called = false;
                let timeoutId = null;
                const onRequestSent = () => {
                    if (!called) {
                        called = true;
                        this.removeBridgeAndSession().then(resolve);
                    }
                };
                try {
                    this.closeGateways();
                    const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
                    timeoutId = setTimeout(() => {
                        abortController.abort();
                    }, this.defaultOpeningDeadlineMS);
                    yield this.sendRequest({ method: 'disconnect', params: [] }, {
                        onRequestSent: onRequestSent,
                        signal: abortController.signal,
                        attempts: 1,
                        traceId
                    });
                }
                catch (e) {
                    logDebug('Disconnect error:', e);
                    if (!called) {
                        this.removeBridgeAndSession().then(resolve);
                    }
                }
                finally {
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                    }
                    onRequestSent();
                }
            }));
        });
    }
    listen(callback) {
        this.listeners.push(callback);
        return () => (this.listeners = this.listeners.filter(listener => listener !== callback));
    }
    pause() {
        var _a;
        (_a = this.gateway) === null || _a === void 0 ? void 0 : _a.pause();
        this.pendingGateways.forEach(bridge => bridge.pause());
    }
    unPause() {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = this.pendingGateways.map(bridge => bridge.unPause());
            if (this.gateway) {
                promises.push(this.gateway.unPause());
            }
            yield Promise.all(promises);
        });
    }
    pendingGatewaysListener(gateway, bridgeUrl, bridgeIncomingMessage) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.pendingGateways.includes(gateway)) {
                yield gateway.close();
                return;
            }
            this.closeGateways({ except: gateway });
            if (this.gateway) {
                logDebug('Gateway is already opened, closing previous gateway');
                yield this.gateway.close();
            }
            this.session.bridgeUrl = bridgeUrl;
            this.gateway = gateway;
            this.gateway.setErrorsListener(this.gatewayErrorsListener.bind(this));
            this.gateway.setListener(this.gatewayListener.bind(this));
            return this.gatewayListener(bridgeIncomingMessage);
        });
    }
    gatewayListener(bridgeIncomingMessage) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const traceId = (_a = bridgeIncomingMessage.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            let walletMessage;
            try {
                walletMessage = JSON.parse(this.session.sessionCrypto.decrypt(Base64.decode(bridgeIncomingMessage.message).toUint8Array(), hexToByteArray(bridgeIncomingMessage.from)));
            }
            catch (err) {
                (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.emitBridgeClientMessageDecodeError({
                    bridge_url: this.session.bridgeUrl,
                    client_id: this.session.sessionCrypto.sessionId,
                    wallet_id: bridgeIncomingMessage.from,
                    error_message: String(err),
                    trace_id: bridgeIncomingMessage === null || bridgeIncomingMessage === void 0 ? void 0 : bridgeIncomingMessage.traceId
                });
                throw err;
            }
            logDebug('Wallet message received:', walletMessage);
            const requestType = 'event' in walletMessage ? walletMessage.event : '';
            (_c = this.analytics) === null || _c === void 0 ? void 0 : _c.emitBridgeClientMessageReceived({
                bridge_url: this.session.bridgeUrl,
                client_id: this.session.sessionCrypto.sessionId,
                wallet_id: bridgeIncomingMessage.from,
                message_id: String(walletMessage.id),
                request_type: requestType,
                trace_id: bridgeIncomingMessage === null || bridgeIncomingMessage === void 0 ? void 0 : bridgeIncomingMessage.traceId
            });
            if (!('event' in walletMessage)) {
                const id = walletMessage.id.toString();
                const resolve = this.pendingRequests.get(id);
                if (!resolve) {
                    logDebug(`Response id ${id} doesn't match any request's id`);
                    return;
                }
                resolve(Object.assign(Object.assign({}, walletMessage), { traceId }));
                this.pendingRequests.delete(id);
                return;
            }
            if (walletMessage.id !== undefined) {
                const lastId = yield this.connectionStorage.getLastWalletEventId();
                if (lastId !== undefined && walletMessage.id <= lastId) {
                    logError(`Received event id (=${walletMessage.id}) must be greater than stored last wallet event id (=${lastId}) `);
                    return;
                }
                if (walletMessage.event !== 'connect') {
                    yield this.connectionStorage.storeLastWalletEventId(walletMessage.id);
                }
            }
            // `this.listeners` might be modified in the event handler
            const listeners = this.listeners;
            if (walletMessage.event === 'connect') {
                yield this.updateSession(walletMessage, bridgeIncomingMessage.from);
            }
            if (walletMessage.event === 'disconnect') {
                logDebug(`Removing bridge and session: received disconnect event`);
                yield this.removeBridgeAndSession();
            }
            listeners.forEach(listener => listener(Object.assign(Object.assign({}, walletMessage), { traceId })));
        });
    }
    gatewayErrorsListener(e) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new TonConnectError(`Bridge error ${JSON.stringify(e)}`);
        });
    }
    updateSession(connectEvent, walletPublicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            this.session = Object.assign(Object.assign({}, this.session), { walletPublicKey });
            const tonAddrItem = connectEvent.payload.items.find(item => item.name === 'ton_addr');
            const connectEventToSave = Object.assign(Object.assign({}, connectEvent), { payload: Object.assign(Object.assign({}, connectEvent.payload), { items: [tonAddrItem] }) });
            yield this.connectionStorage.storeConnection({
                type: 'http',
                session: this.session,
                lastWalletEventId: connectEvent.id,
                connectEvent: connectEventToSave,
                nextRpcRequestId: 0
            });
        });
    }
    removeBridgeAndSession() {
        return __awaiter(this, void 0, void 0, function* () {
            this.closeConnection();
            yield this.connectionStorage.removeConnection();
        });
    }
    openGateways(sessionCrypto, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            if (Array.isArray(this.walletConnectionSource)) {
                // close all gateways before opening new ones
                this.pendingGateways.map(bridge => bridge.close().catch());
                // open new gateways
                this.pendingGateways = this.walletConnectionSource.map(source => {
                    const gateway = new BridgeGateway(this.connectionStorage.storage, source.bridgeUrl, sessionCrypto.sessionId, () => { }, () => { }, this.analyticsManager);
                    gateway.setListener(message => this.pendingGatewaysListener(gateway, source.bridgeUrl, message));
                    return gateway;
                });
                // Wait until the specified optional gateways are opened, not necessarily all gateways
                const gatewaysToWaitFor = Math.max(this.pendingGateways.length - this.optionalOpenGateways, 1);
                yield waitForSome(this.pendingGateways.map(bridge => callForSuccess((_options) => {
                    var _a;
                    if (!this.pendingGateways.some(item => item === bridge)) {
                        return bridge.close();
                    }
                    return bridge.registerSession({
                        openingDeadlineMS: (_a = options === null || options === void 0 ? void 0 : options.openingDeadlineMS) !== null && _a !== void 0 ? _a : this.defaultOpeningDeadlineMS,
                        signal: _options.signal,
                        traceId
                    });
                }, {
                    attempts: Number.MAX_SAFE_INTEGER,
                    delayMs: this.defaultRetryTimeoutMS,
                    signal: options === null || options === void 0 ? void 0 : options.signal
                })), gatewaysToWaitFor);
                return;
            }
            else {
                if (this.gateway) {
                    logDebug(`Gateway is already opened, closing previous gateway`);
                    yield this.gateway.close();
                }
                this.gateway = new BridgeGateway(this.connectionStorage.storage, this.walletConnectionSource.bridgeUrl, sessionCrypto.sessionId, this.gatewayListener.bind(this), this.gatewayErrorsListener.bind(this), this.analyticsManager);
                return yield this.gateway.registerSession({
                    openingDeadlineMS: options === null || options === void 0 ? void 0 : options.openingDeadlineMS,
                    signal: options === null || options === void 0 ? void 0 : options.signal,
                    traceId
                });
            }
        });
    }
    closeGateways(options) {
        var _a;
        (_a = this.gateway) === null || _a === void 0 ? void 0 : _a.close();
        this.pendingGateways
            .filter(item => item !== (options === null || options === void 0 ? void 0 : options.except))
            .forEach(bridge => bridge.close());
        this.pendingGateways = [];
    }
}

function hasProperty(value, propertyKey) {
    return hasProperties(value, [propertyKey]);
}
function hasProperties(value, propertyKeys) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    return propertyKeys.every(propertyKey => propertyKey in value);
}

function isJSBridgeWithMetadata(value) {
    try {
        if (!hasProperty(value, 'tonconnect') || !hasProperty(value.tonconnect, 'walletInfo')) {
            return false;
        }
        return hasProperties(value.tonconnect.walletInfo, [
            'name',
            'app_name',
            'image',
            'about_url',
            'platforms'
        ]);
    }
    catch (_a) {
        return false;
    }
}

/**
 * In memory storage like localStorage, but without persistence.
 * Uses as a fallback for localStorage in Safari's private mode.
 */
class InMemoryStorage {
    static getInstance() {
        if (!InMemoryStorage.instance) {
            InMemoryStorage.instance = new InMemoryStorage();
        }
        return InMemoryStorage.instance;
    }
    constructor() {
        this.storage = {};
    }
    get length() {
        return Object.keys(this.storage).length;
    }
    clear() {
        this.storage = {};
    }
    getItem(key) {
        var _a;
        return (_a = this.storage[key]) !== null && _a !== void 0 ? _a : null;
    }
    key(index) {
        var _a;
        const keys = Object.keys(this.storage);
        if (index < 0 || index >= keys.length) {
            return null;
        }
        return (_a = keys[index]) !== null && _a !== void 0 ? _a : null;
    }
    removeItem(key) {
        delete this.storage[key];
    }
    setItem(key, value) {
        this.storage[key] = value;
    }
}

function getWindow() {
    if (typeof window === 'undefined') {
        return undefined;
    }
    return window;
}
function getDocument() {
    if (typeof document === 'undefined') {
        return undefined;
    }
    return document;
}
function getWebPageManifest() {
    var _a;
    const origin = (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.location.origin;
    if (origin) {
        return origin + '/tonconnect-manifest.json';
    }
    return '';
}
function getOriginWithPath() {
    var _a, _b, _c, _d;
    const origin = (_b = (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.origin;
    const path = (_d = (_c = getWindow()) === null || _c === void 0 ? void 0 : _c.location) === null || _d === void 0 ? void 0 : _d.pathname;
    if (origin && path) {
        return origin + path;
    }
    return '';
}
/**
 * Returns `localStorage` if it is available. In Safari's private mode, it returns `InMemoryStorage`. In Node.js, it throws an error.
 */
function tryGetLocalStorage() {
    if (isLocalStorageAvailable()) {
        return localStorage;
    }
    if (isNodeJs()) {
        throw new TonConnectError('`localStorage` is unavailable, but it is required for TonConnect. For more details, see https://github.com/ton-connect/sdk/tree/main/packages/sdk#init-connector');
    }
    return InMemoryStorage.getInstance();
}
/**
 * Checks if `localStorage` is available.
 */
function isLocalStorageAvailable() {
    // We use a try/catch block because Safari's private mode throws an error when attempting to access localStorage.
    try {
        return typeof localStorage !== 'undefined';
    }
    catch (_a) {
        return false;
    }
}
/**
 * Checks if the environment is Node.js.
 */
function isNodeJs() {
    return (typeof process !== 'undefined' && process.versions != null && process.versions.node != null);
}
/**
 * Returns the current domain (hostname) if available.
 * In browser environment, returns window.location.hostname.
 * In Node.js environment or when window is not available, returns null.
 */
function getDomain() {
    try {
        // In browser environment
        if (typeof window !== 'undefined' && window.location) {
            return window.location.hostname;
        }
        else {
            // In Node.js environment, skip domain validation
            return null;
        }
    }
    catch (_a) {
        return null;
    }
}
/**
 * Returns an array of [key, value] pairs from window object if available.
 * In browser environment, returns Object.entries(window).
 * In Node.js environment or when window is not available, returns empty array.
 */
function getWindowEntries() {
    const window = getWindow();
    if (!window) {
        return [];
    }
    try {
        return Object.entries(window);
    }
    catch (_a) {
        return [];
    }
}

class InjectedProvider {
    static fromStorage(storage, analyticsManager) {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield storage.getInjectedConnection();
            return new InjectedProvider(storage, connection.jsBridgeKey, analyticsManager);
        });
    }
    static isWalletInjected(injectedWalletKey) {
        return InjectedProvider.isWindowContainsWallet(this.window, injectedWalletKey);
    }
    static isInsideWalletBrowser(injectedWalletKey) {
        if (InjectedProvider.isWindowContainsWallet(this.window, injectedWalletKey)) {
            return this.window[injectedWalletKey].tonconnect.isWalletBrowser;
        }
        return false;
    }
    static getCurrentlyInjectedWallets() {
        if (!this.window) {
            return [];
        }
        const windowEntries = getWindowEntries();
        const wallets = windowEntries.filter(([_key, value]) => isJSBridgeWithMetadata(value));
        return wallets.map(([jsBridgeKey, wallet]) => ({
            name: wallet.tonconnect.walletInfo.name,
            appName: wallet.tonconnect.walletInfo.app_name,
            aboutUrl: wallet.tonconnect.walletInfo.about_url,
            imageUrl: wallet.tonconnect.walletInfo.image,
            tondns: wallet.tonconnect.walletInfo.tondns,
            jsBridgeKey,
            injected: true,
            embedded: wallet.tonconnect.isWalletBrowser,
            platforms: wallet.tonconnect.walletInfo.platforms,
            features: wallet.tonconnect.walletInfo.features
        }));
    }
    static isWindowContainsWallet(window, injectedWalletKey) {
        return (!!window &&
            injectedWalletKey in window &&
            typeof window[injectedWalletKey] === 'object' &&
            'tonconnect' in window[injectedWalletKey]);
    }
    constructor(connectionStorage, injectedWalletKey, analyticsManager) {
        this.connectionStorage = connectionStorage;
        this.injectedWalletKey = injectedWalletKey;
        this.type = 'injected';
        this.unsubscribeCallback = null;
        this.listenSubscriptions = false;
        this.listeners = [];
        const window = InjectedProvider.window;
        if (!InjectedProvider.isWindowContainsWallet(window, injectedWalletKey)) {
            throw new WalletNotInjectedError();
        }
        this.injectedWallet = window[injectedWalletKey].tonconnect;
        if (analyticsManager) {
            this.analytics = analyticsManager.scoped({
                bridge_key: injectedWalletKey,
                wallet_app_name: this.injectedWallet.deviceInfo.appName,
                wallet_app_version: this.injectedWallet.deviceInfo.appVersion
            });
        }
    }
    connect(message, options) {
        this._connect(PROTOCOL_VERSION, message, options);
    }
    restoreConnection(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            try {
                logDebug(`Injected Provider restoring connection...`);
                (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.emitJsBridgeCall({
                    js_bridge_method: 'restoreConnection',
                    trace_id: traceId
                });
                const connectEvent = yield this.injectedWallet.restoreConnection();
                (_c = this.analytics) === null || _c === void 0 ? void 0 : _c.emitJsBridgeResponse({
                    js_bridge_method: 'restoreConnection',
                    trace_id: traceId
                });
                logDebug('Injected Provider restoring connection response', connectEvent);
                if (connectEvent.event === 'connect') {
                    this.makeSubscriptions({ traceId });
                    this.listeners.forEach(listener => listener(Object.assign(Object.assign({}, connectEvent), { traceId })));
                }
                else {
                    yield this.connectionStorage.removeConnection();
                }
            }
            catch (e) {
                (_d = this.analytics) === null || _d === void 0 ? void 0 : _d.emitJsBridgeError({
                    js_bridge_method: 'restoreConnection',
                    error_message: String(e),
                    trace_id: traceId
                });
                yield this.connectionStorage.removeConnection();
                console.error(e);
            }
        });
    }
    closeConnection() {
        if (this.listenSubscriptions) {
            this.injectedWallet.disconnect();
        }
        this.closeAllListeners();
    }
    disconnect(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            return new Promise(resolve => {
                const onRequestSent = () => {
                    this.closeAllListeners();
                    this.connectionStorage.removeConnection().then(resolve);
                };
                try {
                    this.injectedWallet.disconnect();
                    onRequestSent();
                }
                catch (e) {
                    logDebug(e);
                    this.sendRequest({
                        method: 'disconnect',
                        params: []
                    }, { onRequestSent, traceId });
                }
            });
        });
    }
    closeAllListeners() {
        var _a;
        this.listenSubscriptions = false;
        this.listeners = [];
        (_a = this.unsubscribeCallback) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    listen(eventsCallback) {
        this.listeners.push(eventsCallback);
        return () => (this.listeners = this.listeners.filter(listener => listener !== eventsCallback));
    }
    sendRequest(request, optionsOrOnRequestSent) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            // TODO: remove deprecated method
            const options = {};
            if (typeof optionsOrOnRequestSent === 'function') {
                options.onRequestSent = optionsOrOnRequestSent;
                options.traceId = UUIDv7();
            }
            else {
                options.onRequestSent = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.onRequestSent;
                options.signal = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.signal;
                options.attempts = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.attempts;
                options.traceId = (_a = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            }
            const id = (yield this.connectionStorage.getNextRpcRequestId()).toString();
            yield this.connectionStorage.increaseNextRpcRequestId();
            logDebug('Send injected-bridge request:', Object.assign(Object.assign({}, request), { id }));
            (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.emitJsBridgeCall({
                js_bridge_method: 'send'
            });
            const result = this.injectedWallet.send(Object.assign(Object.assign({}, request), { id }));
            result
                .then(response => {
                var _a;
                (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.emitJsBridgeResponse({
                    js_bridge_method: 'send'
                });
                logDebug('Wallet message received:', response);
            })
                .catch(error => {
                var _a;
                (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.emitJsBridgeError({
                    js_bridge_method: 'send',
                    error_message: String(error)
                });
            });
            (_c = options === null || options === void 0 ? void 0 : options.onRequestSent) === null || _c === void 0 ? void 0 : _c.call(options);
            return result;
        });
    }
    _connect(protocolVersion, message, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            try {
                logDebug(`Injected Provider connect request: protocolVersion: ${protocolVersion}, message:`, message);
                (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.emitJsBridgeCall({
                    js_bridge_method: 'connect',
                    trace_id: traceId
                });
                const connectEvent = yield this.injectedWallet.connect(protocolVersion, message);
                (_c = this.analytics) === null || _c === void 0 ? void 0 : _c.emitJsBridgeResponse({
                    js_bridge_method: 'connect'
                });
                logDebug('Injected Provider connect response:', connectEvent);
                if (connectEvent.event === 'connect') {
                    yield this.updateSession();
                    this.makeSubscriptions({ traceId });
                }
                this.listeners.forEach(listener => listener(Object.assign(Object.assign({}, connectEvent), { traceId })));
            }
            catch (e) {
                (_d = this.analytics) === null || _d === void 0 ? void 0 : _d.emitJsBridgeError({
                    js_bridge_method: 'connect',
                    error_message: String(e),
                    trace_id: traceId
                });
                logDebug('Injected Provider connect error:', e);
                const connectEventError = {
                    event: 'connect_error',
                    payload: {
                        code: 0,
                        message: e === null || e === void 0 ? void 0 : e.toString()
                    }
                };
                this.listeners.forEach(listener => listener(Object.assign(Object.assign({}, connectEventError), { traceId })));
            }
        });
    }
    makeSubscriptions(options) {
        var _a, _b, _c;
        this.listenSubscriptions = true;
        (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.emitJsBridgeCall({
            js_bridge_method: 'listen',
            trace_id: options.traceId
        });
        try {
            this.unsubscribeCallback = this.injectedWallet.listen(e => {
                var _a;
                const traceId = (_a = e.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
                logDebug('Wallet message received:', e);
                if (this.listenSubscriptions) {
                    this.listeners.forEach(listener => listener(Object.assign(Object.assign({}, e), { traceId })));
                }
                if (e.event === 'disconnect') {
                    this.disconnect({ traceId });
                }
            });
            (_b = this.analytics) === null || _b === void 0 ? void 0 : _b.emitJsBridgeResponse({
                js_bridge_method: 'listen',
                trace_id: options.traceId
            });
        }
        catch (err) {
            (_c = this.analytics) === null || _c === void 0 ? void 0 : _c.emitJsBridgeError({
                js_bridge_method: 'listen',
                error_message: String(err),
                trace_id: options.traceId
            });
            throw err;
        }
    }
    updateSession() {
        return this.connectionStorage.storeConnection({
            type: 'injected',
            jsBridgeKey: this.injectedWalletKey,
            nextRpcRequestId: 0
        });
    }
}
InjectedProvider.window = getWindow();

class BridgeConnectionStorage {
    constructor(storage, walletsListManager) {
        this.storage = storage;
        this.walletsListManager = walletsListManager;
        this.storeKey = 'ton-connect-storage_bridge-connection';
    }
    storeConnection(connection) {
        return __awaiter(this, void 0, void 0, function* () {
            if (connection.type === 'injected' || connection.type === 'wallet-connect') {
                return this.storage.setItem(this.storeKey, JSON.stringify(connection));
            }
            if (!isPendingConnectionHttp(connection)) {
                const rawSession = {
                    sessionKeyPair: connection.session.sessionCrypto.stringifyKeypair(),
                    walletPublicKey: connection.session.walletPublicKey,
                    bridgeUrl: connection.session.bridgeUrl
                };
                const rawConnection = {
                    type: 'http',
                    connectEvent: connection.connectEvent,
                    session: rawSession,
                    lastWalletEventId: connection.lastWalletEventId,
                    nextRpcRequestId: connection.nextRpcRequestId
                };
                return this.storage.setItem(this.storeKey, JSON.stringify(rawConnection));
            }
            const rawConnection = {
                type: 'http',
                connectionSource: connection.connectionSource,
                sessionCrypto: connection.sessionCrypto.stringifyKeypair(),
                createdAt: Date.now()
            };
            return this.storage.setItem(this.storeKey, JSON.stringify(rawConnection));
        });
    }
    removeConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.storage.removeItem(this.storeKey);
        });
    }
    getConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const stored = yield this.storage.getItem(this.storeKey);
                if (!stored) {
                    return null;
                }
                const connection = JSON.parse(stored);
                if (connection.type === 'injected' || connection.type === 'wallet-connect') {
                    return connection;
                }
                if (!isPendingConnectionHttpRaw(connection)) {
                    const sessionCrypto = new SessionCrypto(connection.session.sessionKeyPair);
                    return yield this.actualizeBridgeConnection({
                        type: 'http',
                        connectEvent: connection.connectEvent,
                        lastWalletEventId: connection.lastWalletEventId,
                        nextRpcRequestId: connection.nextRpcRequestId,
                        session: {
                            sessionCrypto,
                            bridgeUrl: connection.session.bridgeUrl,
                            walletPublicKey: connection.session.walletPublicKey
                        }
                    });
                }
                if (isExpiredPendingConnectionHttpRaw(connection)) {
                    yield this.removeConnection();
                    return null;
                }
                return {
                    type: 'http',
                    sessionCrypto: new SessionCrypto(connection.sessionCrypto),
                    connectionSource: connection.connectionSource
                };
            }
            catch (err) {
                logDebug('Error retrieving connection', err);
                return null;
            }
        });
    }
    getHttpConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (!connection) {
                throw new TonConnectError('Trying to read HTTP connection source while nothing is stored');
            }
            if (connection.type !== 'http') {
                throw new TonConnectError(`Trying to read HTTP connection source while ${connection.type} connection is stored`);
            }
            return connection;
        });
    }
    getHttpPendingConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (!connection) {
                throw new TonConnectError('Trying to read HTTP connection source while nothing is stored');
            }
            if (connection.type !== 'http') {
                throw new TonConnectError(`Trying to read HTTP connection source while ${connection.type} connection is stored`);
            }
            if (!isPendingConnectionHttp(connection)) {
                throw new TonConnectError('Trying to read HTTP-pending connection while http connection is stored');
            }
            return connection;
        });
    }
    getInjectedConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (!connection) {
                throw new TonConnectError('Trying to read Injected bridge connection source while nothing is stored');
            }
            if ((connection === null || connection === void 0 ? void 0 : connection.type) !== 'injected') {
                throw new TonConnectError(`Trying to read Injected bridge connection source while ${connection.type} connection is stored`);
            }
            return connection;
        });
    }
    getWalletConnectConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (!connection) {
                throw new TonConnectError('Trying to read wallet connect bridge connection source while nothing is stored');
            }
            if ((connection === null || connection === void 0 ? void 0 : connection.type) !== 'wallet-connect') {
                throw new TonConnectError(`Trying to read wallet connect bridge connection source while ${connection.type} connection is stored`);
            }
            return connection;
        });
    }
    storedConnectionType() {
        return __awaiter(this, void 0, void 0, function* () {
            const stored = yield this.storage.getItem(this.storeKey);
            if (!stored) {
                return null;
            }
            const connection = JSON.parse(stored);
            return connection.type;
        });
    }
    storeLastWalletEventId(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (connection && connection.type === 'http' && !isPendingConnectionHttp(connection)) {
                connection.lastWalletEventId = id;
                return this.storeConnection(connection);
            }
        });
    }
    getLastWalletEventId() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (connection && 'lastWalletEventId' in connection) {
                return connection.lastWalletEventId;
            }
            return undefined;
        });
    }
    increaseNextRpcRequestId() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (connection && 'nextRpcRequestId' in connection) {
                const lastId = connection.nextRpcRequestId || 0;
                connection.nextRpcRequestId = lastId + 1;
                return this.storeConnection(connection);
            }
        });
    }
    getNextRpcRequestId() {
        return __awaiter(this, void 0, void 0, function* () {
            const connection = yield this.getConnection();
            if (connection && 'nextRpcRequestId' in connection) {
                return connection.nextRpcRequestId || 0;
            }
            return 0;
        });
    }
    actualizeBridgeConnection(connection) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const appName = connection.connectEvent.payload.device.appName;
                const wallet = yield this.walletsListManager.getRemoteWallet(appName);
                if (wallet.bridgeUrl === connection.session.bridgeUrl) {
                    return connection;
                }
                const actualizedConnection = Object.assign(Object.assign({}, connection), { session: Object.assign(Object.assign({}, connection.session), { bridgeUrl: wallet.bridgeUrl }) });
                yield this.storeConnection(actualizedConnection);
                return actualizedConnection;
            }
            catch (error) {
                logDebug('Failed to actualize bridge connection', error);
                return connection;
            }
        });
    }
}

/**
 * Default storage to save protocol data, uses `localStorage` if it is available. In Safari's private mode, it uses `InMemoryStorage`. In Node.js, it throws an error.
 */
class DefaultStorage {
    constructor() {
        this.localStorage = tryGetLocalStorage();
    }
    getItem(key) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.localStorage.getItem(key);
        });
    }
    removeItem(key) {
        return __awaiter(this, void 0, void 0, function* () {
            this.localStorage.removeItem(key);
        });
    }
    setItem(key, value) {
        return __awaiter(this, void 0, void 0, function* () {
            this.localStorage.setItem(key, value);
        });
    }
}

/**
 * Checks if `WalletInfo` is `WalletInfoInjectable` and `WalletInfo` is injected to the current webpage (`walletInfo.injected === true`).
 * @param value WalletInfo to check.
 */
function isWalletInfoCurrentlyInjected(value) {
    return isWalletInfoInjectable(value) && value.injected;
}
/**
 * Checks if `WalletInfo` is `WalletInfoInjectable` and dApp is opened inside this wallet's browser.
 * @param value WalletInfo to check.
 */
function isWalletInfoCurrentlyEmbedded(value) {
    return isWalletInfoCurrentlyInjected(value) && value.embedded;
}
/**
 * Checks if `WalletInfo` is `WalletInfoInjected`, but doesn't check if it is injected to the page or not.
 * @param value WalletInfo to check.
 */
function isWalletInfoInjectable(value) {
    return 'jsBridgeKey' in value;
}
/**
 * Checks if `WalletInfo` is `WalletInfoRemote`.
 * @param value WalletInfo to check.
 */
function isWalletInfoRemote(value) {
    return 'bridgeUrl' in value;
}
/**
 * @deprecated use `isWalletInfoInjectable` or `isWalletInfoCurrentlyInjected` instead.
 * @param value WalletInfo to check.
 */
function isWalletInfoInjected(value) {
    return 'jsBridgeKey' in value;
}

const FALLBACK_WALLETS_LIST = [
    {
        app_name: 'telegram-wallet',
        name: 'Wallet',
        image: 'https://wallet.tg/images/logo-288.png',
        about_url: 'https://wallet.tg/',
        universal_url: 'https://t.me/wallet?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://walletbot.me/tonconnect-bridge/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: true
            },
            {
                name: 'SignData',
                types: ['text', 'binary', 'cell']
            }
        ]
    },
    {
        app_name: 'tonkeeper',
        name: 'Tonkeeper',
        image: 'https://tonkeeper.com/assets/tonconnect-icon.png',
        tondns: 'tonkeeper.ton',
        about_url: 'https://tonkeeper.com',
        universal_url: 'https://app.tonkeeper.com/ton-connect',
        deepLink: 'tonkeeper-tc://',
        bridge: [
            {
                type: 'sse',
                url: 'https://bridge.tonapi.io/bridge'
            },
            {
                type: 'js',
                key: 'tonkeeper'
            }
        ],
        platforms: ['ios', 'android', 'chrome', 'firefox', 'macos'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: true
            },
            {
                name: 'SignData',
                types: ['text', 'binary', 'cell']
            }
        ]
    },
    {
        app_name: 'mytonwallet',
        name: 'MyTonWallet',
        image: 'https://static.mytonwallet.io/icon-256.png',
        about_url: 'https://mytonwallet.io',
        universal_url: 'https://connect.mytonwallet.org',
        deepLink: 'mytonwallet-tc://',
        bridge: [
            {
                type: 'js',
                key: 'mytonwallet'
            },
            {
                type: 'sse',
                url: 'https://tonconnectbridge.mytonwallet.org/bridge/'
            }
        ],
        platforms: ['chrome', 'windows', 'macos', 'linux', 'ios', 'android', 'firefox'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            },
            {
                name: 'SignData',
                types: ['text', 'binary', 'cell']
            }
        ]
    },
    {
        app_name: 'tonhub',
        name: 'Tonhub',
        image: 'https://tonhub.com/tonconnect_logo.png',
        about_url: 'https://tonhub.com',
        universal_url: 'https://tonhub.com/ton-connect',
        bridge: [
            {
                type: 'js',
                key: 'tonhub'
            },
            {
                type: 'sse',
                url: 'https://connect.tonhubapi.com/tonconnect'
            }
        ],
        platforms: ['ios', 'android'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: true
            },
            {
                name: 'SignData',
                types: ['text', 'binary', 'cell']
            }
        ]
    },
    {
        app_name: 'bitgetTonWallet',
        name: 'Bitget Wallet',
        image: 'https://raw.githubusercontent.com/bitgetwallet/download/refs/heads/main/logo/png/bitget_wallet_logo_288_mini.png',
        about_url: 'https://web3.bitget.com',
        deepLink: 'bitkeep://',
        bridge: [
            {
                type: 'js',
                key: 'bitgetTonWallet'
            },
            {
                type: 'sse',
                url: 'https://ton-connect-bridge.bgwapi.io/bridge'
            }
        ],
        platforms: ['ios', 'android', 'chrome'],
        universal_url: 'https://bkcode.vip/ton-connect',
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'okxMiniWallet',
        name: 'OKX Mini Wallet',
        image: 'https://static.okx.com/cdn/assets/imgs/2411/8BE1A4A434D8F58A.png',
        about_url: 'https://www.okx.com/web3',
        universal_url: 'https://t.me/OKX_WALLET_BOT?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://www.okx.com/tonbridge/discover/rpc/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'binanceWeb3TonWallet',
        name: 'Binance Wallet',
        image: 'https://public.bnbstatic.com/static/binance-w3w/ton-provider/binancew3w.png',
        about_url: 'https://www.binance.com/en/web3wallet',
        deepLink: 'bnc://app.binance.com/cedefi/ton-connect',
        bridge: [
            {
                type: 'js',
                key: 'binancew3w'
            },
            {
                type: 'sse',
                url: 'https://wallet.binance.com/tonbridge/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        universal_url: 'https://app.binance.com/cedefi/ton-connect',
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'fintopio-tg',
        name: 'Fintopio',
        image: 'https://raw.githubusercontent.com/fintopio/ton-pub/refs/heads/main/logos/tonconnect-icon.png',
        about_url: 'https://fintopio.com',
        universal_url: 'https://t.me/fintopio?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://wallet-bridge.fintopio.com/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'okxTonWallet',
        name: 'OKX Wallet',
        image: 'https://static.okx.com/cdn/assets/imgs/247/58E63FEA47A2B7D7.png',
        about_url: 'https://www.okx.com/web3',
        universal_url: 'https://www.okx.com/download?appendQuery=true&deeplink=okx://web3/wallet/tonconnect',
        bridge: [
            {
                type: 'js',
                key: 'okxTonWallet'
            },
            {
                type: 'sse',
                url: 'https://www.okx.com/tonbridge/discover/rpc/bridge'
            }
        ],
        platforms: ['chrome', 'safari', 'firefox', 'ios', 'android'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'hot',
        name: 'HOT',
        image: 'https://raw.githubusercontent.com/hot-dao/media/main/logo.png',
        about_url: 'https://hot-labs.org/',
        universal_url: 'https://t.me/herewalletbot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://sse-bridge.hot-labs.org'
            },
            {
                type: 'js',
                key: 'hotWallet'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'bybitTonWallet',
        name: 'Bybit Wallet',
        image: 'https://raw.githubusercontent.com/bybit-web3/bybit-web3.github.io/main/docs/images/bybit-logo.png',
        about_url: 'https://www.bybit.com/web3',
        universal_url: 'https://app.bybit.com/ton-connect',
        deepLink: 'bybitapp://',
        bridge: [
            {
                type: 'js',
                key: 'bybitTonWallet'
            },
            {
                type: 'sse',
                url: 'https://api-node.bybit.com/spot/api/web3/bridge/ton/bridge'
            }
        ],
        platforms: ['ios', 'android', 'chrome'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'dewallet',
        name: 'DeWallet',
        image: 'https://raw.githubusercontent.com/delab-team/manifests-images/main/WalletAvatar.png',
        about_url: 'https://delabwallet.com',
        universal_url: 'https://t.me/dewallet?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://bridge.dewallet.pro/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'safepalwallet',
        name: 'SafePal',
        image: 'https://s.pvcliping.com/web/public_image/SafePal_x288.png',
        tondns: '',
        about_url: 'https://www.safepal.com',
        universal_url: 'https://link.safepal.io/ton-connect',
        deepLink: 'safepal-tc://',
        bridge: [
            {
                type: 'sse',
                url: 'https://ton-bridge.safepal.com/tonbridge/v1/bridge'
            },
            {
                type: 'js',
                key: 'safepalwallet'
            }
        ],
        platforms: ['ios', 'android', 'chrome', 'firefox'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 1,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'GateWallet',
        name: 'GateWallet',
        image: 'https://img.gatedataimg.com/prd-ordinal-imgs/036f07bb8730716e/gateio-0925.png',
        about_url: 'https://www.gate.io/',
        bridge: [
            {
                type: 'js',
                key: 'gatetonwallet'
            },
            {
                type: 'sse',
                url: 'https://dapp.gateio.services/tonbridge_api/bridge/v1'
            }
        ],
        platforms: ['ios', 'android'],
        universal_url: 'https://gate.onelink.me/Hls0/web3?gate_web3_wallet_universal_type=ton_connect',
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'openmask',
        name: 'OpenMask',
        image: 'https://raw.githubusercontent.com/OpenProduct/openmask-extension/main/public/openmask-logo-288.png',
        about_url: 'https://www.openmask.app/',
        bridge: [
            {
                type: 'js',
                key: 'openmask'
            }
        ],
        platforms: ['chrome'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'BitgetWeb3',
        name: 'BitgetWeb3',
        image: 'https://img.bitgetimg.com/image/third/1731638059795.png',
        about_url: '​https://www.bitget.com',
        universal_url: 'https://t.me/BitgetOfficialBot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://ton-connect-bridge.bgwapi.io/bridge'
            }
        ],
        platforms: ['ios', 'android', 'windows', 'macos', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'xtonwallet',
        name: 'XTONWallet',
        image: 'https://xtonwallet.com/assets/img/icon-256-back.png',
        about_url: 'https://xtonwallet.com',
        bridge: [
            {
                type: 'js',
                key: 'xtonwallet'
            }
        ],
        platforms: ['chrome', 'firefox'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 1,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'tonwallet',
        name: 'TON Wallet',
        image: 'https://wallet.ton.org/assets/ui/qr-logo.png',
        about_url: 'https://chrome.google.com/webstore/detail/ton-wallet/nphplpgoakhhjchkkhmiggakijnkhfnd',
        bridge: [
            {
                type: 'js',
                key: 'tonwallet'
            }
        ],
        platforms: ['chrome'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'bitgetWalletLite',
        name: 'Bitget Wallet Lite',
        image: 'https://raw.githubusercontent.com/bitgetwallet/download/refs/heads/main/logo/png/bitget_wallet_lite_logo_288.png',
        about_url: 'https://web3.bitget.com',
        universal_url: 'https://t.me/BitgetWallet_TGBot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://ton-connect-bridge.bgwapi.io/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'tomoWallet',
        name: 'Tomo Wallet',
        image: 'https://pub.tomo.inc/logo.png',
        about_url: 'https://www.tomo.inc/',
        universal_url: 'https://t.me/tomowalletbot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://go-bridge.tomo.inc/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'miraiapp-tg',
        name: 'Mirai Mini App',
        image: 'https://cdn.mirailabs.co/miraihub/miraiapp-tg-icon-288.png',
        about_url: 'https://mirai.app',
        universal_url: 'https://t.me/MiraiAppBot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://bridge.mirai.app'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            },
            {
                name: 'SignData',
                types: ['text', 'binary', 'cell']
            }
        ]
    },
    {
        app_name: 'Architec.ton',
        name: 'Architec.ton',
        image: 'https://raw.githubusercontent.com/Architec-Ton/wallet-tma/refs/heads/dev/public/images/arcwallet_logo.png',
        about_url: 'https://architecton.tech',
        universal_url: 'https://t.me/architec_ton_bot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://tc.architecton.su/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'tokenpocket',
        name: 'TokenPocket',
        image: 'https://hk.tpstatic.net/logo/tokenpocket.png',
        about_url: 'https://www.tokenpocket.pro',
        universal_url: 'https://tp-lab.tptool.pro/ton-connect/',
        bridge: [
            {
                type: 'js',
                key: 'tokenpocket'
            },
            {
                type: 'sse',
                url: 'https://ton-connect.mytokenpocket.vip/bridge'
            }
        ],
        platforms: ['ios', 'android'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'uxuyWallet',
        name: 'UXUY Wallet',
        image: 'https://chain-cdn.uxuy.com/logo/square_288.png',
        about_url: 'https://docs.uxuy.com',
        universal_url: 'https://t.me/UXUYbot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://bridge.uxuy.me/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'tonkeeper-pro',
        name: 'Tonkeeper Pro',
        image: 'https://tonkeeper.com/assets/tonconnect-icon-pro.png',
        tondns: 'tonkeeper.ton',
        about_url: 'https://tonkeeper.com/pro',
        universal_url: 'https://app.tonkeeper.com/pro/ton-connect',
        deepLink: 'tonkeeper-pro-tc://',
        bridge: [
            {
                type: 'sse',
                url: 'https://bridge.tonapi.io/bridge'
            }
        ],
        platforms: ['ios', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: true
            },
            {
                name: 'SignData',
                types: ['text', 'binary', 'cell']
            }
        ]
    },
    {
        app_name: 'nicegramWallet',
        name: 'Nicegram Wallet',
        image: 'https://static.nicegram.app/icon.png',
        about_url: 'https://nicegram.app',
        universal_url: 'https://nicegram.app/tc',
        deepLink: 'nicegram-tc://',
        bridge: [
            {
                type: 'sse',
                url: 'https://tc.nicegram.app/bridge'
            },
            {
                type: 'js',
                key: 'nicegramWallet'
            }
        ],
        platforms: ['ios', 'android'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'echoooTonWallet',
        name: 'EchoooWallet',
        image: 'https://cdn.echooo.xyz/front-end/source/images/logo/echooo-ton.png',
        about_url: 'https://www.echooo.xyz',
        universal_url: 'https://www.echooo.xyz/ton-connect',
        deepLink: 'echooo://',
        bridge: [
            {
                type: 'js',
                key: 'echoooTonWallet'
            },
            {
                type: 'sse',
                url: 'https://ton-connect-bridge.echooo.link/bridge'
            }
        ],
        platforms: ['ios', 'android'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'blitzwallet',
        name: 'BLITZ wallet',
        image: 'https://blitzwallet.cfd/wallet/public/logo.png',
        about_url: 'https://blitzwallet.cfd',
        universal_url: 'https://t.me/blitz_wallet_bot?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://blitzwallet.cfd/bridge/'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'koloWeb3Wallet',
        name: 'Kolo',
        image: 'https://raw.githubusercontent.com/onidev1/tc-assets/refs/heads/main/kolo_logo_288.png',
        about_url: 'https://kolo.xyz',
        universal_url: 'https://t.me/kolo?attach=wallet',
        bridge: [
            {
                type: 'sse',
                url: 'https://web3-bridge.kolo.in/bridge'
            }
        ],
        platforms: ['ios', 'android', 'macos', 'windows', 'linux'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'imToken',
        name: 'imToken',
        image: 'https://aws-v2-cdn.token.im/orbit/token-v2/icons/logo-ton-connect.png',
        about_url: 'https://token.im',
        universal_url: 'https://connect.token.im/link/ton-connect',
        deepLink: 'imtokenv2://link/ton-connect',
        bridge: [
            {
                type: 'sse',
                url: 'https://connect.token.im/tonbridge'
            },
            {
                type: 'js',
                key: 'imToken'
            }
        ],
        platforms: ['ios', 'android'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 255,
                extraCurrencySupported: false
            }
        ]
    },
    {
        app_name: 'cactuslink',
        name: 'Cactus Link',
        image: 'https://downloads.mycactus.com/288_cactus_link.png',
        about_url: 'https://www.mycactus.com/defi-connector',
        bridge: [
            {
                type: 'js',
                key: 'cactuslink_ton'
            }
        ],
        platforms: ['chrome'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            },
            {
                name: 'SignData',
                types: ['text', 'binary']
            }
        ]
    },
    {
        app_name: 'onekey',
        name: 'OneKey',
        image: 'https://uni.onekey-asset.com/static/logo/onekey-x288.png',
        about_url: 'https://onekey.so',
        bridge: [
            {
                type: 'js',
                key: 'onekeyTonWallet'
            }
        ],
        platforms: ['chrome'],
        features: [
            {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            }
        ]
    }
];

let qaModeEnabled = false;
let bannerObserver = null;
function enableQaMode() {
    qaModeEnabled = true;
    console.warn('🚨 QA Mode enabled - validation is disabled. This is unsafe for production!');
    showQaModeBanner();
    startBannerObserver();
    addQaModeStyles();
}
function isQaModeEnabled() {
    return qaModeEnabled;
}
function showQaModeBanner() {
    if (typeof window === 'undefined')
        return;
    const existingBanner = document.getElementById('ton-connect-qa-banner');
    if (existingBanner)
        return;
    const banner = document.createElement('div');
    banner.id = 'ton-connect-qa-banner';
    banner.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
        color: white;
        padding: 12px 20px;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-weight: 600;
        font-size: 14px;
        z-index: 999999;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        animation: slideDown 0.3s ease-out;
        user-select: none;
        pointer-events: none;
    `;
    banner.innerHTML = `
        🚨 QA Mode Active - Validation Disabled (Unsafe for Production)
    `;
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
        @keyframes slideDown {
            from { transform: translateY(-100%); }
            to { transform: translateY(0); }
        }
    `;
    document.head.appendChild(style);
    document.body.appendChild(banner);
    addQaModeStyles();
}
function addQaModeStyles() {
    if (typeof window === 'undefined')
        return;
    const existingStyle = document.getElementById('ton-connect-qa-mode-styles');
    if (existingStyle)
        return;
    const style = document.createElement('style');
    style.id = 'ton-connect-qa-mode-styles';
    style.textContent = `
        body.qa-mode-active {
            padding-top: 48px !important;
        }
        
        body.qa-mode-active header {
            margin-top: 48px !important;
        }
        
        body.qa-mode-active .qa-mode-control {
            top: 128px !important;
        }
    `;
    document.head.appendChild(style);
    document.body.classList.add('qa-mode-active');
}
function startBannerObserver() {
    if (typeof window === 'undefined' || bannerObserver)
        return;
    bannerObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.type === 'childList') {
                mutation.removedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node;
                        if (element.id === 'ton-connect-qa-banner' && qaModeEnabled) {
                            console.warn('QA Mode banner was removed, restoring...');
                            setTimeout(() => showQaModeBanner(), 100);
                        }
                        else if (element.id === 'ton-connect-qa-mode-styles' && qaModeEnabled) {
                            console.warn('QA Mode styles were removed, restoring...');
                            setTimeout(() => addQaModeStyles(), 100);
                        }
                    }
                });
            }
        });
    });
    bannerObserver.observe(document.body, {
        childList: true,
        subtree: false
    });
    bannerObserver.observe(document.head, {
        childList: true,
        subtree: false
    });
}

class WalletsListManager {
    constructor(options) {
        var _a;
        this.walletsListDTOCache = null;
        this.walletsListDTOCacheCreationTimestamp = null;
        if (isQaModeEnabled()) {
            this.walletsListSource =
                'https://raw.githubusercontent.com/ton-connect/wallets-list-staging/refs/heads/main/wallets-v2.json';
        }
        else {
            this.walletsListSource =
                (_a = options === null || options === void 0 ? void 0 : options.walletsListSource) !== null && _a !== void 0 ? _a : 'https://config.ton.org/wallets-v2.json';
        }
        this.cacheTTLMs = options === null || options === void 0 ? void 0 : options.cacheTTLMs;
        this.onDownloadDurationMeasured = options === null || options === void 0 ? void 0 : options.onDownloadDurationMeasured;
    }
    getWallets() {
        return __awaiter(this, void 0, void 0, function* () {
            const [walletsListDTO, currentlyInjectedWallets] = yield Promise.all([
                this.fetchWalletsListDTO(),
                this.getCurrentlyInjectedWallets()
            ]);
            return this.mergeWalletsLists(this.walletConfigDTOListToWalletConfigList(walletsListDTO), currentlyInjectedWallets);
        });
    }
    getEmbeddedWallet() {
        return __awaiter(this, void 0, void 0, function* () {
            const walletsList = yield this.getWallets();
            const embeddedWallets = walletsList.filter(isWalletInfoCurrentlyEmbedded);
            return embeddedWallets.length === 1 ? embeddedWallets[0] : null;
        });
    }
    fetchWalletsListDTO() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.cacheTTLMs &&
                this.walletsListDTOCacheCreationTimestamp &&
                Date.now() > this.walletsListDTOCacheCreationTimestamp + this.cacheTTLMs) {
                this.walletsListDTOCache = null;
            }
            if (!this.walletsListDTOCache) {
                this.walletsListDTOCache = this.fetchWalletsListFromSource();
                this.walletsListDTOCache
                    .then(() => {
                    this.walletsListDTOCacheCreationTimestamp = Date.now();
                })
                    .catch(() => {
                    this.walletsListDTOCache = null;
                    this.walletsListDTOCacheCreationTimestamp = null;
                });
            }
            return this.walletsListDTOCache;
        });
    }
    getRemoteWallet(appName) {
        return __awaiter(this, void 0, void 0, function* () {
            const walletsList = yield this.getWallets();
            const wallet = walletsList.find(wallet => wallet.appName === appName);
            if (!wallet) {
                throw new TonConnectError(`Wallet info not found for appName: "${appName}"`);
            }
            if (!isWalletInfoRemote(wallet)) {
                throw new TonConnectError(`Wallet "${appName}" is not remote`);
            }
            return wallet;
        });
    }
    fetchWalletsListFromSource() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            let walletsList = [];
            const startTime = performance.now();
            try {
                const walletsResponse = yield fetch(this.walletsListSource);
                walletsList = yield walletsResponse.json();
                if (!Array.isArray(walletsList)) {
                    throw new FetchWalletsError('Wrong wallets list format, wallets list must be an array.');
                }
                const wrongFormatWallets = walletsList.filter(wallet => !this.isCorrectWalletConfigDTO(wallet));
                if (wrongFormatWallets.length) {
                    logError(`Wallet(s) ${wrongFormatWallets
                        .map(wallet => (wallet === null || wallet === void 0 ? void 0 : wallet.name) || 'unknown')
                        .join(', ')} config format is wrong. They were removed from the wallets list.`);
                    walletsList = walletsList.filter(wallet => this.isCorrectWalletConfigDTO(wallet));
                }
                const endTime = performance.now();
                const duration = Math.round(endTime - startTime);
                (_a = this.onDownloadDurationMeasured) === null || _a === void 0 ? void 0 : _a.call(this, duration);
            }
            catch (e) {
                logError(e);
                walletsList = FALLBACK_WALLETS_LIST;
                (_b = this.onDownloadDurationMeasured) === null || _b === void 0 ? void 0 : _b.call(this, undefined);
            }
            return walletsList;
        });
    }
    getCurrentlyInjectedWallets() {
        if (!isQaModeEnabled()) {
            return [];
        }
        try {
            return InjectedProvider.getCurrentlyInjectedWallets();
        }
        catch (e) {
            logError(e);
            return [];
        }
    }
    walletConfigDTOListToWalletConfigList(walletConfigDTO) {
        return walletConfigDTO.map(walletConfigDTO => {
            const walletConfig = {
                name: walletConfigDTO.name,
                appName: walletConfigDTO.app_name,
                imageUrl: walletConfigDTO.image,
                aboutUrl: walletConfigDTO.about_url,
                tondns: walletConfigDTO.tondns,
                platforms: walletConfigDTO.platforms,
                features: walletConfigDTO.features
            };
            walletConfigDTO.bridge.forEach(bridge => {
                if (bridge.type === 'sse') {
                    walletConfig.bridgeUrl = bridge.url;
                    walletConfig.universalLink =
                        walletConfigDTO.universal_url;
                    walletConfig.deepLink = walletConfigDTO.deepLink;
                }
                if (bridge.type === 'js') {
                    const jsBridgeKey = bridge.key;
                    walletConfig.jsBridgeKey = jsBridgeKey;
                    walletConfig.injected =
                        InjectedProvider.isWalletInjected(jsBridgeKey);
                    walletConfig.embedded =
                        InjectedProvider.isInsideWalletBrowser(jsBridgeKey);
                }
            });
            return walletConfig;
        });
    }
    mergeWalletsLists(list1, list2) {
        const names = new Set(list1.concat(list2).map(item => item.name));
        return [...names.values()].map(name => {
            const list1Item = list1.find(item => item.name === name);
            const list2Item = list2.find(item => item.name === name);
            return Object.assign(Object.assign({}, (list1Item && Object.assign({}, list1Item))), (list2Item && Object.assign({}, list2Item)));
        });
    }
    // eslint-disable-next-line complexity
    isCorrectWalletConfigDTO(value) {
        if (!value || !(typeof value === 'object')) {
            return false;
        }
        const containsName = 'name' in value;
        const containsAppName = 'app_name' in value;
        const containsImage = 'image' in value;
        const containsAbout = 'about_url' in value;
        const containsPlatforms = 'platforms' in value;
        if (!containsName ||
            !containsImage ||
            !containsAbout ||
            !containsPlatforms ||
            !containsAppName) {
            return false;
        }
        if (!value.platforms ||
            !Array.isArray(value.platforms) ||
            !value.platforms.length) {
            return false;
        }
        if (!('bridge' in value) ||
            !Array.isArray(value.bridge) ||
            !value.bridge.length) {
            return false;
        }
        const bridge = value.bridge;
        if (bridge.some(item => !item || typeof item !== 'object' || !('type' in item))) {
            return false;
        }
        const sseBridge = bridge.find(item => item.type === 'sse');
        if (sseBridge) {
            if (!(typeof sseBridge === 'object' && 'url' in sseBridge) ||
                !sseBridge.url ||
                !value.universal_url) {
                return false;
            }
        }
        const jsBridge = bridge.find(item => item.type === 'js');
        if (jsBridge) {
            if (typeof jsBridge !== 'object' ||
                !('key' in jsBridge) ||
                !jsBridge.key) {
                return false;
            }
        }
        return true;
    }
}

function checkSendTransactionSupport(features, options) {
    var _a;
    const supportsDeprecatedSendTransactionFeature = features.includes('SendTransaction');
    const sendTransactionFeature = findFeature(features, 'SendTransaction');
    const requiredFeature = {
        minMessages: options.requiredMessagesNumber,
        extraCurrencyRequired: options.requireExtraCurrencies,
        itemTypes: options.requiredItemTypes
    };
    if (!supportsDeprecatedSendTransactionFeature && !sendTransactionFeature) {
        throw new WalletNotSupportFeatureError("Wallet doesn't support SendTransaction feature.", {
            cause: { requiredFeature: { featureName: 'SendTransaction', value: requiredFeature } }
        });
    }
    if (options.requireExtraCurrencies) {
        if (!sendTransactionFeature || !sendTransactionFeature.extraCurrencySupported) {
            throw new WalletNotSupportFeatureError(`Wallet is not able to handle such SendTransaction request. Extra currencies support is required.`, {
                cause: {
                    requiredFeature: { featureName: 'SendTransaction', value: requiredFeature }
                }
            });
        }
    }
    if ((_a = options.requiredItemTypes) === null || _a === void 0 ? void 0 : _a.length) {
        if (!(sendTransactionFeature === null || sendTransactionFeature === void 0 ? void 0 : sendTransactionFeature.itemTypes)) {
            throw new WalletNotSupportFeatureError(`Wallet doesn't support structured items in SendTransaction.`, {
                cause: {
                    requiredFeature: { featureName: 'SendTransaction', value: requiredFeature }
                }
            });
        }
        const unsupportedTypes = options.requiredItemTypes.filter(t => !sendTransactionFeature.itemTypes.includes(t));
        if (unsupportedTypes.length) {
            throw new WalletNotSupportFeatureError(`Wallet doesn't support item types: ${unsupportedTypes.join(', ')} in SendTransaction.`, {
                cause: {
                    requiredFeature: { featureName: 'SendTransaction', value: requiredFeature }
                }
            });
        }
    }
    if (sendTransactionFeature && sendTransactionFeature.maxMessages !== undefined) {
        if (sendTransactionFeature.maxMessages < options.requiredMessagesNumber) {
            throw new WalletNotSupportFeatureError(`Wallet is not able to handle such SendTransaction request. Max support messages number is ${sendTransactionFeature.maxMessages}, but ${options.requiredMessagesNumber} is required.`, {
                cause: {
                    requiredFeature: { featureName: 'SendTransaction', value: requiredFeature }
                }
            });
        }
        return;
    }
    logWarning("Connected wallet didn't provide information about max allowed messages in the SendTransaction request. Request may be rejected by the wallet.");
}
function checkSignDataSupport(features, options) {
    const signDataFeature = features.find(feature => feature && typeof feature === 'object' && feature.name === 'SignData');
    if (!signDataFeature) {
        throw new WalletNotSupportFeatureError("Wallet doesn't support SignData feature.", {
            cause: {
                requiredFeature: {
                    featureName: 'SignData',
                    value: { types: options.requiredTypes }
                }
            }
        });
    }
    const unsupportedTypes = options.requiredTypes.filter(requiredType => !signDataFeature.types.includes(requiredType));
    if (unsupportedTypes.length) {
        throw new WalletNotSupportFeatureError(`Wallet doesn't support required SignData types: ${unsupportedTypes.join(', ')}.`, {
            cause: {
                requiredFeature: { featureName: 'SignData', value: { types: unsupportedTypes } }
            }
        });
    }
}
function checkSignMessageSupport(features, options) {
    var _a;
    const signMessageFeature = findFeature(features, 'SignMessage');
    const requiredFeature = {
        minMessages: options.requiredMessagesNumber,
        extraCurrencyRequired: options.requireExtraCurrencies,
        itemTypes: options.requiredItemTypes
    };
    if (!signMessageFeature) {
        throw new WalletNotSupportFeatureError("Wallet doesn't support SignMessage feature.", {
            cause: { requiredFeature: { featureName: 'SignMessage', value: requiredFeature } }
        });
    }
    if (options.requireExtraCurrencies && !signMessageFeature.extraCurrencySupported) {
        throw new WalletNotSupportFeatureError(`Wallet is not able to handle such SignMessage request. Extra currencies support is required.`, {
            cause: {
                requiredFeature: { featureName: 'SignMessage', value: requiredFeature }
            }
        });
    }
    if ((_a = options.requiredItemTypes) === null || _a === void 0 ? void 0 : _a.length) {
        if (!signMessageFeature.itemTypes) {
            throw new WalletNotSupportFeatureError(`Wallet doesn't support structured items in SignMessage.`, {
                cause: {
                    requiredFeature: { featureName: 'SignMessage', value: requiredFeature }
                }
            });
        }
        const unsupportedTypes = options.requiredItemTypes.filter(t => !signMessageFeature.itemTypes.includes(t));
        if (unsupportedTypes.length) {
            throw new WalletNotSupportFeatureError(`Wallet doesn't support item types: ${unsupportedTypes.join(', ')} in SignMessage.`, {
                cause: {
                    requiredFeature: { featureName: 'SignMessage', value: requiredFeature }
                }
            });
        }
    }
    if (signMessageFeature.maxMessages !== undefined) {
        if (signMessageFeature.maxMessages < options.requiredMessagesNumber) {
            throw new WalletNotSupportFeatureError(`Wallet is not able to handle such SignMessage request. Max support messages number is ${signMessageFeature.maxMessages}, but ${options.requiredMessagesNumber} is required.`, {
                cause: {
                    requiredFeature: { featureName: 'SignMessage', value: requiredFeature }
                }
            });
        }
        return;
    }
    logWarning("Connected wallet didn't provide information about max allowed messages in the SignMessage request. Request may be rejected by the wallet.");
}
function checkRequiredWalletFeatures(features, walletsRequiredFeatures) {
    if (typeof walletsRequiredFeatures !== 'object') {
        return true;
    }
    const { sendTransaction, signData, signMessage, embeddedRequest } = walletsRequiredFeatures;
    if (sendTransaction) {
        const feature = findFeature(features, 'SendTransaction');
        if (!feature) {
            return false;
        }
        if (!checkSendTransaction(feature, sendTransaction)) {
            return false;
        }
    }
    if (signData) {
        const feature = findFeature(features, 'SignData');
        if (!feature) {
            return false;
        }
        if (!checkSignData(feature, signData)) {
            return false;
        }
    }
    if (signMessage) {
        const feature = findFeature(features, 'SignMessage');
        if (!feature) {
            return false;
        }
        if (!checkSignMessage(feature, signMessage)) {
            return false;
        }
    }
    if (embeddedRequest) {
        const feature = findFeature(features, 'EmbeddedRequest');
        if (!feature) {
            return false;
        }
    }
    return true;
}
function findFeature(features, requiredFeatureName) {
    return features.find(f => f && typeof f === 'object' && f.name === requiredFeatureName);
}
function checkSendTransaction(feature, requiredFeature) {
    var _a;
    const correctMessagesNumber = requiredFeature.minMessages === undefined ||
        requiredFeature.minMessages <= feature.maxMessages;
    const correctExtraCurrency = !requiredFeature.extraCurrencyRequired || feature.extraCurrencySupported;
    const correctItemTypes = !((_a = requiredFeature.itemTypes) === null || _a === void 0 ? void 0 : _a.length) ||
        (feature.itemTypes && requiredFeature.itemTypes.every(t => feature.itemTypes.includes(t)));
    return !!(correctMessagesNumber && correctExtraCurrency && correctItemTypes);
}
function checkSignMessage(feature, requiredFeature) {
    var _a;
    const correctMessagesNumber = requiredFeature.minMessages === undefined ||
        requiredFeature.minMessages <= feature.maxMessages;
    const correctExtraCurrency = !requiredFeature.extraCurrencyRequired || feature.extraCurrencySupported;
    const correctItemTypes = !((_a = requiredFeature.itemTypes) === null || _a === void 0 ? void 0 : _a.length) ||
        (feature.itemTypes && requiredFeature.itemTypes.every(t => feature.itemTypes.includes(t)));
    return !!(correctMessagesNumber && correctExtraCurrency && correctItemTypes);
}
function checkSignData(feature, requiredFeature) {
    return requiredFeature.types.every(requiredType => feature.types.includes(requiredType));
}

/**
 * Create a request version event.
 */
function createRequestVersionEvent() {
    return {
        type: 'request-version'
    };
}
/**
 * Create a response version event.
 * @param version
 */
function createResponseVersionEvent(version) {
    return {
        type: 'response-version',
        version: version
    };
}
/**
 * Create a version info.
 * @param version
 */
function createVersionInfo(version) {
    return {
        ton_connect_sdk_lib: version.ton_connect_sdk_lib,
        ton_connect_ui_lib: version.ton_connect_ui_lib
    };
}
// eslint-disable-next-line complexity
function createConnectionInfo(version, wallet, sessionInfo) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const isTonProof = ((_a = wallet === null || wallet === void 0 ? void 0 : wallet.connectItems) === null || _a === void 0 ? void 0 : _a.tonProof) && 'proof' in wallet.connectItems.tonProof;
    const authType = isTonProof ? 'ton_proof' : 'ton_addr';
    return {
        wallet_address: (_c = (_b = wallet === null || wallet === void 0 ? void 0 : wallet.account) === null || _b === void 0 ? void 0 : _b.address) !== null && _c !== void 0 ? _c : null,
        wallet_state_init: (_d = wallet === null || wallet === void 0 ? void 0 : wallet.account.walletStateInit) !== null && _d !== void 0 ? _d : null,
        wallet_type: (_e = wallet === null || wallet === void 0 ? void 0 : wallet.device.appName) !== null && _e !== void 0 ? _e : null,
        wallet_version: (_f = wallet === null || wallet === void 0 ? void 0 : wallet.device.appVersion) !== null && _f !== void 0 ? _f : null,
        auth_type: authType,
        custom_data: Object.assign({ client_id: (_g = sessionInfo === null || sessionInfo === void 0 ? void 0 : sessionInfo.clientId) !== null && _g !== void 0 ? _g : null, wallet_id: (_h = sessionInfo === null || sessionInfo === void 0 ? void 0 : sessionInfo.walletId) !== null && _h !== void 0 ? _h : null, chain_id: (_k = (_j = wallet === null || wallet === void 0 ? void 0 : wallet.account) === null || _j === void 0 ? void 0 : _j.chain) !== null && _k !== void 0 ? _k : null, provider: (_l = wallet === null || wallet === void 0 ? void 0 : wallet.provider) !== null && _l !== void 0 ? _l : null }, createVersionInfo(version))
    };
}
/**
 * Create a connection init event.
 */
function createConnectionStartedEvent(version, traceId) {
    return {
        type: 'connection-started',
        custom_data: createVersionInfo(version),
        trace_id: traceId !== null && traceId !== void 0 ? traceId : null
    };
}
/**
 * Create a connection completed event.
 * @param version
 * @param wallet
 * @param sessionInfo
 * @param traceId
 */
function createConnectionCompletedEvent(version, wallet, sessionInfo, traceId) {
    return Object.assign({ type: 'connection-completed', is_success: true, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo));
}
/**
 * Create a connection error event.
 * @param version
 * @param error_message
 * @param errorCode
 * @param sessionInfo
 * @param traceId
 */
function createConnectionErrorEvent(version, error_message, errorCode, sessionInfo, traceId) {
    var _a, _b;
    return {
        type: 'connection-error',
        is_success: false,
        error_message: error_message,
        error_code: errorCode !== null && errorCode !== void 0 ? errorCode : null,
        trace_id: traceId !== null && traceId !== void 0 ? traceId : null,
        custom_data: Object.assign({ client_id: (_a = sessionInfo === null || sessionInfo === void 0 ? void 0 : sessionInfo.clientId) !== null && _a !== void 0 ? _a : null, wallet_id: (_b = sessionInfo === null || sessionInfo === void 0 ? void 0 : sessionInfo.walletId) !== null && _b !== void 0 ? _b : null }, createVersionInfo(version))
    };
}
/**
 * Create a connection restoring started event.
 */
function createConnectionRestoringStartedEvent(version, traceId) {
    return {
        type: 'connection-restoring-started',
        custom_data: createVersionInfo(version),
        trace_id: traceId !== null && traceId !== void 0 ? traceId : null
    };
}
/**
 * Create a connection restoring completed event.
 * @param version
 * @param wallet
 * @param sessionInfo
 * @param traceId
 */
function createConnectionRestoringCompletedEvent(version, wallet, sessionInfo, traceId) {
    return Object.assign({ type: 'connection-restoring-completed', is_success: true, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo));
}
/**
 * Create a connection restoring error event.
 * @param version
 * @param errorMessage
 * @param traceId
 */
function createConnectionRestoringErrorEvent(version, errorMessage, traceId) {
    return {
        type: 'connection-restoring-error',
        is_success: false,
        error_message: errorMessage,
        trace_id: traceId !== null && traceId !== void 0 ? traceId : null,
        custom_data: createVersionInfo(version)
    };
}
function createTransactionInfo(wallet, transaction) {
    var _a, _b, _c, _d;
    return {
        valid_until: (_a = String(transaction.validUntil)) !== null && _a !== void 0 ? _a : null,
        from: (_d = (_b = transaction.from) !== null && _b !== void 0 ? _b : (_c = wallet === null || wallet === void 0 ? void 0 : wallet.account) === null || _c === void 0 ? void 0 : _c.address) !== null && _d !== void 0 ? _d : null,
        messages: hasMessages(transaction)
            ? transaction.messages.map(message => {
                var _a, _b;
                return ({
                    address: (_a = message.address) !== null && _a !== void 0 ? _a : null,
                    amount: (_b = message.amount) !== null && _b !== void 0 ? _b : null
                });
            })
            : []
    };
}
function createTransactionFullInfo(wallet, transaction) {
    var _a, _b, _c, _d;
    return {
        valid_until: (_a = String(transaction.validUntil)) !== null && _a !== void 0 ? _a : null,
        from: (_d = (_b = transaction.from) !== null && _b !== void 0 ? _b : (_c = wallet === null || wallet === void 0 ? void 0 : wallet.account) === null || _c === void 0 ? void 0 : _c.address) !== null && _d !== void 0 ? _d : null,
        messages: hasMessages(transaction)
            ? transaction.messages.map(message => {
                var _a, _b, _c, _d;
                return ({
                    address: (_a = message.address) !== null && _a !== void 0 ? _a : null,
                    amount: (_b = message.amount) !== null && _b !== void 0 ? _b : null,
                    payload: (_c = message.payload) !== null && _c !== void 0 ? _c : null,
                    state_init: (_d = message.stateInit) !== null && _d !== void 0 ? _d : null
                });
            })
            : []
    };
}
/**
 * Create a transaction init event.
 * @param version
 * @param wallet
 * @param transaction
 * @param sessionInfo
 * @param traceId
 */
function createTransactionSentForSignatureEvent(version, wallet, transaction, sessionInfo, traceId) {
    return Object.assign(Object.assign({ type: 'transaction-sent-for-signature', trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo)), createTransactionInfo(wallet, transaction));
}
/**
 * Create a transaction signed event.
 * @param version
 * @param wallet
 * @param transaction
 * @param signedTransaction
 * @param sessionInfo
 * @param traceId
 */
function createTransactionSignedEvent(version, wallet, transaction, signedTransaction, sessionInfo, traceId) {
    return Object.assign(Object.assign({ type: 'transaction-signed', is_success: true, signed_transaction: signedTransaction.boc, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo)), createTransactionInfo(wallet, transaction));
}
/**
 * Create a transaction error event.
 * @param version
 * @param wallet
 * @param transaction
 * @param errorMessage
 * @param errorCode
 * @param sessionInfo
 * @param traceId
 */
function createTransactionSigningFailedEvent(version, wallet, transaction, errorMessage, errorCode, sessionInfo, traceId) {
    return Object.assign(Object.assign({ type: 'transaction-signing-failed', is_success: false, error_message: errorMessage, error_code: errorCode !== null && errorCode !== void 0 ? errorCode : null, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo)), createTransactionFullInfo(wallet, transaction));
}
function createDataSentForSignatureEvent(version, wallet, data, sessionInfo, traceId) {
    return Object.assign({ type: 'sign-data-request-initiated', data, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo));
}
function createDataSignedEvent(version, wallet, data, signedData, sessionInfo, traceId) {
    return Object.assign({ type: 'sign-data-request-completed', is_success: true, data, signed_data: signedData, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo));
}
function createDataSigningFailedEvent(version, wallet, data, errorMessage, errorCode, sessionInfo, traceId) {
    return Object.assign({ type: 'sign-data-request-failed', is_success: false, data, error_message: errorMessage, error_code: errorCode !== null && errorCode !== void 0 ? errorCode : null, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo));
}
function createDisconnectionEvent(version, wallet, scope, sessionInfo, traceId) {
    return Object.assign({ type: 'disconnection', scope: scope, trace_id: traceId !== null && traceId !== void 0 ? traceId : null }, createConnectionInfo(version, wallet, sessionInfo));
}
function createWalletModalOpenedEvent(version, visibleWallets, clientId, traceId) {
    return {
        type: 'wallet-modal-opened',
        visible_wallets: visibleWallets,
        client_id: clientId !== null && clientId !== void 0 ? clientId : null,
        custom_data: version,
        trace_id: traceId !== null && traceId !== void 0 ? traceId : null
    };
}
function createSelectedWalletEvent(version, visibleWallets, lastSelectedWallet, walletsMenu, redirectLink, redirectLinkType, clientId, traceId) {
    var _a;
    let walletRedirectMethod = redirectLinkType;
    if (!walletRedirectMethod && redirectLink) {
        walletRedirectMethod = isTelegramUrl(redirectLink) ? 'tg_link' : 'external_link';
    }
    return {
        type: 'selected-wallet',
        wallets_menu: walletsMenu,
        visible_wallets: visibleWallets,
        client_id: clientId !== null && clientId !== void 0 ? clientId : null,
        custom_data: version,
        trace_id: traceId !== null && traceId !== void 0 ? traceId : null,
        wallet_redirect_method: walletRedirectMethod,
        wallet_redirect_link: redirectLink || undefined,
        wallet_type: (_a = lastSelectedWallet === null || lastSelectedWallet === void 0 ? void 0 : lastSelectedWallet.appName) !== null && _a !== void 0 ? _a : null
    };
}

/**
 * Tracker for TonConnect user actions, such as transaction signing, connection, etc.
 *
 * List of events:
 *  * `connection-started`: when a user starts connecting a wallet.
 *  * `connection-completed`: when a user successfully connected a wallet.
 *  * `connection-error`: when a user cancels a connection or there is an error during the connection process.
 *  * `connection-restoring-started`: when the dApp starts restoring a connection.
 *  * `connection-restoring-completed`: when the dApp successfully restores a connection.
 *  * `connection-restoring-error`: when the dApp fails to restore a connection.
 *  * `disconnection`: when a user starts disconnecting a wallet.
 *  * `transaction-sent-for-signature`: when a user sends a transaction for signature.
 *  * `transaction-signed`: when a user successfully signs a transaction.
 *  * `transaction-signing-failed`: when a user cancels transaction signing or there is an error during the signing process.
 *  * `sign-data-request-initiated`: when a user sends data for signature.
 *  * `sign-data-request-completed`: when a user successfully signs data.
 *  * `sign-data-request-failed`: when a user cancels data signing or there is an error during the signing process.
 *
 * If you want to track user actions, you can subscribe to the window events with prefix `ton-connect-`:
 *
 * @example
 * window.addEventListener('ton-connect-transaction-sent-for-signature', (event) => {
 *    console.log('Transaction init', event.detail);
 * });
 *
 * @internal
 */
class TonConnectTracker {
    /**
     * Version of the library.
     */
    get version() {
        return createVersionInfo({
            ton_connect_sdk_lib: this.tonConnectSdkVersion,
            ton_connect_ui_lib: this.tonConnectUiVersion
        });
    }
    constructor(options) {
        /**
         * Event prefix for user actions.
         * @private
         */
        this.eventPrefix = 'ton-connect-';
        /**
         * TonConnect UI version.
         */
        this.tonConnectUiVersion = null;
        this.eventDispatcher = options === null || options === void 0 ? void 0 : options.eventDispatcher;
        this.tonConnectSdkVersion = options.tonConnectSdkVersion;
        this.init().catch();
    }
    /**
     * Called once when the tracker is created and request version other libraries.
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.setRequestVersionHandler();
                this.tonConnectUiVersion = yield this.requestTonConnectUiVersion();
            }
            catch (e) { }
        });
    }
    /**
     * Set request version handler.
     * @private
     */
    setRequestVersionHandler() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.eventDispatcher.addEventListener('ton-connect-request-version', () => __awaiter(this, void 0, void 0, function* () {
                yield this.eventDispatcher.dispatchEvent('ton-connect-response-version', createResponseVersionEvent(this.tonConnectSdkVersion));
            }));
        });
    }
    /**
     * Request TonConnect UI version.
     * @private
     */
    requestTonConnectUiVersion() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield this.eventDispatcher.addEventListener('ton-connect-ui-response-version', (event) => {
                        resolve(event.detail.version);
                    }, { once: true });
                    yield this.eventDispatcher.dispatchEvent('ton-connect-ui-request-version', createRequestVersionEvent());
                }
                catch (e) {
                    reject(e);
                }
            }));
        });
    }
    /**
     * Emit user action event to the window.
     * @param eventDetails
     * @private
     */
    dispatchUserActionEvent(eventDetails) {
        try {
            this.eventDispatcher
                .dispatchEvent(`${this.eventPrefix}${eventDetails.type}`, eventDetails)
                .catch();
        }
        catch (e) { }
    }
    /**
     * Track connection init event.
     * @param args
     */
    trackConnectionStarted(...args) {
        try {
            const event = createConnectionStartedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track connection success event.
     * @param args
     */
    trackConnectionCompleted(...args) {
        try {
            const event = createConnectionCompletedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track connection error event.
     * @param args
     */
    trackConnectionError(...args) {
        try {
            const event = createConnectionErrorEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track connection restoring init event.
     * @param args
     */
    trackConnectionRestoringStarted(...args) {
        try {
            const event = createConnectionRestoringStartedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track connection restoring success event.
     * @param args
     */
    trackConnectionRestoringCompleted(...args) {
        try {
            const event = createConnectionRestoringCompletedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track connection restoring error event.
     * @param args
     */
    trackConnectionRestoringError(...args) {
        try {
            const event = createConnectionRestoringErrorEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track disconnect event.
     * @param args
     */
    trackDisconnection(...args) {
        try {
            const event = createDisconnectionEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track transaction init event.
     * @param args
     */
    trackTransactionSentForSignature(...args) {
        try {
            const event = createTransactionSentForSignatureEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track transaction signed event.
     * @param args
     */
    trackTransactionSigned(...args) {
        try {
            const event = createTransactionSignedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track transaction error event.
     * @param args
     */
    trackTransactionSigningFailed(...args) {
        try {
            const event = createTransactionSigningFailedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track sign data init event.
     * @param args
     */
    trackDataSentForSignature(...args) {
        try {
            const event = createDataSentForSignatureEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track sign data success event.
     * @param args
     */
    trackDataSigned(...args) {
        try {
            const event = createDataSignedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
    /**
     * Track sign data error event.
     * @param args
     */
    trackDataSigningFailed(...args) {
        try {
            const event = createDataSigningFailedEvent(this.version, ...args);
            this.dispatchUserActionEvent(event);
        }
        catch (e) { }
    }
}

const tonConnectSdkVersion = "3.4.1";

const bounceableTag = 0x11;
const noBounceableTag = 0x51;
const testOnlyTag = 0x80;
/**
 * Converts raw TON address to no-bounceable user-friendly format. [See details]{@link https://ton.org/docs/learn/overviews/addresses#user-friendly-address}
 * @param hexAddress raw TON address formatted as "0:<hex string without 0x>".
 * @param [testOnly=false] convert address to test-only form. [See details]{@link https://ton.org/docs/learn/overviews/addresses#user-friendly-address}
 */
function toUserFriendlyAddress(hexAddress, testOnly = false) {
    const { wc, hex } = parseHexAddress(hexAddress);
    let tag = noBounceableTag;
    if (testOnly) {
        tag |= testOnlyTag;
    }
    const addr = new Int8Array(34);
    addr[0] = tag;
    addr[1] = wc;
    addr.set(hex, 2);
    const addressWithChecksum = new Uint8Array(36);
    addressWithChecksum.set(addr);
    addressWithChecksum.set(crc16(addr), 34);
    let addressBase64 = Base64.encode(addressWithChecksum);
    return addressBase64.replace(/\+/g, '-').replace(/\//g, '_');
}
/**
 * Validates if the address is in user-friendly format by attempting to parse it.
 * @param address address to validate
 * @returns true if the address is valid user-friendly format, false otherwise
 */
function isValidUserFriendlyAddress(address) {
    try {
        parseUserFriendlyAddress(address);
        return true;
    }
    catch (_a) {
        return false;
    }
}
/**
 * Validates if the address is in raw hex format (e.g., "0:1234..." or "-1:1234...").
 * @param address address to validate
 * @returns true if the address is valid raw format, false otherwise
 */
function isValidRawAddress(address) {
    try {
        parseHexAddress(address);
        return true;
    }
    catch (_a) {
        return false;
    }
}
function toRawAddress({ wc, hex }) {
    return `${wc}:${hex}`;
}
/**
 * Parses user-friendly address and returns its components.
 * @param address user-friendly address
 * @returns parsed address components
 */
function parseUserFriendlyAddress(address) {
    const base64 = address.replace(/-/g, '+').replace(/_/g, '/');
    let decoded;
    try {
        decoded = Base64.decode(base64).toUint8Array();
    }
    catch (_a) {
        throw new WrongAddressError(`Invalid base64 encoding in address: ${address}`);
    }
    if (decoded.length !== 36) {
        throw new WrongAddressError(`Invalid address length: ${address}`);
    }
    const addr = decoded.slice(0, 34);
    const checksum = decoded.slice(34, 36);
    const calculatedChecksum = crc16(addr);
    if (!checksum.every((byte, i) => byte === calculatedChecksum[i])) {
        throw new WrongAddressError(`Invalid checksum in address: ${address}`);
    }
    let tag = addr[0];
    let isTestOnly = false;
    let isBounceable = false;
    if (tag & testOnlyTag) {
        isTestOnly = true;
        tag = tag ^ testOnlyTag;
    }
    if (tag !== bounceableTag && tag !== noBounceableTag) {
        throw new WrongAddressError(`Unknown address tag: ${tag}`);
    }
    isBounceable = tag === bounceableTag;
    let wc = null;
    if (addr[1] === 0xff) {
        // TODO we should read signed integer here
        wc = -1;
    }
    else {
        wc = addr[1];
    }
    const hex = addr.slice(2);
    if (wc !== 0 && wc !== -1) {
        throw new WrongAddressError(`Invalid workchain: ${wc}`);
    }
    return {
        wc,
        hex: Array.from(hex)
            .map(b => b.toString(16).padStart(2, '0'))
            .join(''),
        testOnly: isTestOnly,
        isBounceable
    };
}
function parseHexAddress(hexAddress) {
    if (!hexAddress.includes(':')) {
        throw new WrongAddressError(`Wrong address ${hexAddress}. Address must include ":".`);
    }
    const parts = hexAddress.split(':');
    if (parts.length !== 2) {
        throw new WrongAddressError(`Wrong address ${hexAddress}. Address must include ":" only once.`);
    }
    const wc = parseInt(parts[0]);
    if (wc !== 0 && wc !== -1) {
        throw new WrongAddressError(`Wrong address ${hexAddress}. WC must be eq 0 or -1, but ${wc} received.`);
    }
    const hex = parts[1];
    if ((hex === null || hex === void 0 ? void 0 : hex.length) !== 64) {
        throw new WrongAddressError(`Wrong address ${hexAddress}. Hex part must be 64bytes length, but ${hex === null || hex === void 0 ? void 0 : hex.length} received.`);
    }
    return {
        wc,
        hex: hexToBytes(hex)
    };
}
function crc16(data) {
    const poly = 0x1021;
    let reg = 0;
    const message = new Uint8Array(data.length + 2);
    message.set(data);
    for (let byte of message) {
        let mask = 0x80;
        while (mask > 0) {
            reg <<= 1;
            if (byte & mask) {
                reg += 1;
            }
            mask >>= 1;
            if (reg > 0xffff) {
                reg &= 0xffff;
                reg ^= poly;
            }
        }
    }
    return new Uint8Array([Math.floor(reg / 256), reg % 256]);
}
const toByteMap = {};
for (let ord = 0; ord <= 0xff; ord++) {
    let s = ord.toString(16);
    if (s.length < 2) {
        s = '0' + s;
    }
    toByteMap[s] = ord;
}
function hexToBytes(hex) {
    hex = hex.toLowerCase();
    const length2 = hex.length;
    if (length2 % 2 !== 0) {
        throw new ParseHexError('Hex string must have length a multiple of 2: ' + hex);
    }
    const length = length2 / 2;
    const result = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        const doubled = i * 2;
        const hexSubstring = hex.substring(doubled, doubled + 2);
        if (!toByteMap.hasOwnProperty(hexSubstring)) {
            throw new ParseHexError('Invalid hex character: ' + hexSubstring);
        }
        result[i] = toByteMap[hexSubstring];
    }
    return result;
}

const BASE64_REGEX = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
const BASE64URL_REGEX = /^[A-Za-z0-9\-_]+$/;
const BOC_PREFIX = 'te6cc';
const INTEGER_REGEX = /^-?\d+$/;
const POSITIVE_INTEGER_REGEX = /^\d+$/;
const MAX_DOMAIN_BYTES = 128;
const MAX_PAYLOAD_BYTES = 128;
const MAX_TOTAL_BYTES = 222;
function isValidNumber(value) {
    return typeof value === 'number' && !isNaN(value);
}
function isValidString(value) {
    return typeof value === 'string' && value.length > 0;
}
function isValidAddress(value) {
    return isValidString(value) && (isValidRawAddress(value) || isValidUserFriendlyAddress(value));
}
function isValidNetwork(value) {
    return isValidString(value) && /^-?\d+$/.test(value);
}
function isValidBoc(value) {
    return (typeof value === 'string' &&
        (BASE64_REGEX.test(value) || BASE64URL_REGEX.test(value)) &&
        value.startsWith(BOC_PREFIX));
}
function isValidObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isValidArray(value) {
    return Array.isArray(value);
}
function hasExtraProperties(obj, allowedKeys) {
    return Object.keys(obj).some(key => !allowedKeys.includes(key));
}
function validateSignMessageRequest(data) {
    return validateSendTransactionRequest(data);
}
function validateSendTransactionRequest(data) {
    if (!isValidObject(data)) {
        return 'Request must be an object';
    }
    const allowedKeys = ['validUntil', 'network', 'from', 'messages', 'items'];
    if (hasExtraProperties(data, allowedKeys)) {
        return 'Request contains extra properties';
    }
    if (data.validUntil) {
        if (!isValidNumber(data.validUntil)) {
            return "Incorrect 'validUntil'";
        }
        const now = Math.floor(Date.now() / 1000);
        const fiveMinutesFromNow = now + 300;
        if (data.validUntil > fiveMinutesFromNow) {
            console.warn(`validUntil (${data.validUntil}) is more than 5 minutes from now (${now})`);
        }
    }
    if (data.network !== undefined) {
        if (!isValidNetwork(data.network)) {
            return "Invalid 'network' format";
        }
    }
    if (data.from !== undefined && !isValidAddress(data.from)) {
        return "Invalid 'from' address format";
    }
    const hasMessagesField = isValidArray(data.messages);
    const hasItemsField = isValidArray(data.items);
    if (hasMessagesField && hasItemsField) {
        return "Request must contain either 'messages' or 'items', not both";
    }
    if (!hasMessagesField && !hasItemsField) {
        return "Request must contain 'messages' or 'items'";
    }
    if (hasMessagesField) {
        if (data.messages.length === 0) {
            return "'messages' must not be empty";
        }
        for (let i = 0; i < data.messages.length; i++) {
            const message = data.messages[i];
            const messageError = validateTransactionMessage(message, i);
            if (messageError) {
                return messageError;
            }
        }
    }
    if (hasItemsField) {
        const error = validateStructuredItems(data.items);
        if (error) {
            return error;
        }
    }
    return null;
}
function validateTransactionMessage(message, index) {
    if (!isValidObject(message)) {
        return `Message at index ${index} must be an object`;
    }
    const allowedKeys = ['address', 'amount', 'stateInit', 'payload', 'extraCurrency'];
    if (hasExtraProperties(message, allowedKeys)) {
        return `Message at index ${index} contains extra properties`;
    }
    if (!isValidString(message.address)) {
        return `'address' is required in message at index ${index}`;
    }
    if (!isValidUserFriendlyAddress(message.address)) {
        return `Wrong 'address' format in message at index ${index}`;
    }
    if (!isValidString(message.amount)) {
        return `'amount' is required in message at index ${index}`;
    }
    if (!/^[0-9]+$/.test(message.amount)) {
        return `Incorrect 'amount' in message at index ${index}`;
    }
    if (message.stateInit !== undefined) {
        if (!isValidString(message.stateInit) || !isValidBoc(message.stateInit)) {
            return `Invalid 'stateInit' in message at index ${index}`;
        }
    }
    if (message.payload !== undefined) {
        if (!isValidString(message.payload) || !isValidBoc(message.payload)) {
            return `Invalid 'payload' in message at index ${index}`;
        }
    }
    if (message.extraCurrency !== undefined) {
        if (!isValidObject(message.extraCurrency)) {
            return `Invalid 'extraCurrency' in message at index ${index}`;
        }
        for (const [key, value] of Object.entries(message.extraCurrency)) {
            if (!INTEGER_REGEX.test(key) ||
                typeof value !== 'string' ||
                !POSITIVE_INTEGER_REGEX.test(value)) {
                return `Invalid 'extraCurrency' format in message at index ${index}`;
            }
        }
    }
    return null;
}
function validateStructuredItems(items) {
    if (items.length === 0) {
        return "'items' must not be empty";
    }
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemError = validateStructuredItem(item, i);
        if (itemError) {
            return itemError;
        }
    }
    return null;
}
function validateStructuredItem(item, index) {
    if (!isValidObject(item)) {
        return `Item at index ${index} must be an object`;
    }
    if (!isValidString(item.type)) {
        return `'type' is required in item at index ${index}`;
    }
    switch (item.type) {
        case 'ton':
            return validateTonItem(item, index);
        case 'jetton':
            return validateJettonItem(item, index);
        case 'nft':
            return validateNftItem(item, index);
        default:
            return `Unknown item type '${item.type}' at index ${index}`;
    }
}
function validateTonItem(item, index) {
    const allowedKeys = ['type', 'address', 'amount', 'payload', 'stateInit', 'extraCurrency'];
    if (hasExtraProperties(item, allowedKeys)) {
        return `Ton item at index ${index} contains extra properties`;
    }
    if (!isValidString(item.address)) {
        return `'address' is required in ton item at index ${index}`;
    }
    if (!isValidUserFriendlyAddress(item.address)) {
        return `Wrong 'address' format in ton item at index ${index}`;
    }
    if (!isValidString(item.amount)) {
        return `'amount' is required in ton item at index ${index}`;
    }
    if (!/^[0-9]+$/.test(item.amount)) {
        return `Incorrect 'amount' in ton item at index ${index}`;
    }
    if (item.payload !== undefined) {
        if (!isValidString(item.payload) || !isValidBoc(item.payload)) {
            return `Invalid 'payload' in ton item at index ${index}`;
        }
    }
    if (item.stateInit !== undefined) {
        if (!isValidString(item.stateInit) || !isValidBoc(item.stateInit)) {
            return `Invalid 'stateInit' in ton item at index ${index}`;
        }
    }
    if (item.extraCurrency !== undefined) {
        if (!isValidObject(item.extraCurrency)) {
            return `Invalid 'extraCurrency' in ton item at index ${index}`;
        }
        for (const [key, value] of Object.entries(item.extraCurrency)) {
            if (!INTEGER_REGEX.test(key) ||
                typeof value !== 'string' ||
                !POSITIVE_INTEGER_REGEX.test(value)) {
                return `Invalid 'extraCurrency' format in ton item at index ${index}`;
            }
        }
    }
    return null;
}
// eslint-disable-next-line complexity
function validateJettonItem(item, index) {
    const allowedKeys = [
        'type',
        'master',
        'destination',
        'amount',
        'attachAmount',
        'responseDestination',
        'customPayload',
        'forwardAmount',
        'forwardPayload',
        'queryId'
    ];
    if (hasExtraProperties(item, allowedKeys)) {
        return `Jetton item at index ${index} contains extra properties`;
    }
    if (!isValidString(item.master)) {
        return `'master' is required in jetton item at index ${index}`;
    }
    if (!isValidAddress(item.master)) {
        return `Wrong 'master' address format in jetton item at index ${index}`;
    }
    if (!isValidString(item.destination)) {
        return `'destination' is required in jetton item at index ${index}`;
    }
    if (!isValidAddress(item.destination)) {
        return `Wrong 'destination' address format in jetton item at index ${index}`;
    }
    if (!isValidString(item.amount)) {
        return `'amount' is required in jetton item at index ${index}`;
    }
    if (!/^[0-9]+$/.test(item.amount)) {
        return `Incorrect 'amount' in jetton item at index ${index}`;
    }
    if (item.attachAmount !== undefined &&
        (!isValidString(item.attachAmount) || !/^[0-9]+$/.test(item.attachAmount))) {
        return `Invalid 'attachAmount' in jetton item at index ${index}`;
    }
    if (item.responseDestination !== undefined && !isValidAddress(item.responseDestination)) {
        return `Wrong 'responseDestination' address format in jetton item at index ${index}`;
    }
    if (item.customPayload !== undefined) {
        if (!isValidString(item.customPayload) || !isValidBoc(item.customPayload)) {
            return `Invalid 'customPayload' in jetton item at index ${index}`;
        }
    }
    if (item.forwardAmount !== undefined) {
        if (!isValidString(item.forwardAmount) || !/^[0-9]+$/.test(item.forwardAmount)) {
            return `Invalid 'forwardAmount' in jetton item at index ${index}`;
        }
    }
    if (item.forwardPayload !== undefined) {
        if (!isValidString(item.forwardPayload) || !isValidBoc(item.forwardPayload)) {
            return `Invalid 'forwardPayload' in jetton item at index ${index}`;
        }
    }
    if (item.queryId !== undefined) {
        if (!isValidString(item.queryId) || !/^[0-9]+$/.test(item.queryId)) {
            return `Invalid 'queryId' in jetton item at index ${index}`;
        }
    }
    return null;
}
// eslint-disable-next-line complexity
function validateNftItem(item, index) {
    const allowedKeys = [
        'type',
        'nftAddress',
        'newOwner',
        'attachAmount',
        'responseDestination',
        'customPayload',
        'forwardAmount',
        'forwardPayload',
        'queryId'
    ];
    if (hasExtraProperties(item, allowedKeys)) {
        return `NFT item at index ${index} contains extra properties`;
    }
    if (!isValidString(item.nftAddress)) {
        return `'nftAddress' is required in nft item at index ${index}`;
    }
    if (!isValidAddress(item.nftAddress)) {
        return `Wrong 'nftAddress' address format in nft item at index ${index}`;
    }
    if (!isValidString(item.newOwner)) {
        return `'newOwner' is required in nft item at index ${index}`;
    }
    if (!isValidAddress(item.newOwner)) {
        return `Wrong 'newOwner' address format in nft item at index ${index}`;
    }
    if (item.attachAmount !== undefined) {
        if (!isValidString(item.attachAmount) || !/^[0-9]+$/.test(item.attachAmount)) {
            return `Invalid 'attachAmount' in nft item at index ${index}`;
        }
    }
    if (item.responseDestination !== undefined && !isValidAddress(item.responseDestination)) {
        return `Wrong 'responseDestination' address format in nft item at index ${index}`;
    }
    if (item.customPayload !== undefined) {
        if (!isValidString(item.customPayload) || !isValidBoc(item.customPayload)) {
            return `Invalid 'customPayload' in nft item at index ${index}`;
        }
    }
    if (item.forwardAmount !== undefined) {
        if (!isValidString(item.forwardAmount) || !/^[0-9]+$/.test(item.forwardAmount)) {
            return `Invalid 'forwardAmount' in nft item at index ${index}`;
        }
    }
    if (item.forwardPayload !== undefined) {
        if (!isValidString(item.forwardPayload) || !isValidBoc(item.forwardPayload)) {
            return `Invalid 'forwardPayload' in nft item at index ${index}`;
        }
    }
    if (item.queryId !== undefined) {
        if (!isValidString(item.queryId) || !/^[0-9]+$/.test(item.queryId)) {
            return `Invalid 'queryId' in nft item at index ${index}`;
        }
    }
    return null;
}
function validateConnectAdditionalRequest(data) {
    if (!isValidObject(data)) {
        return 'Request must be an object';
    }
    const allowedKeys = ['tonProof'];
    if (hasExtraProperties(data, allowedKeys)) {
        return 'Request contains extra properties';
    }
    if (data.tonProof !== undefined) {
        if (typeof data.tonProof !== 'string') {
            return "Invalid 'tonProof'";
        }
        const payload = data.tonProof;
        if (payload.length === 0) {
            return "Empty 'tonProof' payload";
        }
        // Get current domain for validation first
        const domain = getDomain();
        if (!domain) {
            // In Node.js environment, skip domain validation
            return null;
        }
        // Validate domain size (max 128 bytes)
        const domainBytes = new TextEncoder().encode(domain).length;
        if (domainBytes > MAX_DOMAIN_BYTES) {
            return 'Current domain exceeds 128 bytes limit';
        }
        // Validate payload size (max 128 bytes)
        const payloadBytes = new TextEncoder().encode(payload).length;
        if (payloadBytes > MAX_PAYLOAD_BYTES) {
            return "'tonProof' payload exceeds 128 bytes limit";
        }
        // Validate total size (domain + payload <= 222 bytes)
        if (domainBytes + payloadBytes > MAX_TOTAL_BYTES) {
            return "'tonProof' domain + payload exceeds 222 bytes limit";
        }
    }
    return null;
}
function validateEmbeddedRequest(data) {
    if (!isValidObject(data)) {
        return 'Embedded request must be an object';
    }
    if (hasExtraProperties(data, ['method', 'request'])) {
        return 'Embedded request contains extra properties';
    }
    if (!isValidString(data.method)) {
        return "Embedded request: 'method' is required";
    }
    if (!('request' in data)) {
        return "Embedded request: 'request' is required";
    }
    const prefixError = (inner) => inner === null ? null : `Embedded ${data.method}: ${inner}`;
    switch (data.method) {
        case 'sendTransaction':
            return prefixError(validateSendTransactionRequest(data.request));
        case 'signData':
            return prefixError(validateSignDataPayload(data.request));
        case 'signMessage':
            return prefixError(validateSignMessageRequest(data.request));
    }
    return `Invalid 'method' value: ${data.method}`;
}
function validateSignDataPayload(data) {
    if (!isValidObject(data)) {
        return 'Payload must be an object';
    }
    if (!isValidString(data.type)) {
        return "'type' is required";
    }
    switch (data.type) {
        case 'text':
            return validateSignDataPayloadText(data);
        case 'binary':
            return validateSignDataPayloadBinary(data);
        case 'cell':
            return validateSignDataPayloadCell(data);
        default:
            return "Invalid 'type' value";
    }
}
function validateSignDataPayloadText(data) {
    const allowedKeys = ['type', 'text', 'network', 'from'];
    if (hasExtraProperties(data, allowedKeys)) {
        return 'Text payload contains extra properties';
    }
    if (!isValidString(data.text)) {
        return "'text' is required";
    }
    if (data.network !== undefined) {
        if (!isValidNetwork(data.network)) {
            return "Invalid 'network' format";
        }
    }
    if (data.from !== undefined && !isValidAddress(data.from)) {
        return "Invalid 'from'";
    }
    return null;
}
function validateSignDataPayloadBinary(data) {
    const allowedKeys = ['type', 'bytes', 'network', 'from'];
    if (hasExtraProperties(data, allowedKeys)) {
        return 'Binary payload contains extra properties';
    }
    if (!isValidString(data.bytes)) {
        return "'bytes' is required";
    }
    if (data.network !== undefined) {
        if (!isValidNetwork(data.network)) {
            return "Invalid 'network' format";
        }
    }
    if (data.from !== undefined && !isValidAddress(data.from)) {
        return "Invalid 'from'";
    }
    return null;
}
function validateSignDataPayloadCell(data) {
    const allowedKeys = ['type', 'schema', 'cell', 'network', 'from'];
    if (hasExtraProperties(data, allowedKeys)) {
        return 'Cell payload contains extra properties';
    }
    if (!isValidString(data.schema)) {
        return "'schema' is required";
    }
    if (!isValidString(data.cell)) {
        return "'cell' is required";
    }
    if (!isValidBoc(data.cell)) {
        return "Invalid 'cell' format (must be valid base64)";
    }
    if (data.network !== undefined) {
        if (!isValidNetwork(data.network)) {
            return "Invalid 'network' format";
        }
    }
    if (data.from !== undefined && !isValidAddress(data.from)) {
        return "Invalid 'from'";
    }
    return null;
}
/**
 * Validates ton_proof item received from wallet in connect event.
 */
// eslint-disable-next-line complexity
function validateTonProofItemReply(data) {
    if (!isValidObject(data)) {
        return 'ton_proof item must be an object';
    }
    const allowedKeys = ['error', 'proof', 'name'];
    if (hasExtraProperties(data, allowedKeys)) {
        return 'ton_proof item contains extra properties';
    }
    const hasProof = Object.prototype.hasOwnProperty.call(data, 'proof');
    const hasError = Object.prototype.hasOwnProperty.call(data, 'error');
    if (!hasProof && !hasError) {
        return "'ton_proof' item must contain either 'proof' or 'error'";
    }
    if (hasProof && hasError) {
        return "'ton_proof' item must contain either 'proof' or 'error', not both";
    }
    if (hasProof) {
        const proof = data.proof;
        if (!isValidObject(proof)) {
            return "Invalid 'proof' object";
        }
        if (!isValidNumber(proof.timestamp)) {
            return "Invalid 'proof.timestamp'";
        }
        const domain = proof.domain;
        if (!isValidObject(domain)) {
            return "Invalid 'proof.domain'";
        }
        if (!isValidNumber(domain.lengthBytes)) {
            return "Invalid 'proof.domain.lengthBytes'";
        }
        if (!isValidString(domain.value)) {
            return "Invalid 'proof.domain.value'";
        }
        // Try to verify that provided byte length matches actual byte length of value
        try {
            const encoderAvailable = typeof TextEncoder !== 'undefined';
            const actualLength = encoderAvailable
                ? new TextEncoder().encode(domain.value).length
                : domain.value.length;
            if (actualLength !== domain.lengthBytes) {
                return "'proof.domain.lengthBytes' does not match 'proof.domain.value'";
            }
        }
        catch (_a) {
            // Ignore environment issues; best-effort validation
        }
        if (!isValidString(proof.payload)) {
            return "Invalid 'proof.payload'";
        }
        if (!isValidString(proof.signature) || !BASE64_REGEX.test(proof.signature)) {
            return "Invalid 'proof.signature' format";
        }
    }
    if (hasError) {
        const error = data.error;
        if (!isValidObject(error)) {
            return "Invalid 'error' object";
        }
        const allowedErrorKeys = ['code', 'message'];
        if (hasExtraProperties(error, allowedErrorKeys)) {
            return 'ton_proof error contains extra properties';
        }
        if (!isValidNumber(error.code)) {
            return "Invalid 'error.code'";
        }
        if (!isValidString(error.message)) {
            return "Invalid 'error.message'";
        }
    }
    return null;
}

function normalizeStructuredItem(item) {
    switch (item.type) {
        case 'ton': {
            const { extraCurrency } = item, rest = __rest(item, ["extraCurrency"]);
            return Object.assign(Object.assign({}, rest), { payload: normalizeBase64(item.payload), stateInit: normalizeBase64(item.stateInit), extra_currency: extraCurrency });
        }
        case 'jetton': {
            return Object.assign(Object.assign({}, item), { customPayload: normalizeBase64(item.customPayload), forwardPayload: normalizeBase64(item.forwardPayload) });
        }
        case 'nft': {
            return Object.assign(Object.assign({}, item), { customPayload: normalizeBase64(item.customPayload), forwardPayload: normalizeBase64(item.forwardPayload) });
        }
    }
}

/**
 * Converts a PascalCase (or camelCase) string to kebab-case.
 *
 * For example:
 * - "PascalCase" → "pascal-case"
 * - "camelCaseExample" → "camel-case-example"
 *
 * @param value - The input string in PascalCase or camelCase format.
 * @returns The converted kebab-case string.
 */
function pascalToKebab(value) {
    return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
/**
 * Collects static connection metrics from the browser's Performance API.
 * TTFB is measured once at page load and doesn't change.
 * @returns An object containing static connection metrics (TTFB) or empty object if not available.
 */
function getStaticConnectionMetrics() {
    const metrics = {};
    // Get TTFB from Navigation Timing API (static, measured once at page load)
    try {
        const navEntries = performance.getEntriesByType('navigation');
        if (navEntries.length > 0) {
            const nav = navEntries[0];
            if (nav.responseStart && nav.requestStart) {
                metrics.conn_ttfb = Math.round(nav.responseStart - nav.requestStart);
            }
        }
    }
    catch (e) {
        // Performance API not available or error occurred
    }
    return metrics;
}
/**
 * Collects dynamic connection metrics from the browser's Network Information API.
 * @returns An object containing dynamic connection metrics (RTT, network type) or empty object if not available.
 */
function getDynamicConnectionMetrics() {
    const metrics = {};
    // Get RTT and network type from Network Information API (dynamic, can change)
    try {
        const navigatorWithConnection = navigator;
        const connection = navigatorWithConnection.connection ||
            navigatorWithConnection.mozConnection ||
            navigatorWithConnection.webkitConnection;
        if (connection) {
            if (connection.rtt !== undefined) {
                metrics.conn_rtt = connection.rtt;
            }
            if (connection.effectiveType) {
                metrics.conn_network_type = connection.effectiveType;
            }
            else if (connection.type) {
                metrics.conn_network_type = connection.type;
            }
        }
    }
    catch (e) {
        // Network Information API not available or error occurred
    }
    return metrics;
}

class AnalyticsManager {
    constructor(options = {}) {
        var _a, _b, _c, _d, _e, _f;
        this.events = [];
        this.timeoutId = null;
        this.isProcessing = false;
        this.backoff = 1;
        this.shouldSend = true;
        this.batchTimeoutMs = (_a = options.batchTimeoutMs) !== null && _a !== void 0 ? _a : 2000;
        this.currentBatchTimeoutMs = this.batchTimeoutMs;
        this.maxBatchSize = (_b = options.maxBatchSize) !== null && _b !== void 0 ? _b : 100;
        this.analyticsUrl = (_c = options.analyticsUrl) !== null && _c !== void 0 ? _c : 'https://analytics.ton.org/events';
        this.mode = (_d = options.mode) !== null && _d !== void 0 ? _d : 'telemetry';
        this.baseEvent = Object.assign({ subsystem: 'dapp-sdk', version: tonConnectSdkVersion, client_environment: (_f = (_e = options.environment) === null || _e === void 0 ? void 0 : _e.getClientEnvironment) === null || _f === void 0 ? void 0 : _f.call(_e) }, getStaticConnectionMetrics());
        this.addWindowFocusAndBlurSubscriptions();
    }
    scoped(sharedData) {
        return new Proxy(this, {
            get(target, prop) {
                const propName = prop.toString();
                if (propName.startsWith('emit')) {
                    const eventNamePascal = propName.replace('emit', '');
                    const eventNameKebab = pascalToKebab(eventNamePascal);
                    return function (event) {
                        const executedData = Object.fromEntries(Object.entries(sharedData !== null && sharedData !== void 0 ? sharedData : {}).map(([key, value]) => [
                            key,
                            typeof value === 'function' ? value() : value
                        ]));
                        return target.emit(Object.assign(Object.assign({ event_name: eventNameKebab }, executedData), event));
                    };
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return target[prop];
            }
        });
    }
    emit(event) {
        var _a;
        if (this.mode === 'off') {
            return;
        }
        const traceId = (_a = event.trace_id) !== null && _a !== void 0 ? _a : UUIDv7();
        const dynamicMetrics = getDynamicConnectionMetrics();
        const enhancedEvent = Object.assign(Object.assign(Object.assign(Object.assign({}, this.baseEvent), dynamicMetrics), event), { event_id: UUIDv7(), client_timestamp: Math.floor(Date.now() / 1000), trace_id: traceId });
        const filteredEvent = this.mode === 'telemetry' ? this.filterFullModeFields(enhancedEvent) : enhancedEvent;
        if (isQaModeEnabled()) {
            logDebug(filteredEvent);
        }
        this.events.push(filteredEvent);
        if (this.events.length >= this.maxBatchSize) {
            void this.flush();
            return;
        }
        this.startTimeout();
    }
    startTimeout() {
        if (this.timeoutId || this.isProcessing) {
            return;
        }
        this.timeoutId = setTimeout(() => {
            void this.flush();
        }, this.currentBatchTimeoutMs);
    }
    flush() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isProcessing || this.events.length === 0 || !this.shouldSend) {
                return;
            }
            this.clearTimeout();
            this.isProcessing = true;
            const eventsToSend = this.extractEventsToSend();
            try {
                yield this.processEventsBatch(eventsToSend);
                logDebug('Analytics events sent successfully');
            }
            catch (error) {
                this.restoreEvents(eventsToSend);
                logError('Failed to send analytics events:', error);
            }
            finally {
                this.isProcessing = false;
                this.scheduleNextFlushIfNeeded();
            }
        });
    }
    clearTimeout() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
    extractEventsToSend() {
        const eventsToSend = this.events.slice(0, this.maxBatchSize);
        this.events = this.events.slice(this.maxBatchSize);
        return eventsToSend;
    }
    processEventsBatch(eventsToSend) {
        return __awaiter(this, void 0, void 0, function* () {
            logDebug('Sending analytics events...', eventsToSend.length);
            try {
                const response = yield this.sendEvents(eventsToSend);
                this.handleResponse(response);
            }
            catch (err) {
                this.handleUnknownError(err);
            }
        });
    }
    handleResponse(response) {
        const { status, statusText } = response;
        if (this.isTooManyRequests(status)) {
            this.handleTooManyRequests(status, statusText);
        }
        else if (this.isClientError(status)) {
            this.handleClientError(status, statusText);
        }
        else if (this.isServerError(status)) {
            this.handleUnknownError({ status, statusText });
        }
    }
    restoreEvents(eventsToSend) {
        this.events.unshift(...eventsToSend);
    }
    scheduleNextFlushIfNeeded() {
        if (this.events.length > 0) {
            this.startTimeout();
        }
    }
    sendEvents(events) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield fetch(this.analyticsUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Timestamp': Math.floor(Date.now() / 1000).toString()
                },
                body: JSON.stringify(events)
            });
        });
    }
    isClientError(status) {
        return (status >= AnalyticsManager.HTTP_STATUS.CLIENT_ERROR_START &&
            status < AnalyticsManager.HTTP_STATUS.SERVER_ERROR_START);
    }
    isServerError(status) {
        return status >= AnalyticsManager.HTTP_STATUS.SERVER_ERROR_START;
    }
    isTooManyRequests(status) {
        return status === AnalyticsManager.HTTP_STATUS.TOO_MANY_REQUESTS;
    }
    handleClientError(status, statusText) {
        // Don't retry
        logError('Failed to send analytics events:', new TonConnectError(`Analytics API error: ${status} ${statusText}`));
    }
    handleUnknownError(error) {
        if (this.backoff < AnalyticsManager.MAX_BACKOFF_ATTEMPTS) {
            this.backoff++;
            this.currentBatchTimeoutMs *= AnalyticsManager.BACKOFF_MULTIPLIER;
            throw new TonConnectError(`Unknown analytics API error: ${error}`);
        }
        else {
            this.currentBatchTimeoutMs = this.batchTimeoutMs;
            this.backoff = 1;
            return; // Don't retry
        }
    }
    handleTooManyRequests(status, statusText) {
        throw new TonConnectError(`Analytics API error: ${status} ${statusText}`);
    }
    addWindowFocusAndBlurSubscriptions() {
        const document = getDocument();
        if (!document) {
            return;
        }
        try {
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.clearTimeout();
                    this.shouldSend = false;
                }
                else {
                    this.shouldSend = true;
                    this.scheduleNextFlushIfNeeded();
                }
            });
        }
        catch (e) {
            logError('Cannot subscribe to the document.visibilitychange: ', e);
        }
    }
    getMode() {
        return this.mode;
    }
    getPendingEventsCount() {
        return this.events.length;
    }
    filterFullModeFields(event) {
        const filtered = Object.assign({}, event);
        for (const field of AnalyticsManager.FULL_MODE_FIELDS) {
            delete filtered[field];
        }
        // wallet_address is kept for error events, removed for non-error events
        const eventName = 'event_name' in event ? String(event.event_name) : '';
        const isErrorEvent = 'error_code' in event ||
            'error_message' in event ||
            eventName.includes('error') ||
            eventName === 'connection-error' ||
            eventName === 'transaction-signing-failed' ||
            eventName === 'sign-data-request-failed';
        if (!isErrorEvent && 'wallet_address' in filtered) {
            delete filtered.wallet_address;
        }
        return filtered;
    }
    setWalletListDownloadDuration(duration) {
        this.baseEvent = Object.assign(Object.assign({}, this.baseEvent), { wallet_list_download_duration: duration });
    }
}
AnalyticsManager.HTTP_STATUS = {
    TOO_MANY_REQUESTS: 429,
    CLIENT_ERROR_START: 400,
    SERVER_ERROR_START: 500
};
AnalyticsManager.MAX_BACKOFF_ATTEMPTS = 5;
AnalyticsManager.BACKOFF_MULTIPLIER = 2;
AnalyticsManager.FULL_MODE_FIELDS = [
    'user_id',
    'tg_id',
    'locale',
    'tma_is_premium'
];

/**
 * A concrete implementation of EventDispatcher that dispatches events to the browser window.
 */
class BrowserEventDispatcher {
    constructor() {
        /**
         * The window object, possibly undefined in a server environment.
         * @private
         */
        this.window = getWindow();
    }
    /**
     * Dispatches an event with the given name and details to the browser window.
     * @param eventName - The name of the event to dispatch.
     * @param eventDetails - The details of the event to dispatch.
     * @returns A promise that resolves when the event has been dispatched.
     */
    dispatchEvent(eventName, eventDetails) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const event = new CustomEvent(eventName, { detail: eventDetails });
            (_a = this.window) === null || _a === void 0 ? void 0 : _a.dispatchEvent(event);
        });
    }
    /**
     * Adds an event listener to the browser window.
     * @param eventName - The name of the event to listen for.
     * @param listener - The listener to add.
     * @param options - The options for the listener.
     * @returns A function that removes the listener.
     */
    addEventListener(eventName, listener, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this.window) === null || _a === void 0 ? void 0 : _a.addEventListener(eventName, listener, options);
            return () => {
                var _a;
                return (_a = this.window) === null || _a === void 0 ? void 0 : _a.removeEventListener(eventName, listener);
            };
        });
    }
}

function buildVersionInfo(version) {
    return {
        '@tonconnect/sdk': version.ton_connect_sdk_lib || '',
        '@tonconnect/ui': version.ton_connect_ui_lib || ''
    };
}
function buildTonConnectEvent(detail) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return {
        versions: buildVersionInfo(detail.custom_data),
        network_id: (_a = detail.custom_data.chain_id) !== null && _a !== void 0 ? _a : '',
        client_id: (_b = detail.custom_data.client_id) !== null && _b !== void 0 ? _b : '',
        wallet_id: (_c = detail.custom_data.wallet_id) !== null && _c !== void 0 ? _c : '',
        wallet_address: (_d = detail.wallet_address) !== null && _d !== void 0 ? _d : '',
        wallet_app_name: (_e = detail.wallet_type) !== null && _e !== void 0 ? _e : '',
        wallet_app_version: (_f = detail.wallet_version) !== null && _f !== void 0 ? _f : '',
        wallet_state_init: (_g = detail.wallet_state_init) !== null && _g !== void 0 ? _g : '',
        trace_id: (_h = detail.trace_id) !== null && _h !== void 0 ? _h : undefined
    };
}
function bindEventsTo(eventDispatcher, analytics) {
    eventDispatcher.addEventListener('ton-connect-ui-wallet-modal-opened', event => {
        var _a;
        const { detail } = event;
        analytics.emitConnectionStarted({
            client_id: detail.client_id || '',
            versions: buildVersionInfo(detail.custom_data),
            main_screen: detail.visible_wallets,
            trace_id: (_a = detail.trace_id) !== null && _a !== void 0 ? _a : undefined
        });
    });
    eventDispatcher.addEventListener('ton-connect-ui-selected-wallet', event => {
        var _a, _b;
        const { detail } = event;
        analytics.emitConnectionSelectedWallet({
            client_id: detail.client_id || '',
            versions: buildVersionInfo(detail.custom_data),
            main_screen: detail.visible_wallets,
            wallets_menu: detail.wallets_menu,
            trace_id: (_a = detail.trace_id) !== null && _a !== void 0 ? _a : undefined,
            wallet_app_name: (_b = detail.wallet_type) !== null && _b !== void 0 ? _b : '',
            wallet_redirect_method: detail.wallet_redirect_method,
            wallet_redirect_link: detail.wallet_redirect_link
        });
    });
    eventDispatcher.addEventListener('ton-connect-connection-completed', event => {
        const { detail } = event;
        analytics.emitConnectionCompleted(buildTonConnectEvent(detail));
    });
    eventDispatcher.addEventListener('ton-connect-connection-error', event => {
        var _a, _b;
        const { detail } = event;
        analytics.emitConnectionError({
            client_id: detail.custom_data.client_id || '',
            wallet_id: detail.custom_data.wallet_id || '',
            error_code: (_a = detail.error_code) !== null && _a !== void 0 ? _a : 0,
            error_message: detail.error_message,
            trace_id: (_b = detail.trace_id) !== null && _b !== void 0 ? _b : undefined
        });
    });
    eventDispatcher.addEventListener('ton-connect-disconnection', event => {
        const { detail } = event;
        analytics.emitDisconnection(buildTonConnectEvent(detail));
    });
    eventDispatcher.addEventListener('ton-connect-transaction-sent-for-signature', event => {
        const { detail } = event;
        analytics.emitTransactionSent(buildTonConnectEvent(detail));
    });
    eventDispatcher.addEventListener('ton-connect-transaction-signed', event => {
        const { detail } = event;
        analytics.emitTransactionSigned(Object.assign(Object.assign({}, buildTonConnectEvent(detail)), { signed_boc: event.detail.signed_transaction }));
    });
    eventDispatcher.addEventListener('ton-connect-transaction-signing-failed', event => {
        var _a;
        const { detail } = event;
        analytics.emitTransactionSigningFailed(Object.assign(Object.assign({}, buildTonConnectEvent(detail)), { valid_until: Number(detail.valid_until), messages: detail.messages.map(message => {
                var _a, _b, _c, _d;
                return ({
                    address: (_a = message.address) !== null && _a !== void 0 ? _a : '',
                    amount: (_b = message.amount) !== null && _b !== void 0 ? _b : '',
                    payload: (_c = message.payload) !== null && _c !== void 0 ? _c : '',
                    state_init: (_d = message.state_init) !== null && _d !== void 0 ? _d : ''
                });
            }), error_message: detail.error_message, error_code: (_a = detail.error_code) !== null && _a !== void 0 ? _a : 0 }));
    });
    eventDispatcher.addEventListener('ton-connect-sign-data-request-initiated', event => {
        const { detail } = event;
        analytics === null || analytics === void 0 ? void 0 : analytics.emitSignDataRequestInitiated(buildTonConnectEvent(detail));
    });
    eventDispatcher.addEventListener('ton-connect-sign-data-request-completed', event => {
        const { detail } = event;
        analytics === null || analytics === void 0 ? void 0 : analytics.emitSignDataRequestCompleted(buildTonConnectEvent(detail));
    });
    eventDispatcher.addEventListener('ton-connect-sign-data-request-failed', event => {
        var _a;
        const { detail } = event;
        let signDataValue = '';
        let signDataSchema = undefined;
        if (detail.data.type === 'text') {
            signDataValue = detail.data.text;
        }
        if (detail.data.type === 'cell') {
            signDataValue = detail.data.cell;
            signDataSchema = detail.data.schema;
        }
        if (detail.data.type === 'binary') {
            signDataValue = detail.data.bytes;
        }
        analytics === null || analytics === void 0 ? void 0 : analytics.emitSignDataRequestFailed(Object.assign(Object.assign({}, buildTonConnectEvent(detail)), { sign_data_type: detail.data.type, sign_data_value: signDataValue, sign_data_schema: signDataSchema, error_code: (_a = detail.error_code) !== null && _a !== void 0 ? _a : 0, error_message: detail.error_message }));
    });
}

class DefaultEnvironment {
    getClientEnvironment() {
        return '';
    }
    getBrowser() {
        return '';
    }
    getLocale() {
        return '';
    }
    getPlatform() {
        return '';
    }
    getTelegramUser() {
        return undefined;
    }
}

const state = {};
/**
 * Initializes the WalletConnect integration.
 *
 * This function must be called once before using WalletConnect features.
 * A second call will throw an error to prevent accidental re-initialization.
 *
 * @param UniversalConnectorCls - A UniversalConnector class imported from '@reown/appkit-universal-connector'
 * @param {WalletConnectOptions} walletConnectOptions - Configuration options used for initializing WalletConnect.
 * @example
 * import { UniversalConnector } from '@reown/appkit-universal-connector';
 *
 * initializeWalletConnect(UniversalConnector, {
 *     projectId: 'abcd1234abcd1234abcd1234abcd1234',
 *     metadata: {
 *         name: 'Demo DApp',
 *         icons: [
 *             'https://example.com/my-icon.png'
 *         ],
 *         url: window.location.origin,
 *         description: 'Demo DApp'
 *     }
 * });
 */
function initializeWalletConnect(UniversalConnectorCls, walletConnectOptions) {
    if ((state === null || state === void 0 ? void 0 : state.walletConnectOptions) !== undefined || (state === null || state === void 0 ? void 0 : state.UniversalConnectorCls) !== undefined) {
        throw new TonConnectError('Wallet Connect already initialized.');
    }
    if (typeof UniversalConnectorCls !== 'function' || !('init' in UniversalConnectorCls)) {
        throw new TonConnectError('Initialize UniversalConnectorCls must be set');
    }
    state.UniversalConnectorCls = UniversalConnectorCls;
    state.walletConnectOptions = walletConnectOptions;
}
function isWalletConnectInitialized() {
    return state.UniversalConnectorCls !== undefined && state.walletConnectOptions !== undefined;
}
function getUniversalConnector() {
    if (state.UniversalConnectorCls === undefined) {
        throw new TonConnectError('Wallet Connect is not initialized.');
    }
    return state.UniversalConnectorCls;
}
function getWalletConnectOptions() {
    if (state.walletConnectOptions === undefined) {
        throw new TonConnectError('Wallet Connect is not initialized.');
    }
    return state.walletConnectOptions;
}

const DEFAULT_REQUEST_ID = '0';
const DEFAULT_EVENT_ID = 0;
class WalletConnectProvider {
    constructor(connectionStorage) {
        this.connectionStorage = connectionStorage;
        this.type = 'injected';
        this.listeners = [];
        this.connector = undefined;
        const { projectId, metadata } = getWalletConnectOptions();
        this.config = {
            networks: [
                {
                    namespace: 'ton',
                    chains: [
                        {
                            id: -239,
                            chainNamespace: 'ton',
                            caipNetworkId: 'ton:-239',
                            name: 'TON',
                            nativeCurrency: { name: 'TON', symbol: 'TON', decimals: 9 },
                            rpcUrls: { default: { http: [] } }
                        },
                        {
                            id: -3,
                            chainNamespace: 'ton',
                            caipNetworkId: 'ton:-3',
                            name: 'TON',
                            nativeCurrency: { name: 'TON', symbol: 'TON', decimals: 9 },
                            rpcUrls: { default: { http: [] } }
                        }
                    ],
                    methods: ['ton_sendMessage', 'ton_signData'],
                    events: []
                }
            ],
            projectId,
            metadata
        };
    }
    static fromStorage(storage) {
        return __awaiter(this, void 0, void 0, function* () {
            return new WalletConnectProvider(storage);
        });
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.connector) {
                this.connector = yield getUniversalConnector().init(this.config);
            }
            return this.connector;
        });
    }
    connect(message, options) {
        var _a, _b;
        const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
        const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
        (_b = this.abortController) === null || _b === void 0 ? void 0 : _b.abort();
        this.abortController = abortController;
        void this._connect(message, {
            traceId,
            signal: abortController.signal,
            abortController
        }).catch(error => logDebug('WalletConnect connect unexpected error', error));
    }
    _connect(message, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const connector = yield this.initialize();
            if ((_a = options.signal) === null || _a === void 0 ? void 0 : _a.aborted) {
                logDebug('WalletConnect connect aborted before start');
                this.clearAbortController(options.abortController);
                return;
            }
            const tonProof = message.items.find(item => item.name === 'ton_proof');
            const authentication = tonProof
                ? [
                    {
                        domain: new URL(this.config.metadata.url).hostname,
                        chains: ['ton:-239'],
                        nonce: '',
                        uri: 'ton_proof',
                        ttl: 0,
                        statement: tonProof.payload
                    }
                ]
                : undefined;
            logDebug('Connecting through this.connector.connect');
            try {
                yield connector.connect({ authentication });
            }
            catch (error) {
                if ((_b = options.signal) === null || _b === void 0 ? void 0 : _b.aborted) {
                    logDebug('WalletConnect connect aborted via signal');
                    this.clearAbortController(options.abortController);
                    return;
                }
                logDebug('WalletConnect connect error', error);
                const event = {
                    id: DEFAULT_EVENT_ID,
                    event: 'connect_error',
                    traceId: options.traceId,
                    payload: {
                        code: CONNECT_EVENT_ERROR_CODES.USER_REJECTS_ERROR,
                        message: 'User declined the connection'
                    }
                };
                logDebug('WalletConnect connect response:', event);
                this.emit(event);
                this.clearAbortController(options.abortController);
                return;
            }
            logDebug('Connected through this.connector.connect');
            try {
                yield this.onConnect(connector, Object.assign(Object.assign({}, options), { includeTonProof: true }));
            }
            catch (error) {
                logDebug('WalletConnect onConnect error', error);
                yield this.disconnect({ traceId: options.traceId, signal: options.signal });
            }
            finally {
                this.clearAbortController(options.abortController);
            }
        });
    }
    restoreConnection(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            (_b = this.abortController) === null || _b === void 0 ? void 0 : _b.abort();
            this.abortController = abortController;
            if (abortController.signal.aborted) {
                return;
            }
            try {
                logDebug('Restoring WalletConnect connection...');
                const storedConnection = yield this.connectionStorage.getWalletConnectConnection();
                if (!storedConnection || abortController.signal.aborted) {
                    return;
                }
                const connector = yield this.initialize();
                if (abortController.signal.aborted) {
                    return;
                }
                yield this.onConnect(connector, {
                    includeTonProof: false,
                    traceId,
                    signal: abortController.signal
                });
                logDebug('WalletConnect successfully restored.');
            }
            catch (error) {
                logDebug('WalletConnect restore error', error);
                yield this.disconnect({ traceId, signal: abortController.signal });
            }
            finally {
                this.clearAbortController(abortController);
            }
        });
    }
    closeConnection() {
        var _a;
        (_a = this.abortController) === null || _a === void 0 ? void 0 : _a.abort();
        this.abortController = undefined;
        void this.disconnect();
    }
    disconnect(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            (_a = this.abortController) === null || _a === void 0 ? void 0 : _a.abort();
            this.abortController = abortController;
            if (abortController.signal.aborted) {
                return;
            }
            try {
                yield this.connectionStorage.removeConnection();
                if (abortController.signal.aborted) {
                    return;
                }
                yield ((_b = this.connector) === null || _b === void 0 ? void 0 : _b.disconnect());
            }
            catch (error) {
                logDebug('WalletConnect disconnect error', error);
            }
            finally {
                this.clearAbortController(abortController);
            }
        });
    }
    sendRequest(request, optionsOrOnRequestSent) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            if (!this.connector) {
                throw new TonConnectError('Wallet Connect not initialized');
            }
            const options = {};
            if (typeof optionsOrOnRequestSent === 'function') {
                options.onRequestSent = optionsOrOnRequestSent;
            }
            else {
                options.onRequestSent = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.onRequestSent;
                options.signal = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.signal;
                options.attempts = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.attempts;
                options.traceId = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.traceId;
            }
            (_a = options.traceId) !== null && _a !== void 0 ? _a : (options.traceId = UUIDv7());
            try {
                if ((_b = options.signal) === null || _b === void 0 ? void 0 : _b.aborted) {
                    throw new TonConnectError('WalletConnect request aborted');
                }
                logDebug('Send wallet-connect request:', Object.assign(Object.assign({}, request), { id: DEFAULT_REQUEST_ID }));
                if (request.method === 'sendTransaction') {
                    const _e = JSON.parse(request.params[0]), { network } = _e, sendTransactionPayload = __rest(_e, ["network"]);
                    const promise = this.connector.request({
                        method: 'ton_sendMessage',
                        params: sendTransactionPayload
                    }, `ton:${network}`);
                    (_c = options === null || options === void 0 ? void 0 : options.onRequestSent) === null || _c === void 0 ? void 0 : _c.call(options);
                    const result = (yield promise);
                    logDebug('Wallet message received:', { result, id: DEFAULT_REQUEST_ID });
                    return {
                        result,
                        id: DEFAULT_REQUEST_ID,
                        traceId: options.traceId
                    };
                }
                else if (request.method === 'signData') {
                    const _f = JSON.parse(request.params[0]), { network } = _f, signDataPayload = __rest(_f, ["network"]);
                    const promise = this.connector.request({
                        method: 'ton_signData',
                        params: signDataPayload
                    }, `ton:${network}`);
                    (_d = options === null || options === void 0 ? void 0 : options.onRequestSent) === null || _d === void 0 ? void 0 : _d.call(options);
                    const result = (yield promise);
                    logDebug('Wallet message received:', { result, id: DEFAULT_REQUEST_ID });
                    return { result, traceId: options.traceId, id: DEFAULT_REQUEST_ID };
                }
                else if (request.method === 'disconnect') {
                    return {
                        id: DEFAULT_REQUEST_ID,
                        traceId: options.traceId
                    };
                }
            }
            catch (error) {
                logDebug('WalletConnect request error', error, error.stack);
                const result = (yield this.handleWalletConnectError(error, {
                    traceId: options.traceId
                }));
                logDebug('Wallet message received:', result);
                return result;
            }
            return {
                id: DEFAULT_REQUEST_ID,
                error: { code: DISCONNECT_ERROR_CODES.UNKNOWN_ERROR, message: 'Not implemented.' },
                traceId: options.traceId
            };
        });
    }
    handleWalletConnectError(error, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof error === 'object' && error !== null) {
                const message = String('message' in error ? error.message : 'msg' in error ? error.msg : error);
                if (message.toLowerCase().includes('reject')) {
                    return {
                        id: DEFAULT_REQUEST_ID,
                        traceId: options.traceId,
                        error: {
                            code: SEND_TRANSACTION_ERROR_CODES.USER_REJECTS_ERROR,
                            message
                        }
                    };
                }
                if (message.toLowerCase().includes('tonvalidationerror')) {
                    return {
                        id: DEFAULT_REQUEST_ID,
                        traceId: options.traceId,
                        error: {
                            code: SEND_TRANSACTION_ERROR_CODES.BAD_REQUEST_ERROR,
                            message
                        }
                    };
                }
                return {
                    id: DEFAULT_REQUEST_ID,
                    traceId: options.traceId,
                    error: {
                        code: SEND_TRANSACTION_ERROR_CODES.UNKNOWN_ERROR,
                        message
                    }
                };
            }
            return {
                id: DEFAULT_REQUEST_ID,
                traceId: options.traceId,
                error: {
                    code: SEND_TRANSACTION_ERROR_CODES.UNKNOWN_ERROR,
                    message: String(error)
                }
            };
        });
    }
    listen(callback) {
        this.listeners.push(callback);
        return () => (this.listeners = this.listeners.filter(listener => listener !== callback));
    }
    buildTonProof(connector) {
        var _a, _b, _c;
        const auth = (_a = connector.provider.session.authentication) === null || _a === void 0 ? void 0 : _a[0];
        const iat = (_b = auth === null || auth === void 0 ? void 0 : auth.p) === null || _b === void 0 ? void 0 : _b.iat;
        const statement = (_c = auth === null || auth === void 0 ? void 0 : auth.p) === null || _c === void 0 ? void 0 : _c.statement;
        if (!iat || !statement) {
            return;
        }
        const domain = auth.p.domain;
        return {
            name: 'ton_proof',
            proof: {
                timestamp: Math.floor(new Date(iat).getTime() / 1000),
                domain: {
                    lengthBytes: domain.length,
                    value: domain
                },
                payload: statement,
                signature: auth.s.s
            }
        };
    }
    onConnect(connector, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            if ((_a = options.signal) === null || _a === void 0 ? void 0 : _a.aborted) {
                logDebug('WalletConnect onConnect aborted');
                return;
            }
            const session = connector.provider.session;
            const tonNamespace = session.namespaces['ton'];
            if (!((_b = tonNamespace === null || tonNamespace === void 0 ? void 0 : tonNamespace.accounts) === null || _b === void 0 ? void 0 : _b[0])) {
                yield this.disconnectWithError({
                    traceId: options.traceId,
                    code: CONNECT_EVENT_ERROR_CODES.BAD_REQUEST_ERROR,
                    message: 'Connection error. No TON accounts connected.'
                });
                return;
            }
            const account = tonNamespace.accounts[0];
            const [, network, address] = account.split(':', 3);
            const publicKey = (_c = session.sessionProperties) === null || _c === void 0 ? void 0 : _c.ton_getPublicKey;
            if (!publicKey) {
                yield this.disconnectWithError({
                    traceId: options.traceId,
                    code: CONNECT_EVENT_ERROR_CODES.BAD_REQUEST_ERROR,
                    message: 'Connection error. No sessionProperties.ton_getPublicKey provided.'
                });
                return;
            }
            const stateInit = (_d = session.sessionProperties) === null || _d === void 0 ? void 0 : _d.ton_getStateInit;
            if (!stateInit) {
                yield this.disconnectWithError({
                    traceId: options.traceId,
                    code: CONNECT_EVENT_ERROR_CODES.BAD_REQUEST_ERROR,
                    message: 'Connection error. No sessionProperties.ton_getStateInit provided.'
                });
                return;
            }
            connector.provider.once('session_delete', () => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield this.connectionStorage.removeConnection();
                    const event = {
                        event: 'disconnect',
                        traceId: UUIDv7(),
                        payload: {}
                    };
                    logDebug('Wallet message received:', event);
                    this.emit(event);
                }
                catch (err) {
                    logDebug('Error while deleting session', err);
                }
            }));
            const tonProof = (options === null || options === void 0 ? void 0 : options.includeTonProof) ? this.buildTonProof(connector) : undefined;
            const parsedAddress = isValidUserFriendlyAddress(address)
                ? toRawAddress(parseUserFriendlyAddress(address))
                : address;
            const features = this.buildFeatureList(tonNamespace.methods);
            const payload = {
                items: [
                    {
                        name: 'ton_addr',
                        address: parsedAddress,
                        network: network,
                        publicKey,
                        walletStateInit: stateInit
                    },
                    ...(tonProof ? [tonProof] : [])
                ],
                device: {
                    appName: 'wallet_connect',
                    appVersion: '',
                    maxProtocolVersion: 2,
                    features,
                    platform: 'browser'
                }
            };
            logDebug('WalletConnect connect response:', {
                event: 'connect',
                payload,
                id: DEFAULT_EVENT_ID
            });
            this.emit({ event: 'connect', payload, traceId: options.traceId });
            yield this.storeConnection();
        });
    }
    buildFeatureList(methods) {
        const features = [];
        if (methods.includes('ton_sendMessage')) {
            features.push('SendTransaction', {
                name: 'SendTransaction',
                maxMessages: 4,
                extraCurrencySupported: false
            });
        }
        if (methods.includes('ton_signData')) {
            features.push({ name: 'SignData', types: ['text', 'binary', 'cell'] });
        }
        return features;
    }
    disconnectWithError(options) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.disconnect();
            const payload = {
                code: options.code,
                message: options.message
            };
            logDebug('WalletConnect connect response:', {
                event: 'connect_error',
                id: DEFAULT_EVENT_ID,
                payload
            });
            this.emit({
                event: 'connect_error',
                traceId: options.traceId,
                payload
            });
        });
    }
    clearAbortController(abortController) {
        if (this.abortController === abortController) {
            this.abortController = undefined;
        }
    }
    emit(event, listeners) {
        (listeners !== null && listeners !== void 0 ? listeners : this.listeners).forEach(listener => listener(event));
    }
    storeConnection() {
        return this.connectionStorage.storeConnection({
            type: 'wallet-connect'
        });
    }
}

function buildAppRequestPayload(richRequest) {
    switch (richRequest.method) {
        case 'sendTransaction': {
            const tx = richRequest.request;
            return Object.assign(Object.assign(Object.assign(Object.assign({ m: 'st' }, (tx.from ? { f: tx.from } : {})), (tx.network ? { n: tx.network } : {})), { vu: tx.validUntil }), buildWireTransactionBody(tx));
        }
        case 'signMessage': {
            const msg = richRequest.request;
            return Object.assign(Object.assign(Object.assign(Object.assign({ m: 'sm' }, (msg.from ? { f: msg.from } : {})), (msg.network ? { n: msg.network } : {})), { vu: msg.validUntil }), buildWireTransactionBody(msg));
        }
        case 'signData': {
            const sd = richRequest.request;
            const base = Object.assign(Object.assign({ m: 'sd' }, (sd.from ? { f: sd.from } : {})), (sd.network ? { n: sd.network } : {}));
            switch (sd.type) {
                case 'text':
                    return Object.assign(Object.assign({}, base), { t: 'text', tx: sd.text });
                case 'binary':
                    return Object.assign(Object.assign({}, base), { t: 'binary', b: sd.bytes });
                case 'cell':
                    return Object.assign(Object.assign({}, base), { t: 'cell', s: sd.schema, c: sd.cell });
            }
        }
    }
}
function buildWireTransactionBody(tx) {
    if (hasItems(tx)) {
        return {
            i: tx.items.map(item => buildWireItem(item))
        };
    }
    return {
        ms: tx.messages.map(msg => {
            const wire = {
                a: msg.address,
                am: msg.amount
            };
            if (msg.payload) {
                wire.p = normalizeBase64(msg.payload);
            }
            if (msg.stateInit) {
                wire.si = normalizeBase64(msg.stateInit);
            }
            if (msg.extraCurrency) {
                wire.ec = msg.extraCurrency;
            }
            return wire;
        })
    };
}
function buildWireItem(item) {
    switch (item.type) {
        case 'ton':
            return Object.assign(Object.assign(Object.assign({ t: 'ton', a: item.address, am: item.amount }, (item.payload ? { p: normalizeBase64(item.payload) } : {})), (item.stateInit ? { si: normalizeBase64(item.stateInit) } : {})), (item.extraCurrency ? { ec: item.extraCurrency } : {}));
        case 'jetton':
            return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ t: 'jetton', ma: item.master, d: item.destination, am: item.amount }, (item.attachAmount ? { aa: item.attachAmount } : {})), (item.responseDestination ? { rd: item.responseDestination } : {})), (item.customPayload ? { cp: normalizeBase64(item.customPayload) } : {})), (item.forwardAmount ? { fa: item.forwardAmount } : {})), (item.forwardPayload ? { fp: normalizeBase64(item.forwardPayload) } : {})), (item.queryId ? { qi: item.queryId } : {}));
        case 'nft':
            return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ t: 'nft', na: item.nftAddress, no: item.newOwner }, (item.attachAmount ? { aa: item.attachAmount } : {})), (item.responseDestination ? { rd: item.responseDestination } : {})), (item.customPayload ? { cp: normalizeBase64(item.customPayload) } : {})), (item.forwardAmount ? { fa: item.forwardAmount } : {})), (item.forwardPayload ? { fp: normalizeBase64(item.forwardPayload) } : {})), (item.queryId ? { qi: item.queryId } : {}));
    }
}
class WireEmbeddedRequestParser {
    constructor() {
        this.convertToWireEmbeddedRequest = buildAppRequestPayload;
    }
    isError(response) {
        return 'error' in response;
    }
    convertFromRpcResponse(method, response) {
        if (this.isError(response)) {
            return { ok: false, error: response.error };
        }
        switch (method) {
            case 'sendTransaction':
                return { ok: true, result: { boc: response.result } };
            case 'signMessage': {
                return {
                    ok: true,
                    result: {
                        internalBoc: response.result.internalBoc
                    }
                };
            }
            case 'signData': {
                return { ok: true, result: response.result };
            }
            default:
                throw new Error(`Unexpected embedded request method: ${method}`);
        }
    }
}
const wireRequestParser = new WireEmbeddedRequestParser();

class Consumable {
    constructor(value) {
        this.__isConsumable = true;
        if (value && typeof value === 'object' && value.__isConsumable) {
            return value;
        }
        this.value = value;
    }
    peek() {
        return this.value;
    }
    consume() {
        logDebug('Consuming object', this.value);
        const value = this.value;
        this.value = undefined;
        return value;
    }
    get consumed() {
        return this.value === undefined;
    }
}

class TonConnect {
    /**
     * Returns available wallets list.
     */
    static getWallets() {
        return this.walletsList.getWallets();
    }
    /**
     * Shows if the wallet is connected right now.
     */
    get connected() {
        return this._wallet !== null;
    }
    /**
     * Current connected account or null if no account is connected.
     */
    get account() {
        var _a;
        return ((_a = this._wallet) === null || _a === void 0 ? void 0 : _a.account) || null;
    }
    /**
     * Current connected wallet or null if no account is connected.
     */
    get wallet() {
        return this._wallet;
    }
    set wallet(value) {
        this._wallet = value;
        this.statusChangeSubscriptions.forEach(callback => callback(this._wallet));
    }
    constructor(options) {
        var _a, _b, _c;
        this._wallet = null;
        this.provider = null;
        this.statusChangeSubscriptions = [];
        this.statusChangeErrorSubscriptions = [];
        const manifestUrl = (options === null || options === void 0 ? void 0 : options.manifestUrl) || getWebPageManifest();
        this.dappSettings = {
            manifestUrl,
            storage: (options === null || options === void 0 ? void 0 : options.storage) || new DefaultStorage()
        };
        this.walletsRequiredFeatures = options === null || options === void 0 ? void 0 : options.walletsRequiredFeatures;
        this.environment = (_a = options === null || options === void 0 ? void 0 : options.environment) !== null && _a !== void 0 ? _a : new DefaultEnvironment();
        this.walletsList = new WalletsListManager({
            walletsListSource: options === null || options === void 0 ? void 0 : options.walletsListSource,
            cacheTTLMs: options === null || options === void 0 ? void 0 : options.walletsListCacheTTLMs,
            onDownloadDurationMeasured: (duration) => {
                var _a;
                (_a = this.analytics) === null || _a === void 0 ? void 0 : _a.setWalletListDownloadDuration(duration);
            }
        });
        const eventDispatcher = (_b = options === null || options === void 0 ? void 0 : options.eventDispatcher) !== null && _b !== void 0 ? _b : new BrowserEventDispatcher();
        this.tracker = new TonConnectTracker({
            eventDispatcher,
            tonConnectSdkVersion: tonConnectSdkVersion
        });
        this.environment = (_c = options === null || options === void 0 ? void 0 : options.environment) !== null && _c !== void 0 ? _c : new DefaultEnvironment();
        this.initAnalytics(manifestUrl, eventDispatcher, options);
        if (!this.dappSettings.manifestUrl) {
            throw new DappMetadataError('Dapp tonconnect-manifest.json must be specified if window.location.origin is undefined. See more https://github.com/ton-connect/docs/blob/main/requests-responses.md#app-manifest');
        }
        this.bridgeConnectionStorage = new BridgeConnectionStorage(this.dappSettings.storage, this.walletsList);
        if (!(options === null || options === void 0 ? void 0 : options.disableAutoPauseConnection)) {
            this.addWindowFocusAndBlurSubscriptions();
        }
    }
    /**
     * Returns available wallets list.
     */
    getWallets() {
        return this.walletsList.getWallets();
    }
    /**
     * Allows to subscribe to connection status changes and handle connection errors.
     * @param callback will be called after connections status changes with actual wallet or null.
     * @param errorsHandler (optional) will be called with some instance of TonConnectError when connect error is received.
     * @returns unsubscribe callback.
     */
    onStatusChange(callback, errorsHandler) {
        this.statusChangeSubscriptions.push(callback);
        if (errorsHandler) {
            this.statusChangeErrorSubscriptions.push(errorsHandler);
        }
        return () => {
            this.statusChangeSubscriptions = this.statusChangeSubscriptions.filter(item => item !== callback);
            if (errorsHandler) {
                this.statusChangeErrorSubscriptions = this.statusChangeErrorSubscriptions.filter(item => item !== errorsHandler);
            }
        };
    }
    // eslint-disable-next-line complexity
    connect(wallet, requestOrOptions, additionalOptions) {
        var _a, _b, _c, _d;
        // TODO: remove deprecated method
        const options = Object.assign({}, additionalOptions);
        if (typeof requestOrOptions === 'object' &&
            requestOrOptions !== null &&
            'tonProof' in requestOrOptions) {
            options.request = requestOrOptions;
        }
        if (typeof requestOrOptions === 'object' &&
            requestOrOptions !== null &&
            ('openingDeadlineMS' in requestOrOptions ||
                'signal' in requestOrOptions ||
                'request' in requestOrOptions ||
                'traceId' in requestOrOptions)) {
            options.request = requestOrOptions === null || requestOrOptions === void 0 ? void 0 : requestOrOptions.request;
            options.openingDeadlineMS = requestOrOptions === null || requestOrOptions === void 0 ? void 0 : requestOrOptions.openingDeadlineMS;
            options.signal = requestOrOptions === null || requestOrOptions === void 0 ? void 0 : requestOrOptions.signal;
            options.embeddedRequest = requestOrOptions === null || requestOrOptions === void 0 ? void 0 : requestOrOptions.embeddedRequest;
        }
        const embeddedRequest = new Consumable(options.embeddedRequest);
        if (options.request) {
            const validationError = validateConnectAdditionalRequest(options.request);
            if (validationError) {
                if (isQaModeEnabled()) {
                    console.error('ConnectAdditionalRequest validation failed: ' + validationError);
                }
                else {
                    throw new TonConnectError('ConnectAdditionalRequest validation failed: ' + validationError);
                }
            }
        }
        if (embeddedRequest.peek()) {
            const validationError = validateEmbeddedRequest(embeddedRequest.peek());
            if (validationError) {
                if (isQaModeEnabled()) {
                    console.error('EmbeddedRequest validation failed: ' + validationError);
                }
                else {
                    throw new TonConnectError('EmbeddedRequest validation failed: ' + validationError);
                }
            }
        }
        if (this.connected) {
            throw new WalletAlreadyConnectedError();
        }
        const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
        (_a = this.abortController) === null || _a === void 0 ? void 0 : _a.abort();
        this.abortController = abortController;
        if (abortController.signal.aborted) {
            throw new TonConnectError('Connection was aborted');
        }
        (_b = this.provider) === null || _b === void 0 ? void 0 : _b.closeConnection();
        this.provider = this.createProvider(wallet);
        abortController.signal.addEventListener('abort', () => {
            var _a;
            (_a = this.provider) === null || _a === void 0 ? void 0 : _a.closeConnection();
            this.provider = null;
        });
        const traceId = (_c = options === null || options === void 0 ? void 0 : options.traceId) !== null && _c !== void 0 ? _c : UUIDv7();
        this.tracker.trackConnectionStarted(traceId);
        const peeked = embeddedRequest.peek();
        const wireConsumable = peeked
            ? new Consumable(wireRequestParser.convertToWireEmbeddedRequest(peeked))
            : undefined;
        const url = this.provider.connect(this.createConnectRequest(options === null || options === void 0 ? void 0 : options.request), {
            openingDeadlineMS: options === null || options === void 0 ? void 0 : options.openingDeadlineMS,
            signal: abortController.signal,
            traceId,
            embeddedRequest: wireConsumable
        });
        // consume the original only if the provider consumed the wire version
        if (wireConsumable === null || wireConsumable === void 0 ? void 0 : wireConsumable.consumed) {
            this.pendingEmbeddedRequestMethod = (_d = embeddedRequest.consume()) === null || _d === void 0 ? void 0 : _d.method;
        }
        return url;
    }
    /**
     * Try to restore existing session and reconnect to the corresponding wallet. Call it immediately when your app is loaded.
     */
    restoreConnection(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            this.tracker.trackConnectionRestoringStarted(traceId);
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            (_b = this.abortController) === null || _b === void 0 ? void 0 : _b.abort();
            this.abortController = abortController;
            if (abortController.signal.aborted) {
                this.tracker.trackConnectionRestoringError('Connection restoring was aborted', traceId);
                return;
            }
            // TODO: potentially race condition here
            const [bridgeConnectionType, embeddedWallet] = yield Promise.all([
                this.bridgeConnectionStorage.storedConnectionType(),
                this.walletsList.getEmbeddedWallet()
            ]);
            if (abortController.signal.aborted) {
                this.tracker.trackConnectionRestoringError('Connection restoring was aborted', traceId);
                return;
            }
            let provider = null;
            try {
                switch (bridgeConnectionType) {
                    case 'http':
                        provider = yield BridgeProvider.fromStorage(this.bridgeConnectionStorage, this.analytics);
                        break;
                    case 'injected':
                        provider = yield InjectedProvider.fromStorage(this.bridgeConnectionStorage, this.analytics);
                        break;
                    case 'wallet-connect':
                        provider = yield WalletConnectProvider.fromStorage(this.bridgeConnectionStorage);
                        break;
                    default:
                        if (embeddedWallet) {
                            provider = this.createProvider(embeddedWallet);
                        }
                        else {
                            return;
                        }
                }
            }
            catch (err) {
                logDebug('Provider is not restored', err);
                this.tracker.trackConnectionRestoringError('Provider is not restored', traceId);
                yield this.bridgeConnectionStorage.removeConnection();
                provider === null || provider === void 0 ? void 0 : provider.closeConnection();
                provider = null;
                return;
            }
            if (abortController.signal.aborted) {
                provider === null || provider === void 0 ? void 0 : provider.closeConnection();
                this.tracker.trackConnectionRestoringError('Connection restoring was aborted', traceId);
                return;
            }
            if (!provider) {
                logError('Provider is not restored');
                this.tracker.trackConnectionRestoringError('Provider is not restored', traceId);
                return;
            }
            (_c = this.provider) === null || _c === void 0 ? void 0 : _c.closeConnection();
            this.provider = provider;
            provider.listen(this.walletEventsListener.bind(this));
            const onAbortRestore = () => {
                this.tracker.trackConnectionRestoringError('Connection restoring was aborted', traceId);
                provider === null || provider === void 0 ? void 0 : provider.closeConnection();
                provider = null;
            };
            abortController.signal.addEventListener('abort', onAbortRestore);
            const restoreConnectionTask = callForSuccess((_options) => __awaiter(this, void 0, void 0, function* () {
                yield (provider === null || provider === void 0 ? void 0 : provider.restoreConnection({
                    openingDeadlineMS: options === null || options === void 0 ? void 0 : options.openingDeadlineMS,
                    signal: _options.signal,
                    traceId
                }));
                abortController.signal.removeEventListener('abort', onAbortRestore);
                if (this.connected) {
                    const sessionInfo = this.getSessionInfo();
                    this.tracker.trackConnectionRestoringCompleted(this.wallet, sessionInfo, traceId);
                }
                else {
                    this.tracker.trackConnectionRestoringError('Connection restoring failed', traceId);
                }
            }), {
                attempts: Number.MAX_SAFE_INTEGER,
                delayMs: 2000,
                signal: options === null || options === void 0 ? void 0 : options.signal
            });
            const restoreConnectionTimeout = new Promise(resolve => setTimeout(() => resolve(), 12000) // connection deadline
            );
            return Promise.race([restoreConnectionTask, restoreConnectionTimeout]);
        });
    }
    sendTransaction(transaction, optionsOrOnRequestSent) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            // TODO: remove deprecated method
            const options = {};
            if (typeof optionsOrOnRequestSent === 'function') {
                options.onRequestSent = optionsOrOnRequestSent;
            }
            else {
                options.onRequestSent = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.onRequestSent;
                options.signal = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.signal;
                options.traceId = optionsOrOnRequestSent === null || optionsOrOnRequestSent === void 0 ? void 0 : optionsOrOnRequestSent.traceId;
            }
            // Validate transaction
            const validationError = validateSendTransactionRequest(transaction);
            if (validationError) {
                if (isQaModeEnabled()) {
                    console.error('SendTransactionRequest validation failed: ' + validationError);
                }
                else {
                    throw new TonConnectError('SendTransactionRequest validation failed: ' + validationError);
                }
            }
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            if (abortController.signal.aborted) {
                throw new TonConnectError('Transaction sending was aborted');
            }
            this.checkConnection();
            const { requiredMessagesNumber, requireExtraCurrencies, requiredItemTypes } = this.extractRequiredFeatures(transaction);
            checkSendTransactionSupport(this.wallet.device.features, {
                requiredMessagesNumber,
                requireExtraCurrencies,
                requiredItemTypes
            });
            const sessionInfo = this.getSessionInfo();
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            this.tracker.trackTransactionSentForSignature(this.wallet, transaction, sessionInfo, traceId);
            const from = transaction.from || this.account.address;
            const network = transaction.network || this.account.chain;
            if (((_b = this.wallet) === null || _b === void 0 ? void 0 : _b.account.chain) && network !== this.wallet.account.chain) {
                if (!isQaModeEnabled()) {
                    throw new WalletWrongNetworkError('Wallet connected to a wrong network', {
                        cause: {
                            expectedChainId: (_c = this.wallet) === null || _c === void 0 ? void 0 : _c.account.chain,
                            actualChainId: network
                        }
                    });
                }
                console.error('Wallet connected to a wrong network', {
                    expectedChainId: (_d = this.wallet) === null || _d === void 0 ? void 0 : _d.account.chain,
                    actualChainId: network
                });
            }
            const rpcPayload = hasItems(transaction)
                ? this.buildItemsRpcPayload(transaction, from, network)
                : this.buildMessagesRpcPayload(transaction, from, network);
            const response = yield this.provider.sendRequest(sendTransactionParser.convertToRpcRequest(rpcPayload), {
                onRequestSent: options.onRequestSent,
                signal: abortController.signal,
                traceId
            });
            if (sendTransactionParser.isError(response)) {
                this.tracker.trackTransactionSigningFailed(this.wallet, transaction, response.error.message, response.error.code, sessionInfo, traceId);
                return sendTransactionParser.parseAndThrowError(response);
            }
            const result = sendTransactionParser.convertFromRpcResponse(response);
            this.tracker.trackTransactionSigned(this.wallet, transaction, result, sessionInfo, traceId);
            return Object.assign(Object.assign({}, result), { traceId: response.traceId });
        });
    }
    extractRequiredFeatures(transaction) {
        var _a;
        const useItems = hasItems(transaction);
        const requiredMessagesNumber = useItems
            ? transaction.items.length
            : transaction.messages.length;
        const requireExtraCurrencies = useItems
            ? (_a = transaction.items) === null || _a === void 0 ? void 0 : _a.filter(i => i.type === 'ton').some(m => m.extraCurrency && Object.keys(m.extraCurrency).length > 0)
            : transaction.messages.some(m => m.extraCurrency && Object.keys(m.extraCurrency).length > 0);
        const requiredItemTypes = useItems
            ? [...new Set(transaction.items.map(item => item.type))]
            : undefined;
        return { requiredMessagesNumber, requireExtraCurrencies, requiredItemTypes };
    }
    signData(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            if (abortController.signal.aborted) {
                throw new TonConnectError('data sending was aborted');
            }
            // Validate sign data
            const validationError = validateSignDataPayload(data);
            if (validationError) {
                if (isQaModeEnabled()) {
                    console.error('SignDataPayload validation failed: ' + validationError);
                }
                else {
                    throw new TonConnectError('SignDataPayload validation failed: ' + validationError);
                }
            }
            this.checkConnection();
            checkSignDataSupport(this.wallet.device.features, { requiredTypes: [data.type] });
            const sessionInfo = this.getSessionInfo();
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            this.tracker.trackDataSentForSignature(this.wallet, data, sessionInfo, traceId);
            const from = data.from || this.account.address;
            const network = data.network || this.account.chain;
            if (((_b = this.wallet) === null || _b === void 0 ? void 0 : _b.account.chain) && network !== this.wallet.account.chain) {
                if (!isQaModeEnabled()) {
                    throw new WalletWrongNetworkError('Wallet connected to a wrong network', {
                        cause: {
                            expectedChainId: (_c = this.wallet) === null || _c === void 0 ? void 0 : _c.account.chain,
                            actualChainId: network
                        }
                    });
                }
                console.error('Wallet connected to a wrong network', {
                    expectedChainId: (_d = this.wallet) === null || _d === void 0 ? void 0 : _d.account.chain,
                    actualChainId: network
                });
            }
            const response = yield this.provider.sendRequest(signDataParser.convertToRpcRequest(Object.assign(Object.assign(Object.assign({}, data), (data.type === 'cell' ? { cell: normalizeBase64(data.cell) } : {})), { from,
                network })), { onRequestSent: options === null || options === void 0 ? void 0 : options.onRequestSent, signal: abortController.signal, traceId });
            if (signDataParser.isError(response)) {
                this.tracker.trackDataSigningFailed(this.wallet, data, response.error.message, response.error.code, sessionInfo, traceId);
                return signDataParser.parseAndThrowError(response);
            }
            const result = signDataParser.convertFromRpcResponse(response);
            this.tracker.trackDataSigned(this.wallet, data, result, sessionInfo, traceId);
            return Object.assign(Object.assign({}, result), { traceId });
        });
    }
    signMessage(message, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            if (abortController.signal.aborted) {
                throw new TonConnectError('Message signing was aborted');
            }
            const validationError = validateSignMessageRequest(message);
            if (validationError) {
                if (isQaModeEnabled()) {
                    console.error('SignMessageRequest validation failed: ' + validationError);
                }
                else {
                    throw new TonConnectError('SignMessageRequest validation failed: ' + validationError);
                }
            }
            this.checkConnection();
            const { requiredMessagesNumber, requireExtraCurrencies, requiredItemTypes } = this.extractRequiredFeatures(message);
            checkSignMessageSupport(this.wallet.device.features, {
                requiredMessagesNumber,
                requireExtraCurrencies,
                requiredItemTypes
            });
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            const from = message.from || this.account.address;
            const network = message.network || this.account.chain;
            if (((_b = this.wallet) === null || _b === void 0 ? void 0 : _b.account.chain) && network !== this.wallet.account.chain) {
                if (!isQaModeEnabled()) {
                    throw new WalletWrongNetworkError('Wallet connected to a wrong network', {
                        cause: {
                            expectedChainId: (_c = this.wallet) === null || _c === void 0 ? void 0 : _c.account.chain,
                            actualChainId: network
                        }
                    });
                }
                console.error('Wallet connected to a wrong network', {
                    expectedChainId: (_d = this.wallet) === null || _d === void 0 ? void 0 : _d.account.chain,
                    actualChainId: network
                });
            }
            const rpcPayload = hasItems(message)
                ? this.buildItemsRpcPayload(message, from, network)
                : this.buildMessagesRpcPayload(message, from, network);
            const response = yield this.provider.sendRequest(signMessageParser.convertToRpcRequest(rpcPayload), {
                onRequestSent: options === null || options === void 0 ? void 0 : options.onRequestSent,
                signal: abortController.signal,
                traceId
            });
            if (signMessageParser.isError(response)) {
                return signMessageParser.parseAndThrowError(response);
            }
            const result = signMessageParser.convertFromRpcResponse(response);
            return Object.assign(Object.assign({}, result), { traceId: response.traceId });
        });
    }
    /**
     * Set desired network for the connection. Can only be set before connecting.
     * If wallet connects with a different chain, the SDK will throw an error and abort connection.
     * @param network desired network id (e.g., '-239', '-3', or custom). Pass undefined to allow any network.
     */
    setConnectionNetwork(network) {
        if (this.connected) {
            throw new TonConnectError('Cannot change network while wallet is connected');
        }
        this.desiredChainId = network;
    }
    /**
     * Disconnect form thw connected wallet and drop current session.
     */
    disconnect(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.connected) {
                throw new WalletNotConnectedError();
            }
            const abortController = createAbortController(options === null || options === void 0 ? void 0 : options.signal);
            const prevAbortController = this.abortController;
            this.abortController = abortController;
            if (abortController.signal.aborted) {
                throw new TonConnectError('Disconnect was aborted');
            }
            const traceId = (_a = options === null || options === void 0 ? void 0 : options.traceId) !== null && _a !== void 0 ? _a : UUIDv7();
            this.onWalletDisconnected('dapp', { traceId });
            yield ((_b = this.provider) === null || _b === void 0 ? void 0 : _b.disconnect({
                signal: abortController.signal,
                traceId
            }));
            prevAbortController === null || prevAbortController === void 0 ? void 0 : prevAbortController.abort();
        });
    }
    /**
     * Gets the current session ID if available.
     * @returns session ID string or null if not available.
     */
    getSessionId() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.provider) {
                return null;
            }
            try {
                const connection = yield this.bridgeConnectionStorage.getConnection();
                if (!connection || connection.type !== 'http') {
                    return null;
                }
                if ('sessionCrypto' in connection) {
                    // Pending connection
                    return connection.sessionCrypto.sessionId;
                }
                else {
                    // Established connection
                    return connection.session.sessionCrypto.sessionId;
                }
            }
            catch (_a) {
                return null;
            }
        });
    }
    getSessionInfo() {
        var _a;
        if (((_a = this.provider) === null || _a === void 0 ? void 0 : _a.type) !== 'http') {
            return null;
        }
        if (!('session' in this.provider)) {
            return null;
        }
        try {
            const session = this.provider.session;
            if (!session) {
                return null;
            }
            const clientId = session.sessionCrypto.sessionId;
            let walletId = null;
            if ('walletPublicKey' in session) {
                walletId = session.walletPublicKey;
            }
            return { clientId, walletId };
        }
        catch (_b) {
            return null;
        }
    }
    buildMessagesRpcPayload(transaction, from, network) {
        return {
            from,
            network,
            valid_until: transaction.validUntil,
            messages: transaction.messages.map((_a) => {
                var { extraCurrency, payload, stateInit } = _a, msg = __rest(_a, ["extraCurrency", "payload", "stateInit"]);
                return (Object.assign(Object.assign({}, msg), { payload: normalizeBase64(payload), stateInit: normalizeBase64(stateInit), extra_currency: extraCurrency }));
            })
        };
    }
    buildItemsRpcPayload(transaction, from, network) {
        return {
            from,
            network,
            valid_until: transaction.validUntil,
            items: transaction.items.map(item => normalizeStructuredItem(item))
        };
    }
    /**
     * Pause bridge HTTP connection. Might be helpful, if you want to pause connections while browser tab is unfocused,
     * or if you use SDK with NodeJS and want to save server resources.
     */
    pauseConnection() {
        var _a;
        if (((_a = this.provider) === null || _a === void 0 ? void 0 : _a.type) !== 'http') {
            return;
        }
        this.provider.pause();
    }
    /**
     * Unpause bridge HTTP connection if it is paused.
     */
    unPauseConnection() {
        var _a;
        if (((_a = this.provider) === null || _a === void 0 ? void 0 : _a.type) !== 'http') {
            return Promise.resolve();
        }
        return this.provider.unPause();
    }
    addWindowFocusAndBlurSubscriptions() {
        const document = getDocument();
        if (!document) {
            return;
        }
        try {
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.pauseConnection();
                }
                else {
                    this.unPauseConnection().catch(() => { });
                }
            });
        }
        catch (e) {
            logError('Cannot subscribe to the document.visibilitychange: ', e);
        }
    }
    initAnalytics(manifestUrl, eventDispatcher, options) {
        var _a;
        const analyticsSettings = options === null || options === void 0 ? void 0 : options.analytics;
        const mode = (_a = analyticsSettings === null || analyticsSettings === void 0 ? void 0 : analyticsSettings.mode) !== null && _a !== void 0 ? _a : 'telemetry';
        if (mode === 'off') {
            return;
        }
        const analytics = new AnalyticsManager({
            environment: this.environment,
            mode
        });
        this.analytics = analytics;
        const telegramUser = this.environment.getTelegramUser();
        const sharedAnalyticsData = {
            browser: this.environment.getBrowser(),
            platform: this.environment.getPlatform(),
            manifest_json_url: manifestUrl,
            origin_url: getOriginWithPath,
            locale: this.environment.getLocale()
        };
        if (telegramUser) {
            sharedAnalyticsData.tg_id = telegramUser.id;
            sharedAnalyticsData.tma_is_premium = telegramUser.isPremium;
        }
        bindEventsTo(eventDispatcher, analytics.scoped(sharedAnalyticsData));
    }
    createProvider(wallet) {
        let provider;
        if (!Array.isArray(wallet) && isWalletConnectionSourceJS(wallet)) {
            provider = new InjectedProvider(this.bridgeConnectionStorage, wallet.jsBridgeKey, this.analytics);
        }
        else if (!Array.isArray(wallet) && isWalletConnectionSourceWalletConnect(wallet)) {
            provider = new WalletConnectProvider(this.bridgeConnectionStorage);
        }
        else {
            provider = new BridgeProvider(this.bridgeConnectionStorage, wallet, this.analytics);
        }
        provider.listen(this.walletEventsListener.bind(this));
        return provider;
    }
    walletEventsListener(e) {
        switch (e.event) {
            case 'connect':
                this.onWalletConnected(e.payload, {
                    traceId: e.traceId,
                    response: e.response
                });
                break;
            case 'connect_error':
                this.tracker.trackConnectionError(e.payload.message, e.payload.code, this.getSessionInfo(), e.traceId);
                const walletError = connectErrorsParser.parseError(e.payload);
                this.onWalletConnectError(walletError);
                break;
            case 'disconnect':
                this.onWalletDisconnected('wallet', { traceId: e.traceId });
        }
    }
    onWalletConnected(connectEvent, options) {
        var _a, _b;
        const method = this.pendingEmbeddedRequestMethod;
        this.pendingEmbeddedRequestMethod = undefined;
        const tonAccountItem = connectEvent.items.find(item => item.name === 'ton_addr');
        const tonProofItem = connectEvent.items.find(item => item.name === 'ton_proof');
        if (!tonAccountItem) {
            throw new TonConnectError('ton_addr connection item was not found');
        }
        const hasRequiredFeatures = checkRequiredWalletFeatures(connectEvent.device.features, this.walletsRequiredFeatures);
        if (!hasRequiredFeatures) {
            (_a = this.provider) === null || _a === void 0 ? void 0 : _a.disconnect();
            this.onWalletConnectError(new WalletMissingRequiredFeaturesError('Wallet does not support required features', { cause: { connectEvent } }));
            return;
        }
        const wallet = {
            device: connectEvent.device,
            provider: this.provider.type,
            account: {
                address: tonAccountItem.address,
                chain: tonAccountItem.network,
                walletStateInit: tonAccountItem.walletStateInit,
                publicKey: tonAccountItem.publicKey
            }
        };
        if (this.desiredChainId && wallet.account.chain !== this.desiredChainId) {
            const expectedChainId = this.desiredChainId;
            const actualChainId = wallet.account.chain;
            (_b = this.provider) === null || _b === void 0 ? void 0 : _b.disconnect();
            this.onWalletConnectError(new WalletWrongNetworkError('Wallet connected to a wrong network', {
                cause: { expectedChainId, actualChainId }
            }));
            return;
        }
        if (tonProofItem) {
            const validationError = validateTonProofItemReply(tonProofItem);
            let tonProof = undefined;
            if (validationError) {
                if (isQaModeEnabled()) {
                    console.error('TonProofItem validation failed: ' + validationError);
                }
                tonProof = {
                    name: 'ton_proof',
                    error: {
                        code: CONNECT_ITEM_ERROR_CODES.UNKNOWN_ERROR,
                        message: validationError
                    }
                };
            }
            else {
                try {
                    if ('proof' in tonProofItem) {
                        tonProof = {
                            name: 'ton_proof',
                            proof: {
                                timestamp: tonProofItem.proof.timestamp,
                                domain: {
                                    lengthBytes: tonProofItem.proof.domain.lengthBytes,
                                    value: tonProofItem.proof.domain.value
                                },
                                payload: tonProofItem.proof.payload,
                                signature: tonProofItem.proof.signature
                            }
                        };
                    }
                    else if ('error' in tonProofItem) {
                        tonProof = {
                            name: 'ton_proof',
                            error: {
                                code: tonProofItem.error.code,
                                message: tonProofItem.error.message
                            }
                        };
                    }
                    else {
                        throw new TonConnectError('Invalid data format');
                    }
                }
                catch (e) {
                    tonProof = {
                        name: 'ton_proof',
                        error: {
                            code: CONNECT_ITEM_ERROR_CODES.UNKNOWN_ERROR,
                            message: 'Invalid data format'
                        }
                    };
                }
            }
            wallet.connectItems = { tonProof };
        }
        if (options.response && method) {
            wallet.embeddedResponse = wireRequestParser.convertFromRpcResponse(method, options.response);
        }
        this.wallet = wallet;
        const sessionInfo = this.getSessionInfo();
        this.tracker.trackConnectionCompleted(wallet, sessionInfo, options === null || options === void 0 ? void 0 : options.traceId);
    }
    onWalletConnectError(error) {
        this.statusChangeErrorSubscriptions.forEach(errorsHandler => errorsHandler(error));
        logDebug(error);
        if (error instanceof ManifestNotFoundError || error instanceof ManifestContentErrorError) {
            logError(error);
            throw error;
        }
    }
    onWalletDisconnected(scope, options) {
        const sessionInfo = this.getSessionInfo();
        this.tracker.trackDisconnection(this.wallet, scope, sessionInfo, options === null || options === void 0 ? void 0 : options.traceId);
        this.wallet = null;
    }
    checkConnection() {
        if (!this.connected) {
            throw new WalletNotConnectedError();
        }
    }
    createConnectRequest(request) {
        const items = [
            Object.assign({ name: 'ton_addr' }, (this.desiredChainId ? { network: this.desiredChainId } : {}))
        ];
        if (request === null || request === void 0 ? void 0 : request.tonProof) {
            items.push({
                name: 'ton_proof',
                payload: request.tonProof
            });
        }
        return {
            manifestUrl: this.dappSettings.manifestUrl,
            items
        };
    }
}
TonConnect.walletsList = new WalletsListManager();
/**
 * Check if specified wallet is injected and available to use with the app.
 * @param walletJSKey target wallet's js bridge key.
 */
TonConnect.isWalletInjected = (walletJSKey) => InjectedProvider.isWalletInjected(walletJSKey);
/**
 * Check if the app is opened inside specified wallet's browser.
 * @param walletJSKey target wallet's js bridge key.
 */
TonConnect.isInsideWalletBrowser = (walletJSKey) => InjectedProvider.isInsideWalletBrowser(walletJSKey);

export { BadRequestError, BrowserEventDispatcher, Consumable, FetchWalletsError, LocalstorageNotFoundError, ParseHexError, TonConnect, TonConnectError, UUIDv7, UnknownAppError, UnknownError, UserRejectsError, WalletAlreadyConnectedError, WalletMissingRequiredFeaturesError, WalletNotConnectedError, WalletNotInjectedError, WalletNotSupportFeatureError, WalletWrongNetworkError, WalletsListManager, WrongAddressError, checkRequiredWalletFeatures, createConnectionCompletedEvent, createConnectionErrorEvent, createConnectionRestoringCompletedEvent, createConnectionRestoringErrorEvent, createConnectionRestoringStartedEvent, createConnectionStartedEvent, createDataSentForSignatureEvent, createDataSignedEvent, createDataSigningFailedEvent, createDisconnectionEvent, createRequestVersionEvent, createResponseVersionEvent, createSelectedWalletEvent, createTransactionSentForSignatureEvent, createTransactionSignedEvent, createTransactionSigningFailedEvent, createVersionInfo, createWalletModalOpenedEvent, decodeTelegramUrlParameters, TonConnect as default, enableQaMode, encodeTelegramUrlParameters, hasItems, hasMessages, initializeWalletConnect, isConnectUrl, isQaModeEnabled, isTelegramUrl, isWalletConnectInitialized, isWalletInfoCurrentlyEmbedded, isWalletInfoCurrentlyInjected, isWalletInfoInjectable, isWalletInfoInjected, isWalletInfoRemote, toUserFriendlyAddress };
//# sourceMappingURL=index.mjs.map
